import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { SubTabs, Ticker, Select } from '../ui';
import InfoBtn from '../ui/InfoBtn';
import { usePrices } from '../../context/PriceContext';
import { getOptionExpiries, getOptionChain, getOptionSymbols, getKiteStatus,
         loadStrategiesAPI, saveStrategiesAPI, getOIPCRHistory, getOIStrikeHistory, getOIStrikeTimeline,
         getIVHistory, getStrategyMargin, getMaxPainHistory, getLegLtpBatch,
         getNiftyIntraday } from '../../api/client';

// ── Constants ──────────────────────────────────────────────────────────────────

// Static fallback index list (always available before Kite login)
const FALLBACK_INDICES = ['NIFTY','BANKNIFTY','FINNIFTY','MIDCPNIFTY'];

// ── PCR/CPR history line offsets (label → opacity, strokeWidth) ───────────────
const OI_HIST_OFFSETS = [
  { label: 'Now',  op: 0.90, sw: 1.5 },
  { label: '-5m',  op: 0.70, sw: 1.3 },
  { label: '-15m', op: 0.55, sw: 1.1 },
  { label: '-30m', op: 0.40, sw: 0.9 },
  { label: '-1h',  op: 0.28, sw: 0.7 },
  { label: '-2h',  op: 0.18, sw: 0.5 },
];

// ── Drift ribbon helpers ───────────────────────────────────────────────────────
function lerpRGB(a, b, t) {
  return `rgb(${Math.round(a[0]+(b[0]-a[0])*t)},${Math.round(a[1]+(b[1]-a[1])*t)},${Math.round(a[2]+(b[2]-a[2])*t)})`;
}
const DRIFT_CREAM = [240, 239, 238]; // #F0EFEE — hue-neutral grey, ~5 steps lighter than #EBEAE8, closer to paper
const DRIFT_BLUE  = [43,  124, 161];
const DRIFT_GOLD  = [201, 129, 103]; // ce-heat-max: 82% × #C06A4E terracotta + 18% cream

// All tuning knobs in one block — calibrate against real NIFTY OI snapshots before finalising.
const DRIFT_CONFIG = {
  HALFLIFE_MIN : 30,     // exp-decay half-life for bucket weights (minutes of newer endpoint age)
  K_LEN        : 0.008,  // rate→length gain (calibrated: tanh≈0.42 at median |mom|, 0.87 at p90)
  L_MIN        : 10,     // minimum arrow length (px)
  L_SPAN       : 30,     // arrow length span (px)
  T_MIN        : 1.2,    // minimum arrow thickness (px)
  T_SPAN       : 3.0,    // arrow thickness span (px)
  FLAT_THRESH  : 0.001,  // |mom| below which arrow is grey (≈16% of strikes flat in NIFTY session)
  TREND_FLOOR  : 0.015,  // |drift| below which a bucket is skipped for sign-change counting
  RATIO_MIN    : 0.05,   // PCR clamp floor (guard near-zero CE OI before ln)
  RATIO_MAX    : 20,     // PCR clamp ceiling
  SAT_SCALE    : 0.55,   // ribbon saturation: drift magnitude for full colour
};

// Ribbon bucket definitions, top→bottom (newest interval first).
// age = minutes-ago of the NEWER endpoint — this is the exp-decay weight anchor.
const RIBBON_BUCKETS = [
  { newer: 'now',  older: '-5m',  age:  0, span:  5 }, // b5 — top cell    (−5m → now)
  { newer: '-5m',  older: '-15m', age:  5, span: 10 }, // b4               (−15m → −5m)
  { newer: '-15m', older: '-30m', age: 15, span: 15 }, // b3               (−30m → −15m)
  { newer: '-30m', older: '-1h',  age: 30, span: 30 }, // b2               (−1h  → −30m)
  { newer: '-1h',  older: '-2h',  age: 60, span: 60 }, // b1 — bottom cell (−2h  → −1h)
];

const RIBBON_CELL_H = 18;
const RIBBON_PITCH  = 20;
const RIBBON_TOP    = 10;
const ARROW_ROW_Y   = RIBBON_TOP + RIBBON_BUCKETS.length * RIBBON_PITCH + 14;
const RIBBON_SVG_H  = ARROW_ROW_Y + 24;

const MCX_INSTRUMENTS = [
  { id:'NATURALGAS', label:'Natural Gas' },
  { id:'CRUDEOIL',   label:'Crude Oil'   },
  { id:'GOLD',       label:'Gold'        },
  { id:'GOLDM',      label:'Gold Mini'   },
  { id:'SILVER',     label:'Silver'      },
  { id:'SILVERM',    label:'Silver Mini' },
];

const BSE_INSTRUMENTS = [
  { id:'SENSEX', label:'SENSEX'  },
  { id:'BANKEX', label:'BANKEX'  },
];

const NSE_INDICES_SET = new Set(['NIFTY','BANKNIFTY','FINNIFTY','MIDCPNIFTY','SENSEX','BANKEX']);

// Chain switching fires two Kite-backed requests per change (oi-strike-timeline,
// nifty-intraday). 300 ms is long enough to swallow a burst of clicks and short
// enough to feel immediate on a deliberate switch.
const CHAIN_FETCH_DEBOUNCE_MS = 300;

const BSE_INDICES_SET = new Set(['SENSEX','BANKEX']);

const RANGE_OPTS = [
  { l:'±5',  n:5    },
  { l:'±10', n:10   },
  { l:'±15', n:15   },
  { l:'All', n:9999 },
];

const REFRESH_MS = 300_000; // 5 minutes

const fmtExpiry = d => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  const MON = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day}${MON[+m]}${y.slice(2)}`;
};

const fmtOI = v =>
  v >= 10_000_000 ? (v / 10_000_000).toFixed(2) + 'Cr'
  : v >= 100_000  ? (v / 100_000).toFixed(1) + 'L'
  : v >= 1_000    ? (v / 1_000).toFixed(1) + 'K'
  : String(v);

// Signed OI label  e.g. "+12.3L"  "–4.5Cr"
const fmtOISign = v => (v >= 0 ? '+' : '–') + fmtOI(Math.abs(Math.round(v)));

// Absolute OI change from %delta: current * delta / (100 + delta)
const calcAbsOIChg = (oi, delta) => {
  const d = 100 + (delta ?? 0);
  return d <= 0 ? -oi : oi * (delta ?? 0) / d;
};

// Format LTP change: "+₹12.5  (+1.2%)"
function FmtLTPChg({ chg, pct, compact = false, stacked = false }) {
  if (chg === 0 && pct === 0) return <span className="dim">—</span>;
  const up = chg >= 0;
  if (stacked) {
    return (
      <span style={{ fontFamily:'var(--font-data)', fontSize:11,
        color: up ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
        display:'inline-flex', flexDirection:'column', alignItems:'center', lineHeight:1.3 }}>
        <span>{up ? '+' : '−'}₹{Math.abs(chg).toFixed(1)}</span>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>({up ? '+' : '−'}{Math.abs(pct).toFixed(1)}%)</span>
      </span>
    );
  }
  return (
    <span style={{ fontFamily:'var(--font-data)', fontSize:11, whiteSpace:'nowrap',
      color: up ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
      {up ? '+' : '−'}₹{Math.abs(chg).toFixed(1)}
      {!compact && (
        <span style={{ color:'var(--ink-3)', marginLeft:2 }}>
          ({up ? '+' : '−'}{Math.abs(pct).toFixed(1)}%)
        </span>
      )}
    </span>
  );
}


// ── SearchableSelect ───────────────────────────────────────────────────────────

function SearchableSelect({ value, onChange, groups, placeholder = 'Search…' }) {
  // groups: [{ label: 'Indices', items: [{id, label}] }, ...]
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Find the display label for current value
  const selectedLabel = useMemo(() => {
    for (const g of groups) {
      const found = g.items.find(i => i.id === value);
      if (found) return found.label;
    }
    return value;
  }, [value, groups]);

  // Filter groups by query
  const filteredGroups = useMemo(() => {
    if (!query) return groups;
    const q = query.toLowerCase();
    return groups
      .map(g => ({ ...g, items: g.items.filter(i => i.id.toLowerCase().includes(q) || i.label.toLowerCase().includes(q)) }))
      .filter(g => g.items.length > 0);
  }, [groups, query]);

  const handleOpen = () => {
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = id => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  const totalItems = filteredGroups.reduce((s, g) => s + g.items.length, 0);

  return (
    <div ref={wrapRef} style={{ position:'relative', zIndex:20 }}>
      {/* Trigger */}
      <div onClick={handleOpen}
        style={{ display:'flex', alignItems:'center', gap:4, cursor:'pointer',
          border:'.5px solid var(--hair-strong)', borderRadius:3, background:'var(--surface-2)',
          padding:'2px 6px 2px 8px', minWidth:120 }}>
        {open
          ? <input ref={inputRef} value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={placeholder}
              style={{ fontSize:11, fontWeight:600, background:'transparent', border:'none',
                outline:'none', color:'var(--ink)', width:'100%', minWidth:80 }}
              onKeyDown={e => { if (e.key==='Escape') setOpen(false); }}
            />
          : <span style={{ fontSize:11, fontWeight:700, color:'var(--ink)', flex:1 }}>{selectedLabel}</span>
        }
        <span style={{ fontSize:11, color:'var(--ink-3)', userSelect:'none' }}>▾</span>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 2px)', left:0,
          background:'var(--surface)', border:'.5px solid var(--hair-strong)',
          borderRadius:4, boxShadow:'0 4px 16px rgba(0,0,0,.15)',
          maxHeight:240, overflowY:'auto', minWidth:160, zIndex:100,
        }}>
          {totalItems === 0
            ? <div style={{ padding:'8px 12px', fontSize:11, color:'var(--ink-3)' }}>No matches</div>
            : filteredGroups.map(g => (
                <div key={g.label}>
                  {/* Group header */}
                  <div style={{ padding:'5px 10px 2px', fontSize:11, fontWeight:700,
                    color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em',
                    background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)' }}>
                    {g.label}
                  </div>
                  {g.items.map(item => (
                    <div key={item.id} onClick={() => handleSelect(item.id)}
                      style={{ padding:'5px 12px', fontSize:11, cursor:'pointer',
                        background: item.id===value ? 'rgba(26,90,170,.1)' : 'transparent',
                        color: item.id===value ? 'var(--amber-ink)' : 'var(--ink)',
                        fontWeight: item.id===value ? 600 : 400 }}
                      onMouseEnter={e => { if(item.id!==value) e.currentTarget.style.background='var(--surface-2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = item.id===value?'rgba(26,90,170,.1)':'transparent'; }}>
                      {item.id}
                      {item.label !== item.id && (
                        <span style={{ fontSize:11, color:'var(--ink-3)', marginLeft:5 }}>{item.label}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}


// ── Lightweight confirmation modal ────────────────────────────────────────────

function ConfirmModal({ message, onConfirm, onCancel }) {
  if (!message) return null;
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'rgba(0,0,0,.45)', display:'flex', alignItems:'center', justifyContent:'center',
    }} onMouseDown={onCancel}>
      <div onMouseDown={e => e.stopPropagation()}
        style={{ background:'var(--surface)', border:'.5px solid var(--hair-strong)', borderRadius:6,
          padding:'20px 24px', minWidth:260, maxWidth:340, boxShadow:'0 8px 32px rgba(0,0,0,.25)' }}>
        <div style={{ fontSize:11, fontWeight:600, color:'var(--ink)', marginBottom:6 }}>
          Confirm
        </div>
        <div style={{ fontSize:11, color:'var(--ink-2)', marginBottom:18, lineHeight:1.5 }}>
          {message}
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button onClick={onCancel}
            style={{ fontSize:11, fontWeight:600, padding:'4px 14px', borderRadius:4,
              border:'.5px solid var(--hair-strong)', background:'transparent',
              color:'var(--ink-2)', cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ fontSize:11, fontWeight:600, padding:'4px 14px', borderRadius:4,
              border:'none', background:'var(--down-field-ink)', color:'#fff', cursor:'pointer' }}>
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}


// ── useContainerWidth ──────────────────────────────────────────────────────────
// Returns [measuredWidth, callbackRef, remeasure].
// Attach callbackRef as the ref prop on the wrapper div.
// Using a callback ref (not useRef) means it fires when the DOM node *attaches*,
// catching the null→element transition that a useLayoutEffect([ref]) approach misses.
// remeasure() lets callers force a re-measurement (e.g. after a loading guard clears).
// Guards against no-op re-renders: only calls setState when width actually changes.

function useContainerWidth(fallback = 820) {
  const [width, setWidth] = useState(fallback);
  const roRef   = useRef(null);
  const nodeRef = useRef(null);
  const callbackRef = useCallback(node => {
    nodeRef.current = node;
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!node) return;
    const ro = new ResizeObserver(entries => {
      const w = Math.round(entries[0].contentRect.width);
      if (w > 0) setWidth(prev => w !== prev ? w : prev);
    });
    ro.observe(node);
    const initial = Math.round(node.getBoundingClientRect().width);
    if (initial > 0) setWidth(prev => initial !== prev ? initial : prev);
    roRef.current = ro;
  }, []);
  const remeasure = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;
    const w = Math.round(node.getBoundingClientRect().width);
    if (w > 0) setWidth(prev => w !== prev ? w : prev);
  }, []);
  return [Math.max(width, fallback), callbackRef, remeasure];
}


// ── useOptionChain hook ────────────────────────────────────────────────────────

function useOptionChain(symbol, exchange, expiry) {
  const [chain,       setChain]       = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [kiteOk,      setKiteOk]      = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef           = useRef(null);
  const prevConnectedRef   = useRef(null); // tracks last seen kiteStatus.connected value
  const { kiteStatus }     = usePrices() ?? {};

  const doFetch = useCallback(async (retryCount = 0) => {
    if (!symbol || !expiry) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getOptionChain(symbol, expiry, exchange);
      if (data?._error) {
        // 503 on initial load (no chain yet) → auto-retry up to 2 times
        if (data._error.includes('503') && retryCount < 2) {
          setTimeout(() => doFetch(retryCount + 1), 2000);
          return;
        }
        setError(data._error); setKiteOk(false);
      } else {
        setChain(data); setKiteOk(true); setLastUpdated(Date.now());
      }
    } catch (e) {
      // Network-level 503 — retry silently on initial load
      if ((e.message?.includes('503') || e.message?.includes('Service Unavailable')) && retryCount < 2) {
        setTimeout(() => doFetch(retryCount + 1), 2000);
        return;
      }
      setError(e.message); setKiteOk(false);
    } finally {
      setLoading(false);
    }
  }, [symbol, exchange, expiry]);

  useEffect(() => {
    setChain(null);
    doFetch(0);
    timerRef.current = setInterval(() => doFetch(0), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [doFetch]);

  // Initial Kite status check
  useEffect(() => {
    getKiteStatus().then(s => setKiteOk(!!s?.connected)).catch(() => setKiteOk(false));
  }, []);

  // Refetch chain on false→true edge of kiteStatus.connected (morning-connect fix).
  // Reuses the existing 60s PriceContext poll — no new polling added.
  // Guard: only fires on the transition edge, never on every render where connected===true.
  useEffect(() => {
    const connected = kiteStatus?.connected ?? null;
    if (connected === true && prevConnectedRef.current === false) {
      doFetch(0);
    }
    prevConnectedRef.current = connected;
  }, [kiteStatus?.connected, doFetch]);

  return { chain, loading, error, kiteOk, refresh: doFetch, lastUpdated };
}


// ── InstrBar ───────────────────────────────────────────────────────────────────

function InstrBar({ symbol, exchange, setSymbol, setExchange, expiries, expiry, setExpiry,
                    meta, kiteOk, loading, symbolGroups, lastUpdated, onRefresh }) {
  const isMCX = exchange === 'MCX';
  const isBFO = exchange === 'BFO';

  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { const v = localStorage.getItem('options_auto_refresh'); return v === null ? true : v === 'true'; }
    catch { return true; }
  });
  const toggleAutoRefresh = () => setAutoRefresh(v => {
    const next = !v;
    try { localStorage.setItem('options_auto_refresh', String(next)); } catch {}
    return next;
  });
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { onRefresh(); }, 60_000);
    return () => clearInterval(id);
  }, [autoRefresh, onRefresh]);

  const handleExchangeSwitch = ex => {
    setExchange(ex);
    if (ex === 'NFO')      setSymbol('NIFTY');
    else if (ex === 'BFO') setSymbol('SENSEX');
    else                   setSymbol('NATURALGAS');
    setExpiry('');
  };

  // Build groups for the searchable select
  const nseGroups = useMemo(() => {
    const indices = symbolGroups.indices.map(id => ({ id, label: id }));
    const stocks  = symbolGroups.stocks.map(id => ({ id, label: id }));
    const result  = [];
    if (indices.length) result.push({ label:'Indices', items: indices });
    if (stocks.length)  result.push({ label:'Stocks (F&O)', items: stocks });
    return result;
  }, [symbolGroups]);

  const bfoGroups = useMemo(() =>
    [{ label:'BSE Indices', items: BSE_INSTRUMENTS.map(m => ({ id:m.id, label:m.label })) }],
    []
  );

  const mcxGroups = useMemo(() =>
    [{ label:'MCX Commodities', items: MCX_INSTRUMENTS.map(m => ({ id:m.id, label:m.label })) }],
    []
  );

  return (
    <div className="card" style={{ padding:'8px 12px', marginBottom:8 }}>
      {/* Row 1 */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:6 }}>

        {/* Exchange toggle */}
        <div style={{ display:'flex', gap:1, borderRadius:4, overflow:'hidden', border:'.5px solid var(--hair-strong)' }}>
          {['NFO','BFO','MCX'].map(ex => (
            <button key={ex} onClick={() => handleExchangeSwitch(ex)}
              style={{ fontSize:11, fontWeight:700, padding:'2px 8px', cursor:'pointer', border:'none',
                background: exchange===ex ? 'var(--amber-ink)' : 'var(--surface-2)',
                color:      exchange===ex ? '#fff' : 'var(--ink-2)' }}>
              {ex}
            </button>
          ))}
        </div>

        {/* Searchable symbol selector */}
        <SearchableSelect
          value={symbol}
          onChange={id => { setSymbol(id); setExpiry(''); }}
          groups={isMCX ? mcxGroups : isBFO ? bfoGroups : nseGroups}
          placeholder="Search symbol…"
        />

        {/* Exchange badge */}
        <span style={{
          fontSize:11, fontWeight:700, padding:'2px 6px', borderRadius:3, letterSpacing:'.04em',
          background: isMCX ? 'rgba(138,90,0,.15)' : isBFO ? 'rgba(26,74,170,.12)' : NSE_INDICES_SET.has(symbol) ? 'rgba(26,74,170,.12)' : 'rgba(26,106,26,.12)',
          color:      isMCX ? 'var(--amber)'     : isBFO ? 'var(--amber-ink)'        : NSE_INDICES_SET.has(symbol) ? 'var(--amber-ink)'       : 'var(--up-field-ink)',
          border: `.5px solid ${isMCX ? 'rgba(138,90,0,.3)' : isBFO ? 'rgba(26,74,170,.25)' : NSE_INDICES_SET.has(symbol) ? 'rgba(26,74,170,.25)' : 'rgba(26,106,26,.25)'}`,
        }}>
          {isMCX ? 'MCX' : isBFO ? 'BSE INDEX' : NSE_INDICES_SET.has(symbol) ? 'INDEX' : 'STOCK'}
        </span>

        {/* Clickable ticker — opens side panel */}
        <Ticker name={symbol} style={{ fontSize:11, fontWeight:700 }}/>

        {/* Prices */}
        {meta && (
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            {[
              { l: isMCX ? 'Fut' : 'Spot', v: meta.spot?.toLocaleString('en-IN') },
              ...(!isBFO ? [{ l:'Fut', v: meta.futPrice?.toLocaleString('en-IN') }] : []),
            ].map(p => (
              <span key={p.l} style={{ fontSize:11 }}>
                <span style={{ color:'var(--ink-3)', marginRight:2 }}>{p.l}</span>
                <span style={{ fontWeight:600 }}>{p.v}</span>
              </span>
            ))}
          </div>
        )}

        {/* Summary metrics */}
        {meta && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginLeft:'auto', alignItems:'center' }}>
            {[
              { l:'ATM',      v: meta.atm?.toLocaleString('en-IN') },
              { l:'ATM IV',   v: meta.atmIV ? `${meta.atmIV}%` : '—' },
              { l:'Straddle', v: meta.straddle ? `₹${meta.straddle}` : '—' },
              { l:'PCR',      v: meta.pcr ?? '—' },
              { l:'Max Pain', v: meta.maxPain?.toLocaleString('en-IN') ?? '—' },
              { l:'DTE',      v: meta.daysToExpiry != null ? `${meta.daysToExpiry}d` : '—' },
            ].map(m => (
              <span key={m.l} style={{ fontSize:11 }}>
                <span style={{ color:'var(--ink-3)', marginRight:2 }}>{m.l}</span>
                <span style={{ fontWeight:600 }}>{m.v}</span>
              </span>
            ))}

            <button
              onClick={() => window.open('/kite/login', '_blank')}
              style={{
                fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:3,
                cursor:'pointer', whiteSpace:'nowrap', marginLeft:4, border:'.5px solid',
                background:   kiteOk ? 'var(--up-field-ink)' : 'rgba(136,30,16,.15)',
                color:        kiteOk ? '#fff' : 'var(--down-field-ink)',
                borderColor:  kiteOk ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
              }}>
              {kiteOk ? '● Kite Connected' : '○ Kite Disconnected'}
            </button>

            {/* Manual refresh + timestamp */}
            <button onClick={onRefresh} disabled={loading}
              title="Refresh OI chain from Kite"
              style={{ fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:3,
                cursor: loading ? 'default' : 'pointer', border:'.5px solid var(--hair-strong)',
                background:'transparent', color:'var(--ink-2)',
                opacity: loading ? 0.45 : 1, transition:'opacity .15s' }}>
              ↻ Refresh
            </button>
            <button onClick={toggleAutoRefresh}
              title={autoRefresh ? 'Auto-refresh ON — click to disable' : 'Auto-refresh OFF — click to enable (1 min)'}
              style={{ fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:3,
                cursor:'pointer', border:'.5px solid var(--hair-strong)',
                background: autoRefresh ? 'var(--up-field-ink)' : 'transparent',
                color: autoRefresh ? '#fff' : 'var(--ink-3)',
                transition:'background .2s, color .2s' }}>
              ↻ Auto
            </button>
            {lastUpdated && !loading && (
              <span style={{ fontSize:11, color:'var(--ink-3)', whiteSpace:'nowrap', fontFamily:'monospace' }}>
                {new Date(lastUpdated).toLocaleTimeString('en-IN',
                  { hour:'2-digit', minute:'2-digit', second:'2-digit',
                    hour12:false, timeZone:'Asia/Kolkata' })} IST
              </span>
            )}
            {loading && <span style={{ fontSize:11, color:'var(--ink-3)' }}>↻ refreshing…</span>}
          </div>
        )}
      </div>

      {/* No expiry row here — expiry is selected inside the Option Chain & OI sub-tabs */}
    </div>
  );
}


// ── Shared expiry pill bar (used inside Option Chain + OI Analysis) ────────────

function ExpiryBar({ expiries, expiry, setExpiry }) {
  if (!expiries?.length) {
    return (
      <div style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic', marginBottom:6 }}>
        Connect Kite to load expiries
      </div>
    );
  }
  return (
    <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap', marginBottom:8 }}>
      <span style={{ fontSize:11, fontWeight:700, color:'var(--ink-3)', textTransform:'uppercase',
        letterSpacing:'.05em', marginRight:2 }}>Expiry:</span>
      {expiries.map(e => (
        <button key={e} onClick={() => setExpiry(e)}
          style={{ fontSize:11, fontWeight:500, padding:'2px 10px', borderRadius:3,
            border:'.5px solid', cursor:'pointer', transition:'all .15s',
            background:  expiry===e ? 'var(--amber-ink)' : 'var(--surface-2)',
            color:       expiry===e ? '#fff'          : 'var(--ink-2)',
            borderColor: expiry===e ? 'var(--amber-ink)' : 'var(--hair-strong)' }}>
          {fmtExpiry(e)}
        </button>
      ))}
    </div>
  );
}

// ── Multi-select expiry bar for OI Analysis Tab ────────────────────────────────
// Primary expiry (loaded chain) can be swapped; secondary expiries can be toggled.
function OIExpiryBar({ expiries, expiry, setExpiry, selectedExpiries, toggleExpiry, loadingExp }) {
  if (!expiries?.length) {
    return (
      <div style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic', marginBottom:6 }}>
        Connect Kite to load expiries
      </div>
    );
  }
  return (
    <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap', marginBottom:8 }}>
      <span style={{ fontSize:11, fontWeight:700, color:'var(--ink-3)', textTransform:'uppercase',
        letterSpacing:'.05em', marginRight:2 }}>Expiry:</span>
      {expiries.map(e => {
        const isPrimary  = e === expiry;
        const isSelected = selectedExpiries?.has(e);
        const isLoading  = e === loadingExp;
        return (
          <button key={e}
            onClick={() => toggleExpiry(e)}
            disabled={isLoading}
            title={isSelected ? 'Click to exclude' : 'Click to include'}
            style={{ fontSize:11, fontWeight: isSelected ? 700 : 500,
              padding:'2px 10px', borderRadius:3, border:'.5px solid', cursor:'pointer',
              transition:'all .15s', opacity: isLoading ? .55 : 1,
              background:  isSelected ? 'var(--amber-ink)' : 'var(--surface-2)',
              color:       isSelected ? '#fff'          : 'var(--ink-2)',
              borderColor: isSelected ? 'var(--amber-ink)' : 'var(--hair-strong)',
            }}>
            {isLoading ? '…' : fmtExpiry(e)}
          </button>
        );
      })}
      {selectedExpiries && selectedExpiries.size > 1 && (
        <span style={{ fontSize:11, color:'var(--ink-3)', marginLeft:4 }}>
          {selectedExpiries.size} expiries combined
        </span>
      )}
    </div>
  );
}


// ── OptionChainTab ─────────────────────────────────────────────────────────────

function OptionChainTab({ rows, meta, loading, error, expiries, expiry, setExpiry }) {
  // chainView: null = default OI/LTP, 'greeks' = Greeks columns, 'tv' = Time Value columns
  const [chainView,  setChainView]  = useState(null);
  const showGreeks = chainView === 'greeks';
  const showTV     = chainView === 'tv';
  const [rangeIdx,   setRangeIdx]   = useState(2); // default ±15

  // ── Mobile detection (≤600px) — reactive to resize ───────────────────────
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  useEffect(() => {
    const mq      = window.matchMedia('(max-width: 600px)');
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const ATM     = meta?.atm ?? 0;
  const rangeN  = RANGE_OPTS[rangeIdx].n;

  // ── Count-based filter: N strikes each side of ATM ────────────────────────
  // Works universally regardless of strike interval
  const filteredRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    if (rangeN >= 9999) return rows;
    const atmIdx = rows.findIndex(r => r.strike === ATM);
    if (atmIdx === -1) return rows; // ATM not found — show all
    const lo = Math.max(0, atmIdx - rangeN);
    const hi = Math.min(rows.length - 1, atmIdx + rangeN);
    return rows.slice(lo, hi + 1);
  }, [rows, rangeN, ATM]);

  const maxCeOI = useMemo(() => Math.max(1, ...filteredRows.map(r => r.ceOI)), [filteredRows]);
  const maxPeOI = useMemo(() => Math.max(1, ...filteredRows.map(r => r.peOI)), [filteredRows]);

  // ── Auto-scroll to ATM row — only on first load, not every 30 s refresh ──
  const scrollContainerRef = useRef(null);
  const atmRowRef          = useRef(null);
  const didScrollToATM     = useRef(false);

  useEffect(() => {
    if (!rows) { didScrollToATM.current = false; return; } // reset when data clears
    if (didScrollToATM.current) return;                     // already scrolled
    if (!atmRowRef.current || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const atmEl     = atmRowRef.current;
    const contRect  = container.getBoundingClientRect();
    const atmRect   = atmEl.getBoundingClientRect();
    const offset    = container.scrollTop + (atmRect.top - contRect.top) - contRect.height / 2;
    container.scrollTop = offset;
    didScrollToATM.current = true;
  }, [rows]);

  const itmBg = 'rgba(26,74,170,.06)';
  const atmBg = 'var(--atmbg)';

  // CE/PE side column counts (used for OI bar row colSpan and group header colSpan)
  // Desktop — Default: 6 cols; Greeks: 10 cols (+4); Time Value: 5 cols (LTP|CHG|IV|TV|Δ)
  // Mobile  — Always 3 cols per side (column set changes with chainView toggle)
  const ceCols = isMobile ? 3 : (showGreeks ? 10 : showTV ? 5 : 6);
  const peCols = isMobile ? 3 : (showGreeks ? 10 : showTV ? 5 : 6);

  if (error && !rows) {
    return (
      <div>
        <ExpiryBar expiries={expiries} expiry={expiry} setExpiry={setExpiry}/>
        <div className="card" style={{ padding:20, textAlign:'center' }}>
          <div style={{ fontSize:12, color:'var(--down-field-ink)', marginBottom:6 }}>Unable to load option chain</div>
          <div style={{ fontSize:11, color:'var(--ink-3)' }}>{error}</div>
          <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:6 }}>
            Make sure Kite is connected and you've done a daily login.
          </div>
        </div>
      </div>
    );
  }
  if (!rows && loading) return <div><ExpiryBar expiries={expiries} expiry={expiry} setExpiry={setExpiry}/><div style={{ padding:20, fontSize:11, color:'var(--ink-3)', textAlign:'center' }}>Loading option chain…</div></div>;
  if (!rows)            return <div><ExpiryBar expiries={expiries} expiry={expiry} setExpiry={setExpiry}/><div style={{ padding:20, fontSize:11, color:'var(--ink-3)', textAlign:'center' }}>Select an expiry to load the chain.</div></div>;

  return (
    <div>
      <ExpiryBar expiries={expiries} expiry={expiry} setExpiry={setExpiry}/>
      {/* Controls */}
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:7, flexWrap:'wrap' }}>
        {[
          { key: 'greeks', label: 'Greeks' },
          { key: 'tv',     label: 'Time Value' },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setChainView(v => v === key ? null : key)}
            style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:3,
              border:`.5px solid ${chainView===key?'var(--amber-ink)':'var(--hair-strong)'}`,
              color:chainView===key?'var(--amber-ink)':'var(--ink-2)', background:'transparent', cursor:'pointer' }}>
            {label}
          </button>
        ))}
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>Range:</span>
        {RANGE_OPTS.map((r,i) => (
          <button key={r.l} onClick={() => setRangeIdx(i)}
            style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:3, border:'.5px solid',
              cursor:'pointer', background:'transparent',
              color:rangeIdx===i?'var(--amber-ink)':'var(--ink-2)',
              borderColor:rangeIdx===i?'var(--amber-ink)':'var(--hair-strong)' }}>
            {r.l}
          </button>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:6, marginLeft:'auto', fontSize:11, color:'var(--ink-3)' }}>
          <span style={{ display:'inline-block', width:16, height:3, background:'var(--cebar)', borderRadius:1 }}/> CE OI
          <span style={{ display:'inline-block', width:16, height:3, background:'var(--pebar)', borderRadius:1 }}/> PE OI
        </div>
      </div>

      <div className="oc-outer">
        <div className="oc-scroll" ref={scrollContainerRef}>
          <table className={`oct${showGreeks?' show-greeks':''}`}>
            <thead>
              {isMobile ? (
                /* ── Mobile: 3 cols per side ─────────────────────────────── */
                <>
                  <tr className="grp-hdr" style={{ position:'sticky', top:0, zIndex:6, background:'var(--surface-3)', outline:'.5px solid var(--hair)', outlineOffset:'-1px' }}>
                    <th className="ce-grp" colSpan={3}>← Calls (CE)</th>
                    <th className="sk-grp">Strike</th>
                    <th className="pe-grp" colSpan={3}>Puts (PE) →</th>
                  </tr>
                  <tr className="col-hdr" style={{ position:'sticky', top:23, zIndex:5, background:'var(--surface-2)', boxShadow:'0 -23px 0 23px var(--surface-2)' }}>
                    {!showGreeks ? (
                      /* Default: Δ | Chg | LTP  ·  Strike  ·  LTP | Chg | Δ */
                      <>
                        <th className="cer">Δ</th>
                        <th className="cer">Chg</th>
                        <th className="cer" style={{ borderRight:'.5px solid var(--hair)' }}>LTP</th>
                        <th className="skh">Strike</th>
                        <th className="pel" style={{ borderLeft:'.5px solid var(--hair)' }}>LTP</th>
                        <th className="pel">Chg</th>
                        <th className="pel">Δ</th>
                      </>
                    ) : (
                      /* Greeks: IV% | Δ | OI  ·  Strike  ·  OI | Δ | IV% */
                      <>
                        <th className="cer">IV%</th>
                        <th className="cer">Δ</th>
                        <th className="cer" style={{ borderRight:'.5px solid var(--hair)' }}>OI</th>
                        <th className="skh">Strike</th>
                        <th className="pel" style={{ borderLeft:'.5px solid var(--hair)' }}>OI</th>
                        <th className="pel">Δ</th>
                        <th className="pel">IV%</th>
                      </>
                    )}
                  </tr>
                </>
              ) : (
                /* ── Desktop: full column set ─────────────────── */
                <>
                  <tr className="grp-hdr" style={{ position:'sticky', top:0, zIndex:6, background:'var(--surface-3)', outline:'.5px solid var(--hair)', outlineOffset:'-1px' }}>
                    {showGreeks && <th className="gcol ce-grp" colSpan={4}>← Calls Greeks</th>}
                    <th className="ce-grp" colSpan={showTV ? 5 : 6}>← Calls (CE)</th>
                    <th className="sk-grp">Strike</th>
                    <th className="pe-grp" colSpan={showTV ? 5 : 6}>Puts (PE) →</th>
                    {showGreeks && <th className="gcol pe-grp" colSpan={4}>Puts Greeks →</th>}
                  </tr>
                  <tr className="col-hdr" style={{ position:'sticky', top:23, zIndex:5, background:'var(--surface-2)', boxShadow:'0 -23px 0 23px var(--surface-2)' }}>
                    {showGreeks && <><th className="gcol cer">Vol</th><th className="gcol cer">Vega</th><th className="gcol cer">Gamma</th><th className="gcol cer">Theta</th></>}
                    {!showTV && <><th className="cer">OI chg%</th><th className="cer">OI</th></>}
                    {showTV
                      ? <><th className="cer">Intrinsic</th><th className="cer">Time Val</th></>
                      : null}
                    <th className="cer">IV%</th>
                    <th className="cer">Δ</th>
                    <th className="cer">Chg</th>
                    <th className="cer" style={{ borderRight:'.5px solid var(--hair)' }}>Call LTP</th>
                    <th className="skh">Strike / PCR</th>
                    <th className="pel" style={{ borderLeft:'.5px solid var(--hair)' }}>Put LTP</th>
                    <th className="pel">Chg</th>
                    <th className="pel">Δ</th>
                    <th className="pel">IV%</th>
                    {showTV
                      ? <><th className="pel">Time Val</th><th className="pel">Intrinsic</th></>
                      : null}
                    {!showTV && <><th className="pel">OI</th><th className="pel">OI chg%</th></>}
                    {showGreeks && <><th className="gcol pel">Theta</th><th className="gcol pel">Gamma</th><th className="gcol pel">Vega</th><th className="gcol pel">Vol</th></>}
                  </tr>
                </>
              )}
            </thead>
            <tbody>
              {filteredRows.map(row => {
                const isATM = row.strike === ATM;
                const itmCE = row.strike < ATM;
                const itmPE = row.strike > ATM;
                const cePct = row.ceOI / maxCeOI;
                const pePct = row.peOI / maxPeOI;
                const ceSideBg = isATM ? atmBg : itmCE ? itmBg : 'transparent';
                const peSideBg = isATM ? atmBg : itmPE ? itmBg : 'transparent';
                const skBg     = isATM ? atmBg : 'var(--surface-2)';

                const fmtOIChg = v => v >= 0
                  ? <span className="chg-up">+{v}%</span>
                  : <span className="chg-dn">{v}%</span>;

                // ── Shared strike cell (used by both mobile and desktop) ─────
                const strikeCell = (
                  <td className="skc" style={{ background:skBg, borderLeft:'.5px solid var(--hair)', borderRight:'.5px solid var(--hair)' }}>
                    <span className="sk-num">
                      {row.strike.toLocaleString('en-IN')}
                      {isATM && <span className="atm-b">ATM</span>}
                    </span>
                    {/* PCR per-row only on desktop — too noisy on mobile */}
                    {!isMobile && <span className="sk-pcr">PCR {row.pcr}</span>}
                  </td>
                );

                return (
                  <React.Fragment key={row.strike}>
                    <tr ref={isATM ? atmRowRef : null}>
                      {isMobile ? (
                        /* ── Mobile: 3 cols per side ───────────────────────── */
                        !showGreeks ? (
                          /* Default: Δ | Chg | LTP  ·  Strike  ·  LTP | Chg | Δ */
                          <>
                            <td className="cer" style={{ background:ceSideBg }}>
                              <span className="dim">{row.ceDelta || '—'}</span>
                            </td>
                            <td className="cer" style={{ background:ceSideBg }}>
                              <FmtLTPChg chg={row.ceLTPChg ?? 0} pct={row.ceLTPChgPct ?? 0} stacked/>
                            </td>
                            <td className="cer" style={{ background:ceSideBg, borderRight:'.5px solid var(--hair)' }}>
                              <span className="ltp-bold">{row.ceLTP || '—'}</span>
                            </td>
                            {strikeCell}
                            <td className="pel" style={{ background:peSideBg, borderLeft:'.5px solid var(--hair)' }}>
                              <span className="ltp-bold">{row.peLTP || '—'}</span>
                            </td>
                            <td className="pel" style={{ background:peSideBg }}>
                              <FmtLTPChg chg={row.peLTPChg ?? 0} pct={row.peLTPChgPct ?? 0} stacked/>
                            </td>
                            <td className="pel" style={{ background:peSideBg }}>
                              <span className="dim">{row.peDelta || '—'}</span>
                            </td>
                          </>
                        ) : (
                          /* Greeks: IV% | Δ | OI  ·  Strike  ·  OI | Δ | IV% */
                          <>
                            <td className="cer" style={{ background:ceSideBg }}>
                              <span className="dim">{row.ceIV > 0 && row.ceIV <= 150 ? `${row.ceIV}%` : row.ceIV > 150 ? '>150%' : '—'}</span>
                            </td>
                            <td className="cer" style={{ background:ceSideBg }}>
                              <span className="dim">{row.ceDelta || '—'}</span>
                            </td>
                            <td className="cer" style={{ background:ceSideBg, borderRight:'.5px solid var(--hair)' }}>
                              <span className="dim">{fmtOI(row.ceOI)}</span>
                            </td>
                            {strikeCell}
                            <td className="pel" style={{ background:peSideBg, borderLeft:'.5px solid var(--hair)' }}>
                              <span className="dim">{fmtOI(row.peOI)}</span>
                            </td>
                            <td className="pel" style={{ background:peSideBg }}>
                              <span className="dim">{row.peDelta || '—'}</span>
                            </td>
                            <td className="pel" style={{ background:peSideBg }}>
                              <span className="dim">{row.peIV > 0 && row.peIV <= 150 ? `${row.peIV}%` : row.peIV > 150 ? '>150%' : '—'}</span>
                            </td>
                          </>
                        )
                      ) : (
                        /* ── Desktop: full column set ──────────── */
                        (() => {
                          // Intrinsic / Time Value helpers (use actual spot from meta)
                          const underlyingSpot = meta?.spot ?? ATM;
                          const ceIV_val  = row.ceLTP > 0 ? Math.max(0, underlyingSpot - row.strike) : 0;
                          const ceTV_val  = row.ceLTP > 0 ? Math.max(0, row.ceLTP - ceIV_val)       : 0;
                          const peIV_val  = row.peLTP > 0 ? Math.max(0, row.strike - underlyingSpot) : 0;
                          const peTV_val  = row.peLTP > 0 ? Math.max(0, row.peLTP - peIV_val)       : 0;
                          const fmtTV = v => v > 0 ? v.toFixed(1) : '0.0';
                          return (
                            <>
                              {showGreeks && <>
                                <td className="gcol cer" style={{ background:ceSideBg }}><span className="dim">{row.ceVolume ? fmtOI(row.ceVolume) : '—'}</span></td>
                                <td className="gcol cer" style={{ background:ceSideBg }}><span className="dim">{row.ceVega  || '—'}</span></td>
                                <td className="gcol cer" style={{ background:ceSideBg }}><span className="dim">{row.ceGamma || '—'}</span></td>
                                <td className="gcol cer" style={{ background:ceSideBg }}><span className="dim">{row.ceTheta || '—'}</span></td>
                              </>}
                              {!showTV && <>
                                <td className="cer" style={{ background:ceSideBg }}>{fmtOIChg(row.ceDeltaOI)}</td>
                                <td className="cer" style={{ background:ceSideBg }}><span className="dim">{fmtOI(row.ceOI)}</span></td>
                              </>}
                              {showTV && <>
                                <td className="cer" style={{ background:ceSideBg }}><span className="dim">{fmtTV(ceIV_val)}</span></td>
                                <td className="cer" style={{ background:ceSideBg }}><span className="dim">{fmtTV(ceTV_val)}</span></td>
                              </>}
                              <td className="cer" style={{ background:ceSideBg }}><span className="dim">{row.ceIV > 0 && row.ceIV <= 150 ? `${row.ceIV}%` : row.ceIV > 150 ? '>150%' : '—'}</span></td>
                              <td className="cer" style={{ background:ceSideBg }}><span className="dim">{row.ceDelta || '—'}</span></td>
                              <td className="cer" style={{ background:ceSideBg }}>
                                <FmtLTPChg chg={row.ceLTPChg ?? 0} pct={row.ceLTPChgPct ?? 0}/>
                              </td>
                              <td className="cer" style={{ background:ceSideBg, borderRight:'.5px solid var(--hair)' }}>
                                <span className="ltp-bold">{row.ceLTP || '—'}</span>
                              </td>
                              {strikeCell}
                              <td className="pel" style={{ background:peSideBg, borderLeft:'.5px solid var(--hair)' }}>
                                <span className="ltp-bold">{row.peLTP || '—'}</span>
                              </td>
                              <td className="pel" style={{ background:peSideBg }}>
                                <FmtLTPChg chg={row.peLTPChg ?? 0} pct={row.peLTPChgPct ?? 0}/>
                              </td>
                              <td className="pel" style={{ background:peSideBg }}><span className="dim">{row.peDelta || '—'}</span></td>
                              <td className="pel" style={{ background:peSideBg }}><span className="dim">{row.peIV > 0 && row.peIV <= 150 ? `${row.peIV}%` : row.peIV > 150 ? '>150%' : '—'}</span></td>
                              {showTV && <>
                                <td className="pel" style={{ background:peSideBg }}><span className="dim">{fmtTV(peTV_val)}</span></td>
                                <td className="pel" style={{ background:peSideBg }}><span className="dim">{fmtTV(peIV_val)}</span></td>
                              </>}
                              {!showTV && <>
                                <td className="pel" style={{ background:peSideBg }}><span className="dim">{fmtOI(row.peOI)}</span></td>
                                <td className="pel" style={{ background:peSideBg }}>{fmtOIChg(row.peDeltaOI)}</td>
                              </>}
                              {showGreeks && <>
                                <td className="gcol pel" style={{ background:peSideBg }}><span className="dim">{row.peTheta || '—'}</span></td>
                                <td className="gcol pel" style={{ background:peSideBg }}><span className="dim">{row.peGamma || '—'}</span></td>
                                <td className="gcol pel" style={{ background:peSideBg }}><span className="dim">{row.peVega  || '—'}</span></td>
                                <td className="gcol pel" style={{ background:peSideBg }}><span className="dim">{row.peVolume ? fmtOI(row.peVolume) : '—'}</span></td>
                              </>}
                            </>
                          );
                        })()
                      )}
                    </tr>
                    {/* OI bar row */}
                    <tr style={{ borderBottom:'none', height:3 }}>
                      <td colSpan={ceCols} style={{ padding:0, borderBottom:'none', background:ceSideBg }}>
                        <div style={{ width:'100%', height:3, display:'flex', justifyContent:'flex-end' }}>
                          <div style={{ width:`${Math.round(cePct*100)}%`, height:3, background:'var(--cebar)', opacity:.9, borderRadius:'2px 0 0 2px' }}/>
                        </div>
                      </td>
                      <td style={{ padding:0, borderBottom:'none', background:skBg, borderLeft:'.5px solid var(--hair)', borderRight:'.5px solid var(--hair)' }}/>
                      <td colSpan={peCols} style={{ padding:0, borderBottom:'none', background:peSideBg }}>
                        <div style={{ width:'100%', height:3, display:'flex', justifyContent:'flex-start' }}>
                          <div style={{ width:`${Math.round(pePct*100)}%`, height:3, background:'var(--pebar)', opacity:.9, borderRadius:'0 2px 2px 0' }}/>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Bottom strip */}
        {meta && (
          <div className="bot-strip">
            {[
              { l:'PCR',      v: meta.pcr },
              { l:'Max Pain', v: meta.maxPain?.toLocaleString('en-IN') },
              { l:'ATM IV',   v: meta.atmIV ? `${meta.atmIV}%` : '—' },
              { l:'Straddle', v: meta.straddle ? `₹${meta.straddle}` : '—' },
              { l:'DTE',      v: meta.daysToExpiry != null ? `${meta.daysToExpiry} days` : '—' },
              { l:'Lot',      v: meta.lotSize },
            ].map(m => (
              <div key={m.l} className="bot-item">
                <span className="bot-lbl">{m.l}</span>
                <span className="bot-val">{m.v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ── OI Time Range Slider ──────────────────────────────────────────────────────
// Dual-handle slider that snaps to available snapshot timestamps.
// Single-row layout: [PRESET label] [pills] [track] [readout] [replay controls]
// Wraps to two rows on narrow viewports (flexWrap on outer container).
// availableSpanMinutes: how many minutes of history this slider's data actually covers.
// Used to grey-out individual preset pills that exceed the available span.
// Omit (or pass Infinity) for the OI slider — all pills always available there.
function OITimeSlider({ snaps, fromIdx, toIdx, onFrom, onTo, replayActive, replayPaused, onReplay, onStop, onPause, onResume, disabled, availableSpanMinutes = Infinity }) {
  const trackRef = useRef(null);
  const dragging = useRef(null);

  const n = snaps.length;

  if (disabled || n < 2) {
    return (
      <div style={{ padding:'6px 14px 4px', opacity:0.4, pointerEvents:'none', cursor:'not-allowed', userSelect:'none' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
            <span style={{ fontSize:11, color:'var(--ink-3)', fontWeight:600,
              textTransform:'uppercase', letterSpacing:'.04em', marginRight:2 }}>Preset:</span>
            {['5m','15m','30m','1h','2h','Full Day'].map(p => (
              <button key={p} style={{ fontSize:11, padding:'2px 7px', borderRadius:3, lineHeight:1.6,
                border:'.5px solid var(--hair-strong)', background:'transparent', color:'var(--ink-2)',
                cursor:'not-allowed' }}>{p}</button>
            ))}
          </div>
          <div style={{ flex:'1 1 160px', height:20, background:'var(--hair)', borderRadius:4, position:'relative', minWidth:160 }}>
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, color:'var(--ink-3)' }}>
              Scrubber enables once intraday snapshots accumulate
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pctOf      = idx => idx / (n - 1);
  const fromPct    = pctOf(fromIdx) * 100;
  const toPct      = pctOf(toIdx)   * 100;
  const idxFromPct = pct => Math.max(0, Math.min(n - 1, Math.round(pct / 100 * (n - 1))));

  // Returns true if a snap timestamp falls on an exact hour (:00)
  const isHourBoundary = s => {
    const min = parseInt((s.time || '').split(':')[1], 10);
    return min === 0;
  };

  const handleMouseDown = (side, e) => {
    e.preventDefault();
    dragging.current = side;
    const move = ev => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pct = Math.max(0, Math.min(100, (ev.clientX - rect.left) / rect.width * 100));
      const idx = idxFromPct(pct);
      if (side === 'from') onFrom(Math.min(idx, toIdx));
      else                 onTo(Math.max(idx, fromIdx));
    };
    const up = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup',   up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup',   up);
  };

  const handleTrackClick = e => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = Math.max(0, Math.min(100, (e.clientX - rect.left) / rect.width * 100));
    const idx = idxFromPct(pct);
    if (Math.abs(idx - fromIdx) <= Math.abs(idx - toIdx)) onFrom(Math.min(idx, toIdx));
    else                                                   onTo(Math.max(idx, fromIdx));
  };

  const applyPreset = preset => {
    if (preset === 'Full') { onFrom(0); onTo(n - 1); return; }
    const mins = preset === '5m' ? 5 : preset === '15m' ? 15 : preset === '30m' ? 30
               : preset === '1h' ? 60 : 120;
    const newTo = n - 1;
    const toTs  = snaps[newTo]?.ts;
    let targetTs = null;
    if (toTs) {
      targetTs = toTs - mins * 60;
    } else {
      const parts = (snaps[newTo]?.time ?? '').split(':').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]))
        targetTs = (parts[0] * 60 + parts[1]) * 60;
    }
    if (targetTs == null) return;
    let bestIdx = 0, bestDiff = Infinity;
    snaps.forEach((s, i) => {
      const ts = s.ts ?? (() => {
        const p = (s.time || '').split(':').map(Number);
        return p.length === 2 ? (p[0] * 60 + p[1]) * 60 : null;
      })();
      if (ts == null) return;
      const d = Math.abs(ts - targetTs);
      if (d < bestDiff) { bestDiff = d; bestIdx = i; }
    });
    onFrom(Math.min(bestIdx, Math.max(0, newTo - 1)));
    onTo(newTo);
  };

  const isPresetActive = preset => {
    if (preset === 'Full') return fromIdx === 0 && toIdx === n - 1;
    const mins = preset === '5m' ? 5 : preset === '15m' ? 15 : preset === '30m' ? 30
               : preset === '1h' ? 60 : 120;
    const toTs   = snaps[toIdx]?.ts;
    const fromTs = snaps[fromIdx]?.ts;
    if (toTs && fromTs) return Math.abs((toTs - fromTs) / 60 - mins) <= 3;
    const toTime   = snaps[toIdx]?.time   ?? '';
    const fromTime = snaps[fromIdx]?.time ?? '';
    if (!toTime || !fromTime) return false;
    const [th, tm] = toTime.split(':').map(Number);
    const [fh, fm] = fromTime.split(':').map(Number);
    return !isNaN(th) && !isNaN(fh) && Math.abs((th * 60 + tm) - (fh * 60 + fm) - mins) <= 3;
  };

  const HANDLE_R = 7;
  const btnBase  = { fontSize:11, padding:'2px 7px', borderRadius:3, cursor:'pointer', lineHeight:1.6 };

  return (
    <div style={{ padding:'6px 14px 4px', userSelect:'none' }}>
      {/* Single flex row — wraps to two lines on narrow containers */}
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>

        {/* ── Pills group (no-wrap) ── */}
        <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
          <span style={{ fontSize:11, color:'var(--ink-3)', fontWeight:600,
            textTransform:'uppercase', letterSpacing:'.04em', marginRight:2 }}>Preset:</span>
          {['5m','15m','30m','1h','2h','Full'].map(preset => {
            const active = isPresetActive(preset);
            const presetMins = { '5m':5, '15m':15, '30m':30, '1h':60, '2h':120 };
            const pillDisabled = preset !== 'Full' && (presetMins[preset] ?? 0) > availableSpanMinutes;
            return (
              <button key={preset}
                onClick={() => { if (!pillDisabled) applyPreset(preset); }}
                style={{ ...btnBase,
                  fontWeight: active ? 700 : 500,
                  border: active && !pillDisabled ? 'none' : '.5px solid var(--hair-strong)',
                  background: active && !pillDisabled ? 'var(--amber-ink)' : 'transparent',
                  color: pillDisabled ? 'var(--ink-4)' : active ? '#fff' : 'var(--ink-2)',
                  opacity: pillDisabled ? 0.4 : 1,
                  cursor: pillDisabled ? 'not-allowed' : 'pointer' }}>
                {preset === 'Full' ? 'Full Day' : `Last ${preset}`}
              </button>
            );
          })}
        </div>

        {/* ── Track + readout + replay (flex-grow, wraps as a unit below pills on small screens) ── */}
        <div style={{ flex:'1 1 160px', display:'flex', alignItems:'center', gap:6, minWidth:160 }}>
          {/* Track */}
          <div ref={trackRef} onClick={handleTrackClick}
            style={{ flex:1, position:'relative', height:20, cursor:'pointer' }}>
            {/* Background rail */}
            <div style={{ position:'absolute', top:'50%', left:0, right:0, height:3,
              transform:'translateY(-50%)', background:'var(--hair-strong)', borderRadius:2 }}/>
            {/* Active range fill */}
            <div style={{ position:'absolute', top:'50%', height:3, transform:'translateY(-50%)',
              background:'var(--amber-ink)', borderRadius:2, opacity:.7,
              left:`${fromPct}%`, width:`${toPct - fromPct}%` }}/>
            {/* Ticks: only hour boundaries are rendered; all snaps remain snap targets */}
            {snaps.map((s, i) => {
              if (!isHourBoundary(s) && i !== 0 && i !== n - 1) return null;
              const inRange = i >= fromIdx && i <= toIdx;
              return (
                <div key={i} style={{ position:'absolute', top:'50%',
                  transform:'translateX(-50%) translateY(-50%)',
                  left:`${pctOf(i) * 100}%`, width:1.5, height:10,
                  background: inRange ? 'var(--amber-ink)' : 'var(--hair-strong)',
                  borderRadius:1, opacity:.8 }}/>
              );
            })}
            {/* Hour labels below ticks */}
            {snaps.map((s, i) => {
              if (!isHourBoundary(s) && i !== 0 && i !== n - 1) return null;
              const isHandle = i === fromIdx || i === toIdx;
              return (
                <div key={i} style={{ position:'absolute', top:'calc(50% + 7px)',
                  left:`${pctOf(i) * 100}%`, transform:'translateX(-50%)',
                  fontSize:9, color: isHandle ? 'var(--amber-ink)' : 'var(--ink-3)',
                  fontWeight: isHandle ? 700 : 400,
                  whiteSpace:'nowrap', pointerEvents:'none', fontFamily:'monospace' }}>
                  {s.time}
                </div>
              );
            })}
            {/* FROM handle */}
            <div onMouseDown={e => handleMouseDown('from', e)}
              style={{ position:'absolute', top:'50%', transform:'translate(-50%,-50%)',
                left:`${fromPct}%`, width:HANDLE_R*2, height:HANDLE_R*2,
                borderRadius:'50%', background:'var(--amber-ink)', border:'2px solid var(--paper)',
                boxShadow:'0 1px 4px rgba(0,0,0,.3)', cursor:'ew-resize', zIndex:2 }}/>
            {/* TO handle */}
            <div onMouseDown={e => handleMouseDown('to', e)}
              style={{ position:'absolute', top:'50%', transform:'translate(-50%,-50%)',
                left:`${toPct}%`, width:HANDLE_R*2, height:HANDLE_R*2,
                borderRadius:'50%', background:'var(--amber-ink)', border:'2px solid var(--paper)',
                boxShadow:'0 1px 4px rgba(0,0,0,.3)', cursor:'ew-resize', zIndex:2 }}/>
          </div>

          {/* Range readout */}
          <span style={{ fontSize:11, color:'var(--ink-3)', fontFamily:'monospace', flexShrink:0 }}>
            {snaps[fromIdx]?.time ?? '—'} → {snaps[toIdx]?.time ?? '—'}
          </span>

          {/* Replay controls */}
          {onReplay && (
            <div style={{ display:'flex', gap:3, flexShrink:0 }}>
              {replayActive ? (
                <>
                  {/* Pause — freezes position so user can analyse */}
                  <button onClick={replayPaused ? onResume : onPause}
                    title={replayPaused ? 'Resume replay' : 'Pause replay'}
                    style={{ ...btnBase, fontWeight:700, fontSize:12,
                      border:'.5px solid var(--hair-strong)', background:'transparent',
                      color: replayPaused ? 'var(--amber-ink)' : 'var(--ink-2)', letterSpacing:1 }}>
                    {replayPaused ? '▶' : '⏸'}
                  </button>
                  {/* Stop — resets to full range */}
                  <button onClick={onStop}
                    title="Stop replay"
                    style={{ ...btnBase, fontWeight:700, fontSize:12,
                      border:'.5px solid var(--hair-strong)', background:'transparent',
                      color:'var(--ink-2)', letterSpacing:1 }}>
                    ■
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => onReplay(1000)}
                    title="Replay at 1 step/s"
                    style={{ ...btnBase, border:'.5px solid var(--hair-strong)',
                      background:'transparent', color:'var(--ink-2)' }}>
                    ▶
                  </button>
                  <button onClick={() => onReplay(500)}
                    title="Replay at 2 steps/s"
                    style={{ ...btnBase, border:'.5px solid var(--hair-strong)',
                      background:'transparent', color:'var(--ink-2)' }}>
                    ▶▶
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── OIAnalysisTab ──────────────────────────────────────────────────────────────

function OIAnalysisTab({ rows, meta, expiries, expiry, setExpiry, symbol, exchange,
                         loading, error, kiteOk }) {
  const [strikeFilter,     setStrikeFilter]     = useState(10);
  const [priceStrikeFilter, setPriceStrikeFilter] = useState(10);
  const [hoveredIdx,       setHoveredIdx]        = useState(null);
  const [ribbonHoveredIdx, setRibbonHoveredIdx]  = useState(null);
  // Multi-expiry: set of selected expiry strings (primary always included)
  const [selectedExpiries, setSelectedExpiries]  = useState(() => new Set([expiry].filter(Boolean)));
  // Cached extra chains: { expiryStr: rows[] }
  const [extraChains,      setExtraChains]       = useState({});
  const [loadingExp,       setLoadingExp]        = useState(null);
  // Phase-2: intraday PCR history (timeline chart)
  const [pcrHistory,       setPcrHistory]        = useState([]);
  const [niftyIntraday,    setNiftyIntraday]     = useState([]);
  // Max Pain history (last 5 sessions)
  const [maxPainHistory,   setMaxPainHistory]    = useState([]);
  // Phase-2: per-strike snapshots for historical PCR/CPR lines (6 fixed offsets)
  const [strikeHistory,    setStrikeHistory]     = useState([]); // [{label,time,strikes}]
  const [historyDate,      setHistoryDate]       = useState(null); // YYYY-MM-DD from _display_date()
  // Full intraday per-strike timeline for the time range slider (~5-min cadence, all day)
  const [strikeTimeline,   setStrikeTimeline]    = useState([]); // [{time,ts,strikes}]
  // Time range slider state — OI chart
  const [sliderFrom,       setSliderFrom]        = useState(0);
  const [sliderTo,         setSliderTo]          = useState(0); // will be updated to n-1 when snaps arrive
  // Time range slider state — Premium chart (fully independent)
  const [priceSliderFrom,  setPriceSliderFrom]   = useState(0);
  const [priceSliderTo,    setPriceSliderTo]     = useState(0);
  // Which time-offset lines are visible (toggled via legend)
  const [visibleOffsets,   setVisibleOffsets]    = useState(
    () => new Set(['Now','-5m','-15m','-30m','-1h','-2h'])
  );
  // IV / HV history for charts
  const [ivHistory,        setIvHistory]         = useState([]);
  const [ivMeta,           setIvMeta]            = useState(null);
  // Shared hover date for IV chart sync (string YYYY-MM-DD or null)
  const [ivHoverDate,      setIvHoverDate]       = useState(null);
  // D-1 close line toggle
  const [showPrevClose,    setShowPrevClose]      = useState(false);
  // Drift arrow layer toggle
  const [showArrows,       setShowArrows]         = useState(true);
  // Replay — OI chart
  const [replayActive,      setReplayActive]       = useState(false);
  const [replayPaused,      setReplayPaused]       = useState(false);
  const replayRef       = useRef(null);
  const replaySpeedRef  = useRef(1000);
  const snapsLenRef     = useRef(0);
  // Replay — Premium chart (fully independent)
  const [priceReplayActive, setPriceReplayActive]  = useState(false);
  const [priceReplayPaused, setPriceReplayPaused]  = useState(false);
  const priceReplayRef  = useRef(null);
  const priceReplaySpeedRef = useRef(1000);
  const priceSnapsLenRef    = useRef(0);
  const [priceHoveredIdx,  setPriceHoveredIdx]  = useState(null);

  // Responsive SVG widths — track container pixel width so 1 SVG unit = 1 screen px.
  const [oiSvgW,      oiChartRef,      remeasureOi]      = useContainerWidth(820);
  const [premiumSvgW, premiumChartRef, remeasurePremium] = useContainerWidth(820);

  const rawATM   = meta?.atm      ?? 0;
  const spot     = meta?.spot     ?? rawATM;
  const prevClose = meta?.prevClose ?? null;
  // When meta.atm is unavailable (fallback mode / Kite disconnected), approximate ATM
  // as the centre of the known strike range so the ±N pills still work.
  const ATM = rawATM > 0 ? rawATM : (() => {
    const strikes = Object.keys(strikeHistory[0]?.strikes ?? {}).map(Number).sort((a, b) => a - b);
    return strikes.length ? strikes[Math.floor(strikes.length / 2)] : 0;
  })();

  // Build ordered snapshot timeline for the time slider (oldest → newest)
  // Uses the full intraday timeline (~5-min cadence) for full-day coverage.
  // Falls back to strikeHistory if timeline not yet loaded.
  const sliderSnaps = useMemo(() => {
    if (strikeTimeline.length) {
      // Timeline entries already have {time, ts, strikes} — sort by ts for safety
      return [...strikeTimeline].sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0));
    }
    if (!strikeHistory.length) return [];
    // Fallback: sort non-Now entries by time, append Now at end
    const others = strikeHistory
      .filter(s => s.label !== 'Now')
      .sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
    const nowEntry = strikeHistory.find(s => s.label === 'Now');
    if (nowEntry) others.push(nowEntry);
    return others;
  }, [strikeTimeline, strikeHistory]);

  // Snapshots that actually carry price data (ceLTP != null on at least one strike).
  // This is the authoritative source for the premium chart's scrubber — distinct from
  // sliderSnaps which may have hundreds of OI-only snaps from before price columns were deployed.
  const priceSnaps = useMemo(() =>
    sliderSnaps.filter(s => Object.values(s.strikes ?? {}).some(st => st.ceLTP != null)),
    [sliderSnaps]
  );

  // Minutes spanned by the price timeline (first → last price snap).
  // Drives per-pill enabling: a pill is enabled only if this span covers its interval.
  const priceAvailableSpanMinutes = useMemo(() => {
    if (priceSnaps.length < 2) return 0;
    return ((priceSnaps[priceSnaps.length - 1].ts ?? 0) - (priceSnaps[0].ts ?? 0)) / 60;
  }, [priceSnaps]);

  // Keep OI slider range clamped + default to full range when snapshots first arrive
  useEffect(() => {
    if (!sliderSnaps.length) return;
    setSliderFrom(prev => Math.min(prev, sliderSnaps.length - 1));
    setSliderTo(sliderSnaps.length - 1);
  }, [sliderSnaps.length]);
  useEffect(() => { snapsLenRef.current = sliderSnaps.length; }, [sliderSnaps.length]);

  // Keep Premium slider range clamped — gated on PRICE snapshot count, not OI count
  useEffect(() => {
    if (!priceSnaps.length) return;
    setPriceSliderFrom(prev => Math.min(prev, priceSnaps.length - 1));
    setPriceSliderTo(priceSnaps.length - 1);
  }, [priceSnaps.length]);
  useEffect(() => { priceSnapsLenRef.current = priceSnaps.length; }, [priceSnaps.length]);

  const stopReplay = useCallback(() => {
    if (replayRef.current) { clearInterval(replayRef.current); replayRef.current = null; }
    setReplayActive(false);
    setReplayPaused(false);
  }, []);

  const _startInterval = useCallback((speed) => {
    if (replayRef.current) clearInterval(replayRef.current);
    replayRef.current = setInterval(() => {
      setSliderTo(prev => {
        if (prev >= snapsLenRef.current - 1) {
          clearInterval(replayRef.current);
          replayRef.current = null;
          setReplayActive(false);
          setReplayPaused(false);
          return prev;
        }
        return prev + 1;
      });
    }, speed);
  }, []);

  const startReplay = useCallback((speed) => {
    stopReplay();
    replaySpeedRef.current = speed;
    setSliderFrom(0);
    setSliderTo(0);
    setReplayActive(true);
    setReplayPaused(false);
    _startInterval(speed);
  }, [stopReplay, _startInterval]);

  const pauseReplay = useCallback(() => {
    if (replayRef.current) { clearInterval(replayRef.current); replayRef.current = null; }
    setReplayPaused(true);
  }, []);

  const resumeReplay = useCallback(() => {
    setReplayPaused(false);
    _startInterval(replaySpeedRef.current);
  }, [_startInterval]);

  // Cleanup OI interval on unmount
  useEffect(() => () => { if (replayRef.current) clearInterval(replayRef.current); }, []);

  // ── Premium chart replay (fully independent) ─────────────────────────────
  const stopPriceReplay = useCallback(() => {
    if (priceReplayRef.current) { clearInterval(priceReplayRef.current); priceReplayRef.current = null; }
    setPriceReplayActive(false);
    setPriceReplayPaused(false);
  }, []);

  const _startPriceInterval = useCallback((speed) => {
    if (priceReplayRef.current) clearInterval(priceReplayRef.current);
    priceReplayRef.current = setInterval(() => {
      setPriceSliderTo(prev => {
        if (prev >= priceSnapsLenRef.current - 1) {
          clearInterval(priceReplayRef.current);
          priceReplayRef.current = null;
          setPriceReplayActive(false);
          setPriceReplayPaused(false);
          return prev;
        }
        return prev + 1;
      });
    }, speed);
  }, []);

  const startPriceReplay = useCallback((speed) => {
    stopPriceReplay();
    priceReplaySpeedRef.current = speed;
    setPriceSliderFrom(0);
    setPriceSliderTo(0);
    setPriceReplayActive(true);
    setPriceReplayPaused(false);
    _startPriceInterval(speed);
  }, [stopPriceReplay, _startPriceInterval]);

  const pausePriceReplay = useCallback(() => {
    if (priceReplayRef.current) { clearInterval(priceReplayRef.current); priceReplayRef.current = null; }
    setPriceReplayPaused(true);
  }, []);

  const resumePriceReplay = useCallback(() => {
    setPriceReplayPaused(false);
    _startPriceInterval(priceReplaySpeedRef.current);
  }, [_startPriceInterval]);

  useEffect(() => () => { if (priceReplayRef.current) clearInterval(priceReplayRef.current); }, []);

  // Fetch PCR history + strike history on every auto-refresh (rows change).
  //
  // DEBOUNCED by CHAIN_FETCH_DEBOUNCE_MS.  Two of these five endpoints hit Kite
  // (oi-strike-timeline and nifty-intraday), and this effect re-runs on every
  // `symbol` change, so clicking quickly through chains fired 2 Kite calls per
  // click with nothing pacing them — 2 chains in a second is ~4 req/s against a
  // documented limit of 3, and the capture sweep is already using most of that
  // budget on the same API key.  The timer is cleared by the cleanup below,
  // which React runs both on a further symbol/expiry change and on unmount, so
  // a superseded fetch is never issued at all.
  useEffect(() => {
    if (!symbol || !expiry) return;
    let cancelled = false;
    const fetch_ = async () => {
      try {
        const [agg, strikes, timeline, mpHist, nifty] = await Promise.all([
          getOIPCRHistory(symbol, expiry, exchange || 'NFO'),
          getOIStrikeHistory(symbol, expiry, exchange || 'NFO'),
          getOIStrikeTimeline(symbol, expiry, exchange || 'NFO'),
          getMaxPainHistory(symbol, exchange || 'NFO', 5),
          // Request the chain's OWN symbol.  This used to substitute 'NIFTY' for
          // anything outside NSE_INDICES_SET, so a stock chain drew NIFTY prices —
          // and once FIX 29 started labelling the line with the chain symbol, a
          // RELIANCE chain showed NIFTY prices labelled RELIANCE.  Safe now: the
          // backend resolves non-index symbols from instrument_master, and an
          // unresolvable one returns {points: []}, which every render site already
          // gates on, so it degrades to "no overlay".
          getNiftyIntraday(symbol),
        ]);
        if (!cancelled) {
          setPcrHistory(agg.history ?? []);
          setStrikeHistory(strikes.snapshots ?? []);
          setHistoryDate(strikes.display_date ?? null);
          setStrikeTimeline(timeline.timeline ?? []);
          setMaxPainHistory(mpHist.history ?? []);
          setNiftyIntraday(nifty.points ?? []);
        }
      } catch (_) { /* silent */ }
    };
    const timer = setTimeout(fetch_, CHAIN_FETCH_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [symbol, expiry, exchange, rows]);

  // Fetch IV/HV history once on mount (updated daily — no need to re-fetch per chain refresh)
  useEffect(() => {
    let cancelled = false;
    getIVHistory(252).then(d => {
      if (!cancelled && d?.rows?.length) {
        setIvHistory(d.rows);
        setIvMeta(d.meta ?? null);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // When primary expiry changes, reset to single selection and reset both sliders
  useEffect(() => {
    setSelectedExpiries(new Set([expiry].filter(Boolean)));
    setExtraChains({});
    setSliderFrom(0);
    setSliderTo(0);
    setPriceSliderFrom(0);
    setPriceSliderTo(0);
  }, [expiry]);

  // Toggle any expiry on/off (including the primary loaded chain)
  const toggleExpiry = useCallback(async exp => {
    // Always toggle in/out of selectedExpiries — primary can also be excluded
    setSelectedExpiries(prev => {
      const next = new Set(prev);
      if (next.has(exp)) { next.delete(exp); return next; }
      next.add(exp);
      return next;
    });
    // Primary expiry chain already in `rows` — no fetch needed
    if (exp === expiry) return;
    // Fetch non-primary chain if not yet cached
    if (!extraChains[exp]) {
      setLoadingExp(exp);
      try {
        const data = await getOptionChain(symbol, exp, exchange || 'NFO');
        setExtraChains(prev => ({ ...prev, [exp]: data.rows ?? [] }));
      } catch (e) {
        console.warn('[OIAnalysis] failed to load expiry', exp, e);
        setSelectedExpiries(prev => { const n = new Set(prev); n.delete(exp); return n; });
      } finally {
        setLoadingExp(null);
      }
    }
  }, [expiry, extraChains, symbol, exchange]);

  // ── Fallback rows from persisted snapshot when live chain is unavailable ─
  // Used on holidays and before morning-connect refetch.
  // Bars render with zero change overlays (honest: no intraday-change data).
  const fallbackRows = useMemo(() => {
    if (rows?.length) return null;               // live chain present — no fallback needed
    const snap = strikeHistory[0];               // most-recent snapshot (label "Now")
    if (!snap?.strikes) return null;
    return Object.entries(snap.strikes).map(([strike, st]) => ({
      strike:    parseInt(strike, 10),
      ceOI:      st.ceOI ?? 0,
      peOI:      st.peOI ?? 0,
      _ceAbsChg: 0,
      _peAbsChg: 0,
      ceDeltaOI: 0,
      peDeltaOI: 0,
      pcr:       st.ceOI > 0 ? (st.peOI ?? 0) / st.ceOI : 0,
      _multi:    false,
    })).sort((a, b) => a.strike - b.strike);
  }, [rows, strikeHistory]);

  const isHistoryFallback = !rows?.length && (fallbackRows?.length ?? 0) > 0;

  // ── Merge rows across selected expiries ─────────────────────────────────
  const mergedRows = useMemo(() => {
    const liveRows = rows?.length ? rows : (fallbackRows ?? []);
    if (!liveRows.length) return [];
    const byStrike = {};
    // Primary chain: only include if selected
    if (selectedExpiries.has(expiry)) {
      liveRows.forEach(r => {
        const ceAbsChg = calcAbsOIChg(r.ceOI ?? 0, r.ceDeltaOI ?? 0);
        const peAbsChg = calcAbsOIChg(r.peOI ?? 0, r.peDeltaOI ?? 0);
        byStrike[r.strike] = {
          strike:    r.strike,
          ceOI:      r.ceOI ?? 0,
          peOI:      r.peOI ?? 0,
          _ceAbsChg: ceAbsChg,
          _peAbsChg: peAbsChg,
          ceDeltaOI: r.ceDeltaOI ?? 0,
          peDeltaOI: r.peDeltaOI ?? 0,
          pcr:       r.pcr ?? 0,
          _multi:    false,
        };
      });
    }
    // Add selected extra chains
    for (const [exp, expRows] of Object.entries(extraChains)) {
      if (!selectedExpiries.has(exp)) continue;
      expRows.forEach(r => {
        const ceAbsChg = calcAbsOIChg(r.ceOI ?? 0, r.ceDeltaOI ?? 0);
        const peAbsChg = calcAbsOIChg(r.peOI ?? 0, r.peDeltaOI ?? 0);
        if (byStrike[r.strike]) {
          byStrike[r.strike].ceOI      += r.ceOI ?? 0;
          byStrike[r.strike].peOI      += r.peOI ?? 0;
          byStrike[r.strike]._ceAbsChg += ceAbsChg;
          byStrike[r.strike]._peAbsChg += peAbsChg;
          byStrike[r.strike]._multi     = true;
        } else {
          byStrike[r.strike] = {
            strike: r.strike, ceOI: r.ceOI ?? 0, peOI: r.peOI ?? 0,
            _ceAbsChg: ceAbsChg, _peAbsChg: peAbsChg,
            ceDeltaOI: r.ceDeltaOI ?? 0, peDeltaOI: r.peDeltaOI ?? 0,
            pcr: 0, _multi: true,
          };
        }
      });
    }
    // Recompute pcr + effective delta% for overlay sizing on merged rows
    return Object.values(byStrike)
      .sort((a, b) => a.strike - b.strike)
      .map(r => ({
        ...r,
        pcr: r.ceOI > 0 ? r.peOI / r.ceOI : 0,
        // For multi-expiry: derive effective delta% from abs/total
        ceDeltaOI: r._multi && r.ceOI > 0 ? (r._ceAbsChg / (r.ceOI - r._ceAbsChg)) * 100 : r.ceDeltaOI,
        peDeltaOI: r._multi && r.peOI > 0 ? (r._peAbsChg / (r.peOI - r._peAbsChg)) * 100 : r.peDeltaOI,
      }));
  }, [rows, fallbackRows, extraChains, selectedExpiries]);

  // Re-measure OI/Premium SVG widths once data arrives (callback ref fires when the
  // div mounts, but that only happens after the loading guard clears; remeasure()
  // reads nodeRef.current which is set by then, ensuring 1 SVG unit = 1 screen px).
  const hasOIData = mergedRows.length > 0 || isHistoryFallback;
  useEffect(() => {
    if (hasOIData) { remeasureOi(); remeasurePremium(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOIData]);

  // ── Slider-derived rows (must come AFTER mergedRows is defined) ──────────
  const sliderIsDefault      = sliderFrom === 0 && (sliderTo === 0 || sliderTo === sliderSnaps.length - 1);
  const priceSliderIsDefault = priceSliderFrom === 0 && (priceSliderTo === 0 || priceSliderTo === priceSnaps.length - 1);

  // Spot/ATM that tracks the scrubber position during replay.
  // When scrubber is at default (full-day / live), use live meta values.
  const snapToATM = v => Math.round(v / 50) * 50;
  const oiDisplaySpot = (!sliderIsDefault && sliderSnaps[sliderTo]?.spot > 0)
    ? sliderSnaps[sliderTo].spot : spot;
  const oiDisplayATM  = snapToATM(oiDisplaySpot) || ATM;
  const priceDisplaySpot = (!priceSliderIsDefault && priceSnaps[priceSliderTo]?.spot > 0)
    ? priceSnaps[priceSliderTo].spot : spot;
  const priceDisplayATM  = snapToATM(priceDisplaySpot) || ATM;

  const sliderActiveRows = useMemo(() => {
    if (sliderIsDefault || !sliderSnaps.length) return null;
    const toSnap   = sliderSnaps[sliderTo];
    const fromSnap = sliderSnaps[sliderFrom];
    if (!toSnap || !fromSnap) return null;

    return (mergedRows ?? []).map(row => {
      const strike  = parseInt(row.strike, 10);
      // TO values
      let toCeOI = row.ceOI ?? 0;
      let toPeOI = row.peOI ?? 0;
      if (toSnap.label === 'Now' && toSnap.strikes) {
        const s = toSnap.strikes[strike] ?? toSnap.strikes[String(strike)] ?? {};
        if (s.ceOI != null) { toCeOI = s.ceOI; toPeOI = s.peOI ?? 0; }
      } else if (toSnap.strikes) {
        const s = toSnap.strikes[strike] ?? toSnap.strikes[String(strike)] ?? {};
        toCeOI = s.ceOI ?? row.ceOI ?? 0;
        toPeOI = s.peOI ?? row.peOI ?? 0;
      }
      // FROM values
      let fromCeOI = toCeOI, fromPeOI = toPeOI;
      if (fromSnap.strikes) {
        const fs = fromSnap.strikes[strike] ?? fromSnap.strikes[String(strike)] ?? {};
        fromCeOI = fs.ceOI ?? toCeOI;
        fromPeOI = fs.peOI ?? toPeOI;
      }
      const ceAbsChg = toCeOI - fromCeOI;
      const peAbsChg = toPeOI - fromPeOI;
      const ceDeltaOI = fromCeOI > 0 ? ceAbsChg / fromCeOI * 100 : 0;
      const peDeltaOI = fromPeOI > 0 ? peAbsChg / fromPeOI * 100 : 0;
      return { ...row, ceOI: toCeOI, peOI: toPeOI, ceDeltaOI, peDeltaOI, _ceAbsChg: ceAbsChg, _peAbsChg: peAbsChg };
    });
  }, [sliderIsDefault, sliderSnaps, sliderFrom, sliderTo, mergedRows]);

  // ── Price history presence — true when any timeline snap has non-null ceLTP ──
  const hasPriceHistory = useMemo(() =>
    strikeTimeline.some(s => Object.values(s.strikes ?? {}).some(st => st.ceLTP != null)),
    [strikeTimeline]
  );

  // ── Per-strike price diff between PRICE slider FROM and TO snapshots ───────
  // Indexes into priceSnaps (entries with ceLTP data) — independent of OI slider.
  // Returns null when default (full-day = vs D-1 close) or when no price snaps exist.
  const sliderActivePriceRows = useMemo(() => {
    if (priceSliderIsDefault || !priceSnaps.length) return null;
    const toSnap   = priceSnaps[priceSliderTo];
    const fromSnap = priceSnaps[priceSliderFrom];
    if (!toSnap || !fromSnap) return null;
    // priceSnaps are pre-filtered to have ceLTP, but double-check TO snap
    const toPriceAvail = Object.values(toSnap.strikes ?? {}).some(st => st.ceLTP != null);
    if (!toPriceAvail) return null;

    return (mergedRows ?? []).map(row => {
      const strike = parseInt(row.strike, 10);
      const toSt   = toSnap.strikes?.[strike]   ?? toSnap.strikes?.[String(strike)]   ?? {};
      const fromSt = fromSnap.strikes?.[strike] ?? fromSnap.strikes?.[String(strike)] ?? {};
      const toCeLTP   = toSt.ceLTP   ?? null;
      const toPeLTP   = toSt.peLTP   ?? null;
      const fromCeLTP = fromSt.ceLTP ?? null;
      const fromPeLTP = fromSt.peLTP ?? null;
      return {
        strike,
        ceLTP:    toCeLTP,
        peLTP:    toPeLTP,
        ceLTPChg: (toCeLTP != null && fromCeLTP != null) ? toCeLTP - fromCeLTP : null,
        peLTPChg: (toPeLTP != null && fromPeLTP != null) ? toPeLTP - fromPeLTP : null,
      };
    });
  }, [priceSliderIsDefault, priceSnaps, priceSliderFrom, priceSliderTo, mergedRows]);

  // ── Price chart rows: independent strike set + own ±N filter ────────────
  // Full unfiltered price data (all strikes), then sliced by priceStrikeFilter.
  const priceShowRows = useMemo(() => {
    const liveRows = rows?.length ? rows : null;
    // Build full per-strike price list (not limited to OI chart's showRows)
    const base = sliderActivePriceRows
      ? [...sliderActivePriceRows].sort((a, b) => Number(a.strike) - Number(b.strike))
      : liveRows
        ? liveRows.map(r => ({
            strike:   r.strike,
            ceLTP:    r.ceLTP    ?? null,
            peLTP:    r.peLTP    ?? null,
            ceLTPChg: r.ceLTPChg != null ? r.ceLTPChg : null,
            peLTPChg: r.peLTPChg != null ? r.peLTPChg : null,
          })).sort((a, b) => Number(a.strike) - Number(b.strike))
        : null;
    if (!base) return null;
    if (priceStrikeFilter === 'all') return base;
    const atmIdx = base.findIndex(r => Number(r.strike) === ATM);
    if (atmIdx === -1) return base;
    const lo = Math.max(0, atmIdx - priceStrikeFilter);
    const hi = Math.min(base.length - 1, atmIdx + priceStrikeFilter);
    return base.slice(lo, hi + 1);
  }, [rows, sliderActivePriceRows, ATM, priceStrikeFilter]);

  // ── Rows filtered ±N around ATM ──────────────────────────────────────────
  // activeBase: slider-derived rows (if slider is not at default) or live mergedRows
  const activeBase = sliderActiveRows ?? mergedRows ?? [];

  const showRows = useMemo(() => {
    if (!activeBase.length) return [];
    if (strikeFilter === 'all') return activeBase;
    const atmIdx = activeBase.findIndex(r => r.strike === ATM);
    if (atmIdx === -1) return activeBase;
    const lo = Math.max(0, atmIdx - strikeFilter);
    const hi = Math.min(activeBase.length - 1, atmIdx + strikeFilter);
    return activeBase.slice(lo, hi + 1);
  }, [activeBase, ATM, strikeFilter]);

  // ── Totals (active base rows) ────────────────────────────────────────────
  const totalCeOI  = useMemo(() => activeBase.reduce((s, r) => s + (r.ceOI ?? 0), 0), [activeBase]);
  const totalPeOI  = useMemo(() => activeBase.reduce((s, r) => s + (r.peOI ?? 0), 0), [activeBase]);
  const totalCeChg = useMemo(() => activeBase.reduce((s, r) => s + (r._ceAbsChg ?? 0), 0), [activeBase]);
  const totalPeChg = useMemo(() => activeBase.reduce((s, r) => s + (r._peAbsChg ?? 0), 0), [activeBase]);

  // Max Pain: strike where total option-writer pain (ITM OI × distance) is minimised.
  // Computed from mergedRows so it always reflects the currently selected expiries.
  const computedMaxPain = useMemo(() => {
    const rows_ = mergedRows ?? [];
    if (rows_.length < 3) return null;
    const strikes = rows_.map(r => r.strike);
    let minPain = Infinity, mpStrike = null;
    for (const test of strikes) {
      let pain = 0;
      for (const r of rows_) {
        if (r.strike < test) pain += (test - r.strike) * (r.ceOI || 0); // ITM calls
        if (r.strike > test) pain += (r.strike - test) * (r.peOI || 0); // ITM puts
      }
      if (pain < minPain) { minPain = pain; mpStrike = test; }
    }
    return mpStrike;
  }, [mergedRows]);

  // ── Drift ribbon data: per-interval bucket drifts (v2) ──────────────────────
  // Each bucket = ln(pcr_newer / pcr_older) for its own consecutive time interval.
  // hasData = BOTH snapshot slots exist in the history buffer — NOT derived from OI magnitude.
  // A strike with near-zero CE OI still has data (ratio clamped); a missing snapshot does not.
  const driftData = useMemo(() => {
    if (!strikeHistory.length || !showRows.length) return {};
    const { RATIO_MIN, RATIO_MAX } = DRIFT_CONFIG;

    const snapByLabel = Object.fromEntries(strikeHistory.map(s => [s.label, s]));

    const getPcr = (ceOI, peOI) => {
      const c = ceOI > 0 ? ceOI : 1e-9; // guard zero denominator before ln
      return Math.max(RATIO_MIN, Math.min(RATIO_MAX, (peOI ?? 0) / c));
    };

    const result = {};
    showRows.forEach(row => {
      const strike = row.strike;

      // Resolve ceOI/peOI for a given offset label.
      // snapExists = snapshot slot is present (independent of OI values).
      const getOI = (label) => {
        if (label === 'now') {
          return { ceOI: row.ceOI ?? 0, peOI: row.peOI ?? 0, snapExists: true };
        }
        const snap = snapByLabel[label];
        if (!snap) return { snapExists: false, ceOI: 0, peOI: 0 };
        const st = snap.strikes?.[strike] ?? snap.strikes?.[String(strike)];
        return { ceOI: st?.ceOI ?? 0, peOI: st?.peOI ?? 0, snapExists: true };
      };

      result[strike] = RIBBON_BUCKETS.map(bucket => {
        const n = getOI(bucket.newer);
        const o = getOI(bucket.older);
        // hasData requires both snapshot slots to exist — thin OI ≠ missing
        const hasData = n.snapExists && o.snapExists;
        if (!hasData) return { hasData: false, drift: null, age: bucket.age, span: bucket.span };
        const drift = Math.log(getPcr(n.ceOI, n.peOI) / getPcr(o.ceOI, o.peOI));
        return { hasData: true, drift, age: bucket.age, span: bucket.span };
      });
    });
    return result;
  }, [strikeHistory, showRows]);

  // ── Arrow data: net-2h direction + bucket-weighted momentum/conviction ────────
  // Direction + color = sign(mom) — recency-weighted per-minute rate of PCR change.
  // Length = tanh(|mom| / K_LEN) scaled to [L_MIN, L_MIN+L_SPAN].
  // Thickness = conviction = |Σw·drift| / Σw|drift| using raw (not rate-normalized) drifts.
  const arrowData = useMemo(() => {
    if (!showRows.length || !Object.keys(driftData).length) return {};
    const { HALFLIFE_MIN, K_LEN, L_MIN, L_SPAN, T_MIN, T_SPAN, FLAT_THRESH, RATIO_MIN, RATIO_MAX } = DRIFT_CONFIG;
    const EPS = 1e-6, LN2 = Math.LN2;

    const snapByLabel = Object.fromEntries(strikeHistory.map(s => [s.label, s]));
    const snap2h = snapByLabel['-2h'];

    const getPcr = (ceOI, peOI) => {
      const c = ceOI > 0 ? ceOI : 1e-9;
      return Math.max(RATIO_MIN, Math.min(RATIO_MAX, (peOI ?? 0) / c));
    };

    const result = {};
    showRows.forEach(row => {
      const strike = row.strike;
      if (!(row.ceOI > 0)) return;
      const buckets = driftData[strike];
      if (!buckets) return;

      // Change A: rate-normalize each bucket before recency-weighting.
      // momNum accumulates w·(drift/span) for the per-minute rate signal.
      // netDrift / grossDrift accumulate raw drift for conviction (Change C: unchanged).
      let wSum = 0, momNum = 0, netDrift = 0, grossDrift = 0;
      buckets.forEach(b => {
        if (!b.hasData) return;
        const w = Math.exp(-LN2 * b.age / HALFLIFE_MIN);
        wSum       += w;
        momNum     += w * (b.drift / b.span); // per-minute rate (Change A)
        netDrift   += w * b.drift;            // raw drift — conviction numerator
        grossDrift += w * Math.abs(b.drift);  // raw drift — conviction denominator
      });
      if (wSum < EPS) return;

      const mom        = momNum / wSum;                              // signed weighted mean rate (per min)
      const conviction = Math.abs(netDrift) / Math.max(grossDrift, EPS); // unchanged (Change C)

      // net2h — kept for reference; no longer drives direction or color.
      const nowPcr = getPcr(row.ceOI, row.peOI ?? 0);
      const st2h   = snap2h?.strikes?.[strike] ?? snap2h?.strikes?.[String(strike)];
      const net2h  = snap2h
        ? Math.log(nowPcr / getPcr(st2h?.ceOI ?? 0, st2h?.peOI ?? 0))
        : null;

      const length    = L_MIN + L_SPAN * Math.tanh(Math.abs(mom) / K_LEN);
      const thickness = T_MIN + T_SPAN * conviction;
      const flat      = Math.abs(mom) < FLAT_THRESH; // Change B: flat from mom rate, not net2h
      const color     = flat
        ? 'rgb(177,164,136)'
        : mom > 0 ? 'rgb(43,124,161)' : 'rgb(168,85,57)'; // Change B: color from sign(mom)

      result[strike] = {
        direction: mom >= 0 ? 1 : -1, // Change B: sign(mom), not sign(net2h)
        length, thickness, color, flat,
        mom, conviction, net2h,        // exposed for ribbon tooltip
      };
    });
    return result;
  }, [driftData, strikeHistory, showRows]);

  // ── Blank-state / fallback-banner ───────────────────────────────────────
  if (!mergedRows.length && !isHistoryFallback) {
    let msg;
    if (loading) {
      msg = 'Loading OI analysis…';
    } else if (!expiry) {
      msg = 'Select an expiry to load the chain.';
    } else if (error && kiteOk === false) {
      msg = 'Waiting for Kite connection…';
    } else {
      msg = 'No data available.';
    }
    return (
      <div>
        <OIExpiryBar expiries={expiries} expiry={expiry} setExpiry={setExpiry}
          selectedExpiries={selectedExpiries} toggleExpiry={toggleExpiry} loadingExp={loadingExp}/>
        <div className="card" style={{ padding:20, textAlign:'center', fontSize:11, color:'var(--ink-3)' }}>
          {msg}
        </div>
      </div>
    );
  }

  // ── Last-session banner (isHistoryFallback: live chain unavailable, showing persisted data) ──
  const historyBanner = isHistoryFallback ? (
    <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px',
      marginBottom:6, borderRadius:4, fontSize:10, color:'var(--ink-3)',
      background:'var(--paper-2)', border:'.5px solid var(--hair)' }}>
      <span style={{ opacity:0.6 }}>⏱</span>
      <span>
        Last available session data
        {(historyDate || strikeHistory[0]?.time) ? ' — as of ' : ''}
        {historyDate ? historyDate : ''}
        {historyDate && strikeHistory[0]?.time ? ' ' : ''}
        {strikeHistory[0]?.time ?? ''}
      </span>
    </div>
  ) : null;

  const overallPCR = totalCeOI > 0 ? totalPeOI / totalCeOI : (meta?.pcr ?? 0);

  // ── SVG dimensions ───────────────────────────────────────────────────────
  const _mob  = typeof window !== 'undefined' && window.matchMedia('(max-width:600px)').matches;
  const SVG_W = oiSvgW, SVG_H = _mob ? 300 : 460;
  const PAD   = { l:58, r:52, t:28, b:62 };
  const chartW = SVG_W - PAD.l - PAD.r;
  const chartH = SVG_H - PAD.t - PAD.b;

  const n      = showRows.length || 1;
  const groupW = chartW / n;
  // Narrower bars with more breathing room between pairs
  const barW   = Math.max(2, Math.min(12, groupW * 0.25));
  const gap    = Math.max(1.5, (groupW - barW * 2) / 3);

  // ── OI y-scale (left axis) ───────────────────────────────────────────────
  // Include from-snapshot OI (= current − absChg) so decrease outlines never clip above the axis
  const maxOI = Math.max(1, ...showRows.flatMap(r => [
    r.ceOI ?? 0, r.peOI ?? 0,
    (r.ceOI ?? 0) - (r._ceAbsChg ?? 0),
    (r.peOI ?? 0) - (r._peAbsChg ?? 0),
  ]));
  const yOI   = v => PAD.t + chartH * (1 - v / maxOI);
  const y0    = PAD.t + chartH;

  // ── PCR / CPR shared right-axis scale ────────────────────────────────────
  // Use 90th-percentile so extreme OTM strikes don't squash the ATM region.
  const lineVals = showRows.flatMap(r => {
    const pcr = r.pcr ?? 0;
    const cpr = (r.peOI > 0 && r.ceOI > 0) ? r.ceOI / r.peOI : 0;
    return [pcr, cpr];
  }).filter(v => v > 0 && v < 20);
  const _lSorted = [...lineVals].sort((a, b) => a - b);
  const _p90     = _lSorted[Math.floor(_lSorted.length * 0.90)] || 2.5;
  const lineMax  = Math.min(5, Math.max(1.5, _p90 * 1.15));
  const yLine    = v => PAD.t + chartH * (1 - Math.min(v, lineMax) / lineMax);

  const atmShowIdx    = showRows.findIndex(r => r.strike === ATM);
  const oiATMShowIdx  = showRows.findIndex(r => r.strike === oiDisplayATM);

  // ── prevClose: find closest strike index for vertical line ───────────────
  // Plain variable (not useMemo) — must not be a hook after an early return
  let prevCloseIdx = -1;
  if (prevClose != null && showRows.length) {
    let bestDist = Infinity;
    showRows.forEach((r, i) => {
      const d = Math.abs(r.strike - prevClose);
      if (d < bestDist) { bestDist = d; prevCloseIdx = i; }
    });
  }

  // ── PCR line: solid below ATM, faint dashed above ────────────────────────
  const pcrBelowPts = showRows
    .map((r, i) => r.strike <= ATM && r.pcr > 0
      ? `${(PAD.l + (i + 0.5) * groupW).toFixed(1)},${yLine(r.pcr).toFixed(1)}` : null)
    .filter(Boolean).join(' ');
  const pcrAbovePts = showRows
    .map((r, i) => r.strike >= ATM && r.pcr > 0
      ? `${(PAD.l + (i + 0.5) * groupW).toFixed(1)},${yLine(r.pcr).toFixed(1)}` : null)
    .filter(Boolean).join(' ');

  // ── CPR line: solid above ATM, faint dashed below ────────────────────────
  const cprAbovePts = showRows
    .map((r, i) => {
      if (r.strike < ATM || !r.ceOI || !r.peOI) return null;
      const cpr = r.ceOI / r.peOI;
      return `${(PAD.l + (i + 0.5) * groupW).toFixed(1)},${yLine(cpr).toFixed(1)}`;
    }).filter(Boolean).join(' ');
  const cprBelowPts = showRows
    .map((r, i) => {
      if (r.strike > ATM || !r.ceOI || !r.peOI) return null;
      const cpr = r.ceOI / r.peOI;
      return `${(PAD.l + (i + 0.5) * groupW).toFixed(1)},${yLine(cpr).toFixed(1)}`;
    }).filter(Boolean).join(' ');

  // ── Grid ticks (OI: nice round Indian numbers; PCR: evenly spaced) ──────
  const _L = 100_000, _Cr = 10_000_000;
  const _oiNiceSteps = [5*_L, 10*_L, 25*_L, 50*_L, _Cr, 2*_Cr, 2.5*_Cr, 5*_Cr, 10*_Cr];
  const _oiRawStep   = maxOI / 4;
  const _oiStep      = _oiNiceSteps.find(s => s >= _oiRawStep) || _oiNiceSteps[_oiNiceSteps.length - 1];
  const oiTicks = [];
  for (let v = 0; v <= maxOI * 1.05; v += _oiStep) oiTicks.push({ v, y: yOI(v) });

  const lineTicks = [0,.25,.5,.75,1].map(t => ({ v: lineMax*t, y: yLine(lineMax*t) }));

  // ── Mouse handler → hover index ──────────────────────────────────────────
  const handleMouseMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx   = (e.clientX - rect.left) / rect.width * SVG_W - PAD.l;
    if (mx < 0 || mx > chartW) { setHoveredIdx(null); return; }
    const idx = Math.floor(mx / groupW);
    setHoveredIdx(idx >= 0 && idx < showRows.length ? idx : null);
  };

  const handleRibbonMouseMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx   = (e.clientX - rect.left) / rect.width * SVG_W - PAD.l;
    if (mx < 0 || mx > chartW) { setRibbonHoveredIdx(null); return; }
    const idx = Math.floor(mx / groupW);
    setRibbonHoveredIdx(idx >= 0 && idx < showRows.length ? idx : null);
  };

  return (
    <div>
      <OIExpiryBar expiries={expiries} expiry={expiry} setExpiry={setExpiry}
        selectedExpiries={selectedExpiries} toggleExpiry={toggleExpiry} loadingExp={loadingExp}/>
      {historyBanner}
      <div className="card" style={{ padding:10 }}>

        {/* ── Header ── */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
          <span style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:11, fontWeight:600 }}>OI Analysis</span>
            <InfoBtn align="right">
              Strike-level OI bars show CE (orange) and PE (blue) open interest. Hatched portions show the change in the selected time window. PCR/CPR lines overlay per-strike put-call ratios.
            </InfoBtn>
          </span>

          {/* Strike pills */}
          <div style={{ display:'flex', gap:4, marginLeft:8 }}>
            {(['all', 5, 10, 15]).map(v => {
              const active = strikeFilter === v;
              return (
                <button key={v} onClick={() => setStrikeFilter(v)}
                  style={{ fontSize:11, fontWeight:600, padding:'2px 9px', cursor:'pointer',
                    borderRadius:10, border: active ? 'none' : '.5px solid var(--hair-strong)',
                    background: active ? 'var(--amber-ink)' : 'transparent',
                    color: active ? '#fff' : 'var(--ink-2)', lineHeight:1.6 }}>
                  {v === 'all' ? 'All' : `±${v}`}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ marginLeft:'auto', display:'flex', gap:10, fontSize:11,
              color:'var(--ink-3)', alignItems:'center', flexWrap:'wrap' }}>
            {/* PE swatch */}
            <span style={{ display:'flex', alignItems:'center', gap:3 }}>
              <svg width="24" height="10">
                <rect x="0" y="1" width="12" height="8" fill="var(--pebar)" opacity=".85"/>
                <rect x="12" y="1" width="12" height="8" fill="var(--pebar)" opacity=".35"/>
              </svg>
              PE / Incr
            </span>
            <span style={{ display:'flex', alignItems:'center', gap:3 }}>
              <svg width="14" height="10">
                <rect x="1" y="1" width="12" height="4" fill="none" stroke="var(--pebar)" strokeWidth="1" opacity=".5"/>
                <rect x="1" y="5" width="12" height="4" fill="var(--pebar)" opacity=".85"/>
              </svg>
              PE Decr
            </span>
            {/* CE swatch */}
            <span style={{ display:'flex', alignItems:'center', gap:3 }}>
              <svg width="24" height="10">
                <rect x="0" y="1" width="12" height="8" fill="var(--cebar)" opacity=".85"/>
                <rect x="12" y="1" width="12" height="8" fill="var(--cebar)" opacity=".35"/>
              </svg>
              CE / Incr
            </span>
            <span style={{ display:'flex', alignItems:'center', gap:3 }}>
              <svg width="14" height="10">
                <rect x="1" y="1" width="12" height="4" fill="none" stroke="var(--cebar)" strokeWidth="1" opacity=".5"/>
                <rect x="1" y="5" width="12" height="4" fill="var(--cebar)" opacity=".85"/>
              </svg>
              CE Decr
            </span>
            {/* PCR/CPR lines */}
            <span style={{ display:'flex', alignItems:'center', gap:3 }}>
              <svg width="28" height="10">
                <polyline points="0,7 7,4 14,7" fill="none" stroke="var(--pebar)" strokeWidth="1.5"/>
                <polyline points="14,7 21,4 28,2" fill="none" stroke="var(--cebar)" strokeWidth="1.5"/>
              </svg>
              PCR / CPR
            </span>
            {/* Drift arrow toggle */}
            {strikeHistory.length > 0 && (
              <span onClick={() => setShowArrows(v => !v)}
                style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer',
                  opacity: showArrows ? 1 : 0.35, userSelect:'none' }}>
                <svg width="24" height="10">
                  <line x1="2" y1="5" x2="18" y2="5" stroke="rgb(43,124,161)" strokeWidth="1.8"/>
                  <polygon points="18,2 24,5 18,8" fill="rgb(43,124,161)"/>
                </svg>
                Arrows
              </span>
            )}
          </div>
        </div>

        {/* ── PCR/CPR history line toggles ── */}
        <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:6, flexWrap:'wrap' }}>
          <button
            onClick={() => setVisibleOffsets(prev => {
              const allLabels = OI_HIST_OFFSETS.map(o => o.label);
              const allOn = allLabels.every(l => prev.has(l));
              return allOn ? new Set() : new Set(allLabels);
            })}
            title="Toggle all history lines on / off"
            style={{ fontSize:11, color:'var(--ink-3)', fontWeight:600,
              textTransform:'uppercase', letterSpacing:'.04em', marginRight:2,
              background:'none', border:'none', cursor:'pointer', padding:0,
              textDecoration: 'underline dotted', textUnderlineOffset:2 }}>
            History:
          </button>
          {OI_HIST_OFFSETS.map(({ label, op, sw }) => {
            const active  = visibleOffsets.has(label);
            const hasData = label === 'Now' || strikeHistory.some(s => s.label === label);
            return (
              <button key={label}
                onClick={() => setVisibleOffsets(prev => {
                  const next = new Set(prev);
                  next.has(label) ? next.delete(label) : next.add(label);
                  return next;
                })}
                title={hasData ? (active ? 'Click to hide' : 'Click to show')
                               : 'No data yet — accumulates through the day'}
                style={{ fontSize:11, fontWeight: active ? 700 : 400,
                  padding:'1px 7px', borderRadius:3, cursor:'pointer',
                  border: active ? '.5px solid transparent' : '.5px solid var(--hair-strong)',
                  background: active ? 'transparent' : 'transparent',
                  color: active ? 'var(--ink)' : 'var(--ink-3)',
                  opacity: hasData ? 1 : 0.45,
                  display:'flex', alignItems:'center', gap:4, transition:'all .15s' }}>
                {/* mini line swatch with opacity matching the actual line */}
                <svg width="18" height="6" style={{ flexShrink:0 }}>
                  <line x1="0" y1="3" x2="18" y2="3"
                    stroke="var(--pebar)" strokeWidth={sw * 2.5} opacity={op}/>
                </svg>
                {label}
              </button>
            );
          })}
        </div>

        {/* ── SVG Chart ── */}
        <div ref={oiChartRef} style={{ overflowX:'auto' }}>
          <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} height={SVG_H}
            style={{ width:'100%', minWidth:400, display:'block', cursor:'crosshair' }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoveredIdx(null)}>

            {/* ── OI grid + left labels ── */}
            {oiTicks.map(({ v, y }) => (
              <g key={v}>
                <line x1={PAD.l} x2={SVG_W - PAD.r} y1={y} y2={y}
                  stroke="var(--hair)" strokeWidth=".5" strokeDasharray="3,3"/>
                <text x={PAD.l - 4} y={y + 3.5} textAnchor="end" fontSize={11} fill="var(--ink-3)">
                  {fmtOI(Math.round(v))}
                </text>
              </g>
            ))}

            {/* ── PCR/CPR right-axis labels ── */}
            {lineTicks.map(({ v, y }) => (
              <text key={v} x={SVG_W - PAD.r + 4} y={y + 3.5} textAnchor="start"
                fontSize={11} fill="var(--ink-3)" opacity=".7">
                {v.toFixed(1)}
              </text>
            ))}

            {/* ── Axis titles ── */}
            <text x={10} y={PAD.t + chartH / 2} textAnchor="middle" fontSize={11} fill="var(--ink-3)"
              transform={`rotate(-90,10,${PAD.t + chartH / 2})`}>OI</text>
            <text x={SVG_W - 7} y={PAD.t + chartH / 2} textAnchor="middle" fontSize={11} fill="var(--ink-3)" opacity=".7"
              transform={`rotate(90,${SVG_W - 7},${PAD.t + chartH / 2})`}>PCR/CPR</text>

            {/* ── ATM dashed vertical + spot label (tracks scrubber/replay) ── */}
            {(oiATMShowIdx >= 0 || atmShowIdx >= 0) && (() => {
              const useIdx = oiATMShowIdx >= 0 ? oiATMShowIdx : atmShowIdx;
              const atmX = PAD.l + (useIdx + 0.5) * groupW;
              return (
                <g>
                  <line x1={atmX} y1={PAD.t} x2={atmX} y2={y0}
                    stroke="var(--ink-2)" strokeWidth="1" strokeDasharray="4,3" opacity=".45"/>
                  <text x={atmX} y={PAD.t - 8} textAnchor="middle"
                    fontSize={8.5} fontWeight={600} fill="var(--ink-2)">
                    {oiDisplaySpot?.toLocaleString('en-IN')}
                  </text>
                </g>
              );
            })()}

            {/* ── D-1 Close → LTP horizontal arrow at top of chart ── */}
            {showPrevClose && prevCloseIdx >= 0 && atmShowIdx >= 0 && (() => {
              // Interpolate prevClose X precisely between nearest strikes
              const r0 = showRows[prevCloseIdx];
              const r1 = showRows[prevCloseIdx + 1];
              let pcX = PAD.l + (prevCloseIdx + 0.5) * groupW;
              if (r1 && r0 && r1.strike !== r0.strike) {
                const frac = (prevClose - r0.strike) / (r1.strike - r0.strike);
                pcX = PAD.l + (prevCloseIdx + frac) * groupW;
              }
              const atmX  = PAD.l + (atmShowIdx + 0.5) * groupW;
              const isUp  = spot >= prevClose;
              const color = isUp ? '#22c55e' : '#ef4444';
              const chgPct = prevClose > 0 ? ((spot - prevClose) / prevClose * 100).toFixed(2) : '0';
              const chgSign = isUp ? '+' : '';
              const arrowY  = 16;
              const markId  = isUp ? 'oi-arrowUp' : 'oi-arrowDn';
              // Stop 32px short of the LTP label so the arrow doesn't overlap it
              const ARROW_GAP = 32;
              const arrowEndX = isUp ? atmX - ARROW_GAP : atmX + ARROW_GAP;
              // D-1 label sits at the prevClose tick end, on same line as arrow
              const labelOnLeft = pcX > atmX;
              const labelX      = labelOnLeft ? pcX + 3 : pcX - 3;
              const labelAnchor = labelOnLeft ? 'start' : 'end';
              return (
                <g>
                  <defs>
                    <marker id={markId} markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
                      <path d="M0,0 L5,2.5 L0,5 Z" fill={color} opacity=".9"/>
                    </marker>
                  </defs>
                  {/* Arrow body stops short of LTP label to avoid overlap */}
                  <line x1={pcX} y1={arrowY} x2={arrowEndX} y2={arrowY}
                    stroke={color} strokeWidth="1.8" opacity=".85"
                    markerEnd={`url(#${markId})`}/>
                  {/* Tick at prevClose end */}
                  <line x1={pcX} y1={arrowY - 4} x2={pcX} y2={arrowY + 4}
                    stroke={color} strokeWidth="1.2" opacity=".7"/>
                  {/* D-1 label — same horizontal line as arrow, italic */}
                  <text x={labelX} y={arrowY + 4} textAnchor={labelAnchor}
                    fontSize={7} fontStyle="italic" fill={color} opacity=".85">
                    D-1 {prevClose?.toLocaleString('en-IN')}
                  </text>
                  {/* Change % label — centred on the drawn arrow body */}
                  <text x={(pcX + arrowEndX) / 2} y={arrowY + 10}
                    textAnchor="middle" fontSize={7} fontWeight={600} fill={color} opacity=".9">
                    {chgSign}{chgPct}%
                  </text>
                </g>
              );
            })()}

            {/* ── Bars: PE left, CE right ── */}
            {showRows.map((row, i) => {
              const xStart  = PAD.l + i * groupW;
              // PE on left, CE on right
              const peX     = xStart + gap;
              const ceX     = peX + barW + gap * 0.5;
              const isATM   = row.strike === ATM;
              const showLbl = row.strike % 100 === 0 || isATM; // skip X50 labels

              const ceOI    = row.ceOI    ?? 0;
              const peOI    = row.peOI    ?? 0;
              const ceDelta = row.ceDeltaOI ?? 0;
              const peDelta = row.peDeltaOI ?? 0;

              // PE bar
              const peTop = yOI(peOI);
              const peH   = Math.max(1, y0 - peTop);
              // Overlay height is purely proportional to the actual change.
              // Sub-pixel results render as sub-pixel (correctly invisible at that scale).
              // No floor, no threshold — the gate is purely on the sign of peDelta.
              const peOvH = peDelta >= 0
                ? Math.min(peH, peH * peDelta / (100 + peDelta))
                : Math.min(peH, peH * Math.abs(peDelta) / Math.max(1, 100 - Math.abs(peDelta)));
              // Increase: body drawn from the ago level down; light cap sits above it.
              // Decrease: body spans full height; hollow outline floats above.
              const peBodyH   = peDelta > 0 ? Math.max(0, peH - peOvH) : peH;
              const peBodyY   = peDelta > 0 ? peTop + peOvH : peTop;
              const peOverlay = peDelta > 0
                ? <rect x={peX} y={peTop} width={barW} height={peOvH} fill="var(--pebar)" opacity=".35" rx="1"/>
                : peDelta < 0
                  ? <rect x={peX + 0.5} y={peTop - peOvH} width={barW - 1} height={peOvH}
                      fill="none" stroke="var(--pebar)" strokeWidth="1" opacity=".5"/>
                  : null;

              // CE bar
              const ceTop = yOI(ceOI);
              const ceH   = Math.max(1, y0 - ceTop);
              const ceOvH = ceDelta >= 0
                ? Math.min(ceH, ceH * ceDelta / (100 + ceDelta))
                : Math.min(ceH, ceH * Math.abs(ceDelta) / Math.max(1, 100 - Math.abs(ceDelta)));
              const ceBodyH   = ceDelta > 0 ? Math.max(0, ceH - ceOvH) : ceH;
              const ceBodyY   = ceDelta > 0 ? ceTop + ceOvH : ceTop;
              const ceOverlay = ceDelta > 0
                ? <rect x={ceX} y={ceTop} width={barW} height={ceOvH} fill="var(--cebar)" opacity=".35" rx="1"/>
                : ceDelta < 0
                  ? <rect x={ceX + 0.5} y={ceTop - ceOvH} width={barW - 1} height={ceOvH}
                      fill="none" stroke="var(--cebar)" strokeWidth="1" opacity=".5"/>
                  : null;

              return (
                <g key={row.strike}>
                  {isATM && (
                    <rect x={xStart} y={PAD.t} width={groupW} height={chartH}
                      fill="rgba(254,249,231,.35)" rx="1"/>
                  )}
                  {/* hover highlight */}
                  {hoveredIdx === i && (
                    <rect x={xStart} y={PAD.t} width={groupW} height={chartH}
                      fill="var(--hair)" opacity=".18" rx="1"/>
                  )}

                  {/* PE: body (solid, up to ago level) then cap (35% for increases) */}
                  <rect x={peX} y={peBodyY} width={barW} height={peBodyH}
                    fill="var(--pebar)" opacity=".85" rx="1"/>
                  {peOverlay}

                  {/* CE: body (solid, up to ago level) then cap (35% for increases) */}
                  <rect x={ceX} y={ceBodyY} width={barW} height={ceBodyH}
                    fill="var(--cebar)" opacity=".85" rx="1"/>
                  {ceOverlay}

                  {/* X-axis label — only round 100s + ATM */}
                  {showLbl && (
                    <text
                      x={xStart + groupW / 2}
                      y={SVG_H - PAD.b + 14}
                      textAnchor="middle"
                      fontSize={isATM ? 12 : 11}
                      fontWeight={isATM ? 700 : 400}
                      fill={isATM ? 'var(--amber)' : 'var(--ink-3)'}
                      transform={`rotate(-40,${xStart + groupW / 2},${SVG_H - PAD.b + 14})`}>
                      {row.strike}
                    </text>
                  )}
                </g>
              );
            })}

            {/* ── PCR=1 / CPR=1 reference ── */}
            <line x1={PAD.l} x2={SVG_W - PAD.r} y1={yLine(1)} y2={yLine(1)}
              stroke="var(--hair-strong)" strokeWidth=".6" strokeDasharray="2,4" opacity=".5"/>

            {/* ── Historical PCR/CPR lines (older snapshots, drawn oldest-first so newer is on top) ── */}
            {[...strikeHistory].reverse()
              .filter(snap => snap.label !== 'Now' && visibleOffsets.has(snap.label))
              .map(snap => {
                const style_ = OI_HIST_OFFSETS.find(o => o.label === snap.label) || { op:0.3, sw:0.8 };
                const histPcrPts = showRows
                  .map((r, i) => {
                    if (r.strike > ATM) return null;
                    const s = snap.strikes[r.strike];
                    if (!s || !s.ceOI) return null;
                    const pcr = s.peOI / s.ceOI;
                    return `${(PAD.l + (i + 0.5) * groupW).toFixed(1)},${yLine(pcr).toFixed(1)}`;
                  }).filter(Boolean).join(' ');
                const histCprPts = showRows
                  .map((r, i) => {
                    if (r.strike < ATM) return null;
                    const s = snap.strikes[r.strike];
                    if (!s || !s.peOI) return null;
                    const cpr = s.ceOI / s.peOI;
                    return `${(PAD.l + (i + 0.5) * groupW).toFixed(1)},${yLine(cpr).toFixed(1)}`;
                  }).filter(Boolean).join(' ');
                return (
                  <g key={snap.label}>
                    {histPcrPts && (
                      <polyline points={histPcrPts} fill="none" stroke="var(--pebar)"
                        strokeWidth={style_.sw} strokeLinejoin="round" strokeLinecap="round"
                        opacity={style_.op}/>
                    )}
                    {histCprPts && (
                      <polyline points={histCprPts} fill="none" stroke="var(--cebar)"
                        strokeWidth={style_.sw} strokeLinejoin="round" strokeLinecap="round"
                        opacity={style_.op}/>
                    )}
                  </g>
                );
              })
            }

            {/* ── PCR line (below ATM solid, above faint) — "Now" ── */}
            {visibleOffsets.has('Now') && pcrBelowPts && (
              <polyline points={pcrBelowPts} fill="none" stroke="var(--pebar)"
                strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity=".9"/>
            )}
            {visibleOffsets.has('Now') && pcrAbovePts && (
              <polyline points={pcrAbovePts} fill="none" stroke="var(--pebar)"
                strokeWidth="1" strokeLinejoin="round" strokeDasharray="2,3" opacity=".3"/>
            )}

            {/* ── CPR line (above ATM solid, below faint) — "Now" ── */}
            {visibleOffsets.has('Now') && cprAbovePts && (
              <polyline points={cprAbovePts} fill="none" stroke="var(--cebar)"
                strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity=".9"/>
            )}
            {visibleOffsets.has('Now') && cprBelowPts && (
              <polyline points={cprBelowPts} fill="none" stroke="var(--cebar)"
                strokeWidth="1" strokeLinejoin="round" strokeDasharray="2,3" opacity=".3"/>
            )}

            {/* ── Dots on "Now" lines ── */}
            {visibleOffsets.has('Now') && showRows.map((row, i) => {
              const cx  = PAD.l + (i + 0.5) * groupW;
              const pcr = row.pcr ?? 0;
              const cpr = (row.ceOI > 0 && row.peOI > 0) ? row.ceOI / row.peOI : 0;
              const isAbove = row.strike > ATM;
              return (
                <g key={row.strike}>
                  {pcr > 0 && (
                    <circle cx={cx} cy={yLine(pcr)} r={row.strike === ATM ? 2.5 : 1.8}
                      fill="var(--pebar)" opacity={isAbove ? .3 : .9}/>
                  )}
                  {cpr > 0 && (
                    <circle cx={cx} cy={yLine(cpr)} r={row.strike === ATM ? 2.5 : 1.8}
                      fill="var(--cebar)" opacity={isAbove ? .9 : .3}/>
                  )}
                </g>
              );
            })}

            {/* ── Hover crosshair vertical line ── */}
            {hoveredIdx !== null && (() => {
              const hx = PAD.l + (hoveredIdx + 0.5) * groupW;
              return <line x1={hx} y1={PAD.t} x2={hx} y2={y0}
                stroke="var(--hair-strong)" strokeWidth=".6" opacity=".5" pointerEvents="none"/>;
            })()}

            {/* ── Hover tooltip ── */}
            {hoveredIdx !== null && (() => {
              const row = showRows[hoveredIdx];
              if (!row) return null;
              const isAbove  = row.strike > ATM;
              const pcr      = row.pcr ?? 0;
              const cpr      = (row.ceOI > 0 && row.peOI > 0) ? row.ceOI / row.peOI : 0;
              // Use pre-computed abs changes from mergedRows (handles multi-expiry correctly)
              const ceAbsChg = row._ceAbsChg ?? calcAbsOIChg(row.ceOI ?? 0, row.ceDeltaOI ?? 0);
              const peAbsChg = row._peAbsChg ?? calcAbsOIChg(row.peOI ?? 0, row.peDeltaOI ?? 0);

              const TW = 110, TH = 132;
              const cx = PAD.l + (hoveredIdx + 0.5) * groupW;
              const tx = Math.round(Math.max(PAD.l, Math.min(SVG_W - PAD.r - TW, cx - TW / 2)));
              const ty = PAD.t + 4;

              // Order: PE OI → CE OI → PE OI Change → CE OI Change → PCR → CPR
              const rows2 = [
                { l:'PE OI',    v: fmtOI(row.peOI ?? 0),           c:'var(--pebar)' },
                { l:'CE OI',    v: fmtOI(row.ceOI ?? 0),           c:'var(--cebar)' },
                { l:'PE Chg',   v: fmtOISign(peAbsChg),             c: peAbsChg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' },
                { l:'CE Chg',   v: fmtOISign(ceAbsChg),             c: ceAbsChg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' },
                { l:'PCR',      v: pcr.toFixed(2),                  c:'var(--pebar)', muted: isAbove },
                { l:'CPR',      v: cpr > 0 ? cpr.toFixed(2) : '—', c:'var(--cebar)', muted: !isAbove },
              ];

              return (
                <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
                  <rect x={tx} y={ty} width={TW} height={TH}
                    fill="var(--surface-2)" fillOpacity="0.7" stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
                    style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
                  {/* Title */}
                  <text x={tx + 7} y={ty + 18} fontSize={11} fontWeight={700}
                    fill="var(--ink)">
                    {row.strike.toLocaleString('en-IN')}
                    {row.strike === ATM ? ' (ATM)' : ''}
                  </text>
                  {/* Data rows */}
                  {rows2.map((item, k) => (
                    <g key={k}>
                      <text x={tx + 7} y={ty + 32 + k * 16} fontSize={11}
                        fill={item.muted ? 'var(--ink-3)' : 'var(--ink-2)'}>
                        {item.l}
                      </text>
                      <text x={tx + TW - 7} y={ty + 32 + k * 16} textAnchor="end"
                        fontSize={11} fontWeight={item.muted ? 400 : 600}
                        fill={item.muted ? 'var(--ink-3)' : item.c}>
                        {item.v}
                      </text>
                    </g>
                  ))}
                </g>
              );
            })()}
          </svg>

          {/* ── Drift Ribbon + Arrows (aligned with chart above) ── */}
          {strikeHistory.length > 0 && (() => {
            const { RATIO_MIN, RATIO_MAX, FLAT_THRESH, K_LEN, TREND_FLOOR } = DRIFT_CONFIG;
            const snapByLabelR = Object.fromEntries(strikeHistory.map(s => [s.label, s]));

            // getOI for a given label + strike (shared with driftData logic)
            const getOIR = (label, strike, currentRow) => {
              if (label === 'now') return { ceOI: currentRow.ceOI ?? 0, peOI: currentRow.peOI ?? 0, snapExists: true };
              const snap = snapByLabelR[label];
              if (!snap) return { snapExists: false, ceOI: 0, peOI: 0 };
              const st = snap.strikes?.[strike] ?? snap.strikes?.[String(strike)];
              return { ceOI: st?.ceOI ?? 0, peOI: st?.peOI ?? 0, snapExists: true };
            };

            const clampRatio = (ceOI, peOI) => {
              const c = ceOI > 0 ? ceOI : 1e-9;
              return Math.max(RATIO_MIN, Math.min(RATIO_MAX, (peOI ?? 0) / c));
            };

            // Returns the display ratio (PCR or CPR) for a given OI point and strike
            const getRatio = (ceOI, peOI, isAbove) => {
              if (isAbove) {
                // CPR = ceOI/peOI
                const p = peOI > 0 ? peOI : 1e-9;
                return Math.max(RATIO_MIN, Math.min(RATIO_MAX, (ceOI ?? 0) / p));
              }
              return clampRatio(ceOI, peOI); // PCR = peOI/ceOI
            };

            // Arrow label helpers — thresholds co-tune with arrow knobs
            // Change E: Bias from sign(mom) — mom>0 → PCR rate rising → more PEs → Bullish PE bias
            const getBias = (mom, flat) => {
              if (flat) return 'Flat';
              return mom > 0 ? 'Bullish' : 'Bearish';
            };
            // Change A: cutoffs on per-minute rate scale (was raw drift scale)
            const getMomentum = mom => {
              const m = Math.abs(mom);
              if (m >= 0.008) return 'Strong'; // tanh(0.008/0.008)=0.76 → 76% of max arrow length
              if (m >= 0.003) return 'Normal';
              return 'Weak';
            };
            // Change D: sign-change count on raw bucket drifts, oldest→newest
            const getTrend = (buckets) => {
              if (!buckets) return '—';
              const timed = [...buckets].reverse() // oldest→newest (RIBBON_BUCKETS is newest-first)
                .filter(b => b.hasData && Math.abs(b.drift) >= TREND_FLOOR);
              let changes = 0;
              for (let i = 1; i < timed.length; i++) {
                if (Math.sign(timed[i].drift) !== Math.sign(timed[i - 1].drift)) changes++;
              }
              if (changes === 0) return 'Trending';
              if (changes === 1) return 'Turning';
              return 'Churning';
            };

            // Six snapshot time points (newest → oldest).
            // Each row shows the ratio AT that point, colored by the brick it sits above.
            // Brick k: newer=SNAP_POINTS[k], older=SNAP_POINTS[k+1] → colors row k.
            // Last row (-2h) is the base → always neutral grey.
            const SNAP_POINTS = ['now', '-5m', '-15m', '-30m', '-1h', '-2h'];

            return (
              <div style={{ position:'relative' }}>
                <svg viewBox={`0 0 ${SVG_W} ${RIBBON_SVG_H}`} height={RIBBON_SVG_H}
                  style={{ width:'100%', minWidth:400, display:'block', marginTop:1, cursor:'crosshair' }}
                  onMouseMove={handleRibbonMouseMove}
                  onMouseLeave={() => setRibbonHoveredIdx(null)}>

                  <defs>
                    {/* Hatch fill for buckets whose snapshot is absent (no-data ≠ flat/zero) */}
                    <pattern id="ribbon-hatch" x="0" y="0" width="4" height="4"
                      patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="4"
                        stroke="var(--ink-3)" strokeWidth="0.8" opacity="0.35"/>
                    </pattern>
                  </defs>

                  {/* Left axis ticks */}
                  <text x={PAD.l - 4} y={RIBBON_TOP + 5}
                    textAnchor="end" fontSize={11} fill="var(--ink-3)">now</text>
                  <text x={PAD.l - 4} y={RIBBON_TOP + (RIBBON_BUCKETS.length - 1) * RIBBON_PITCH + RIBBON_CELL_H}
                    textAnchor="end" fontSize={11} fill="var(--ink-3)">−2h</text>

                  {/* Ribbon cells — each cell = one interval bucket, top=newest, bottom=oldest.
                      hasData=false → hatched (snapshot absent); hasData=true → cream→blue/gold ramp. */}
                  {showRows.map((row, i) => {
                    const cellW = Math.max(3, groupW - 2);
                    const cellX = PAD.l + i * groupW + 1;
                    const buckets = driftData[row.strike];
                    if (!buckets) {
                      return RIBBON_BUCKETS.map((_, ri) => (
                        <rect key={`${row.strike}-nd-${ri}`}
                          x={cellX} y={RIBBON_TOP + ri * RIBBON_PITCH}
                          width={cellW} height={RIBBON_CELL_H}
                          fill="url(#ribbon-hatch)" rx="1.5"/>
                      ));
                    }
                    return buckets.map((b, ri) => {
                      let fill;
                      if (!b.hasData) {
                        fill = 'url(#ribbon-hatch)';
                      } else {
                        const t = Math.max(-1, Math.min(1, b.drift / DRIFT_CONFIG.SAT_SCALE));
                        const g = Math.pow(Math.abs(t), 0.65);
                        fill = lerpRGB(DRIFT_CREAM, t >= 0 ? DRIFT_BLUE : DRIFT_GOLD, g);
                      }
                      return (
                        <rect key={`${row.strike}-b${ri}`}
                          x={cellX} y={RIBBON_TOP + ri * RIBBON_PITCH}
                          width={cellW} height={RIBBON_CELL_H}
                          fill={fill} rx="1.5"/>
                      );
                    });
                  })}

                  {/* Drift arrows */}
                  {showArrows && showRows.map((row, i) => {
                    const arrow = arrowData[row.strike];
                    if (!arrow) return null;
                    const cx   = PAD.l + (i + 0.5) * groupW;
                    const ay   = ARROW_ROW_Y;
                    const halfL = arrow.length / 2;
                    const HEAD  = 5;
                    const tipX  = arrow.direction > 0 ? cx + halfL : cx - halfL;
                    const tailX = arrow.direction > 0 ? cx - halfL : cx + halfL;
                    const shaftEnd = arrow.direction > 0 ? tipX - HEAD : tipX + HEAD;
                    const pts = arrow.direction > 0
                      ? `${tipX},${ay} ${tipX-HEAD},${ay-HEAD*0.6} ${tipX-HEAD},${ay+HEAD*0.6}`
                      : `${tipX},${ay} ${tipX+HEAD},${ay-HEAD*0.6} ${tipX+HEAD},${ay+HEAD*0.6}`;
                    return (
                      <g key={row.strike}>
                        <line x1={tailX} y1={ay} x2={shaftEnd} y2={ay}
                          stroke={arrow.color} strokeWidth={arrow.thickness} strokeLinecap="round"/>
                        <polygon points={pts} fill={arrow.color}/>
                      </g>
                    );
                  })}

                  {/* Column hover highlight — crisp outline, no fill, so brick colors stay visible */}
                  {ribbonHoveredIdx !== null && (() => {
                    const cellX = PAD.l + ribbonHoveredIdx * groupW + 1;
                    const cellW = Math.max(3, groupW - 2);
                    const cellH = (RIBBON_BUCKETS.length - 1) * RIBBON_PITCH + RIBBON_CELL_H;
                    return <rect x={cellX - 1} y={RIBBON_TOP - 1} width={cellW + 2} height={cellH + 2}
                      fill="none" stroke="var(--ink)" strokeWidth="1.5" strokeOpacity="0.55" rx="3"
                      pointerEvents="none"/>;
                  })()}

                  {/* Full-height transparent hit targets per column */}
                  {showRows.map((row, i) => (
                    <rect key={`ht-${row.strike}`}
                      x={PAD.l + i * groupW} y={0} width={groupW} height={RIBBON_SVG_H}
                      fill="transparent"/>
                  ))}
                </svg>

                {/* ── Ribbon HTML tooltip ── */}
                {ribbonHoveredIdx !== null && (() => {
                  const row = showRows[ribbonHoveredIdx];
                  if (!row) return null;
                  const isAbove = row.strike > ATM;
                  const ratioLabel = isAbove ? 'CPR' : 'PCR';
                  const arrow      = arrowData[row.strike];
                  const strikeBuckets = driftData[row.strike]; // per-interval drifts for color

                  // Six snapshot rows: ratio AT each time point, colored by the brick above it.
                  // Brick si (index 0=newest) = diff between SNAP_POINTS[si] and SNAP_POINTS[si+1].
                  // Row si gets the color of brick si (or neutral for si=5, the base -2h point).
                  const snapRows = SNAP_POINTS.map((label, si) => {
                    const oi = getOIR(label, row.strike, row);
                    if (!oi.snapExists) return { label, ratio: null, color: 'var(--ink-3)' };
                    const ratio = getRatio(oi.ceOI, oi.peOI, isAbove);
                    // Last row (-2h) is the base: no comparison → neutral
                    let color = 'var(--ink-3)';
                    if (si < 5) {
                      const b = strikeBuckets?.[si];
                      if (b?.hasData) color = b.drift > 0
                        ? `rgb(${DRIFT_BLUE.join(',')})` : `rgb(${DRIFT_GOLD.join(',')})`;
                    }
                    return { label, ratio, color };
                  });

                  // Arrow labels
                  const biasLabel  = arrow ? getBias(arrow.mom, arrow.flat)  : '—';
                  const momLabel   = arrow ? getMomentum(arrow.mom)          : '—';
                  const trendLabel = getTrend(strikeBuckets);
                  const biasColor  = biasLabel === 'Bullish' ? 'var(--up-field-ink)'
                    : biasLabel === 'Bearish' ? 'var(--down-field-ink)' : 'var(--ink-3)';

                  // FIX 1 — anchor ABOVE ribbon, centred on column.
                  // bottom:100% puts the tooltip's lower edge at the ribbon wrapper's top edge
                  // (the gap between the OI bars chart and the ribbon).
                  // left is the column-centre fraction; translateX(-50%) centres the box on the column.
                  const colCentreSvg = PAD.l + (ribbonHoveredIdx + 0.5) * groupW;
                  const colFrac      = colCentreSvg / SVG_W; // 0..1 across the full SVG width

                  const atmTag = row.strike === ATM ? 'ATM'
                    : row.strike > ATM ? 'above' : 'below';

                  return (
                    <div style={{
                      position:'absolute', bottom:'100%', marginBottom:6,
                      left:`clamp(0px, calc(${(colFrac * 100).toFixed(2)}% - 77px), calc(100% - 158px))`,
                      pointerEvents:'none', zIndex:30,
                      background:'var(--paper)', border:'1px solid var(--hair-strong)',
                      borderRadius:6, padding:'6px 8px',
                      boxShadow:'0 4px 18px rgba(0,0,0,.30)',
                      fontSize:11, lineHeight:'1.55', minWidth:154,
                      opacity: 0.92,
                    }}>
                      {/* Header */}
                      <div style={{ fontFamily:'var(--font-data)', fontWeight:700, color:'var(--ink)',
                        borderBottom:'.5px solid var(--hair)', paddingBottom:3, marginBottom:5, fontSize:11 }}>
                        {row.strike.toLocaleString('en-IN')}
                        <span style={{ fontWeight:400, color:'var(--ink-3)', marginLeft:5 }}>({atmTag})</span>
                      </div>

                      {/* 6 snapshot rows — ratio AT each time point, colored by brick above it */}
                      {snapRows.map((sr, k) => (
                        <div key={k} style={{ display:'flex', justifyContent:'space-between',
                          gap:8, alignItems:'baseline', marginBottom:1 }}>
                          <span style={{ color:'var(--ink-3)', flexShrink:0 }}>{sr.label}</span>
                          <span style={{ color: sr.color, fontFamily:'var(--font-data)',
                            fontWeight:600, whiteSpace:'nowrap' }}>
                            {sr.ratio === null ? '—' : sr.ratio.toFixed(2)}
                          </span>
                        </div>
                      ))}

                      {/* Divider */}
                      <div style={{ borderTop:'.5px solid var(--hair)', marginTop:4, paddingTop:4 }}>
                        {/* Bias */}
                        <div style={{ display:'flex', justifyContent:'space-between', gap:8, marginBottom:1 }}>
                          <span style={{ color:'var(--ink-3)' }}>Bias</span>
                          <span style={{ fontWeight:600, color: biasColor }}>{biasLabel}</span>
                        </div>
                        {/* Momentum */}
                        <div style={{ display:'flex', justifyContent:'space-between', gap:8, marginBottom:1 }}>
                          <span style={{ color:'var(--ink-3)' }}>Momentum</span>
                          <span style={{ fontWeight:600, color:'var(--ink)' }}>{momLabel}</span>
                        </div>
                        {/* Trend */}
                        <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                          <span style={{ color:'var(--ink-3)' }}>Trend</span>
                          <span style={{ fontWeight:600, color:'var(--ink)' }}>{trendLabel}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>

        {/* ── Time Range Slider + Replay ── */}
        <OITimeSlider
          snaps={sliderSnaps}
          fromIdx={sliderFrom}
          toIdx={sliderTo}
          onFrom={setSliderFrom}
          onTo={setSliderTo}
          replayActive={replayActive}
          replayPaused={replayPaused}
          onReplay={startReplay}
          onStop={stopReplay}
          onPause={pauseReplay}
          onResume={resumeReplay}
          disabled={sliderSnaps.length <= 1}
        />

        {/* ── 4-column summary ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr auto 1fr auto 1fr',
            alignItems:'stretch', marginTop:10, borderRadius:6,
            border:'.5px solid var(--hair-strong)', overflow:'hidden' }}>
          {/* Col 1: CE OI + PE OI — stacked so label/value don't compete for row width */}
          <div style={{ padding:'8px 10px', background:'var(--surface-2)' }}>
            <div style={{ marginBottom:7 }}>
              <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase',
                  letterSpacing:'.04em', marginBottom:2 }}>CE OI</div>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--ce-ink)',
                  fontFamily:'var(--font-data)', whiteSpace:'nowrap' }}>
                {fmtOI(totalCeOI)}
              </div>
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase',
                  letterSpacing:'.04em', marginBottom:2 }}>PE OI</div>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--pebar)',
                  fontFamily:'var(--font-data)', whiteSpace:'nowrap' }}>
                {fmtOI(totalPeOI)}
              </div>
            </div>
          </div>
          <div style={{ width:1, background:'var(--hair-strong)' }}/>
          {/* Col 2: CE Change + PE Change */}
          <div style={{ padding:'8px 10px', background:'var(--surface-2)' }}>
            <div style={{ marginBottom:7 }}>
              <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase',
                  letterSpacing:'.04em', marginBottom:2 }}>CE Chg</div>
              <div style={{ fontSize:11, fontWeight:700, fontFamily:'var(--font-data)',
                  whiteSpace:'nowrap', color: totalCeChg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                {fmtOISign(totalCeChg)}
              </div>
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase',
                  letterSpacing:'.04em', marginBottom:2 }}>PE Chg</div>
              <div style={{ fontSize:11, fontWeight:700, fontFamily:'var(--font-data)',
                  whiteSpace:'nowrap', color: totalPeChg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                {fmtOISign(totalPeChg)}
              </div>
            </div>
          </div>
          <div style={{ width:1, background:'var(--hair-strong)' }}/>
          {/* Col 3: PCR */}
          <div style={{ padding:'8px 10px', background:'var(--surface-2)', display:'flex',
              flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase',
                letterSpacing:'.04em', marginBottom:4 }}>PCR</div>
            <div style={{ fontSize:18, fontWeight:700, color:'var(--ink)',
                fontFamily:'var(--font-data)', lineHeight:1, whiteSpace:'nowrap' }}>
              {overallPCR.toFixed(2)}
            </div>
          </div>
          <div style={{ width:1, background:'var(--hair-strong)' }}/>
          {/* Col 4: Max Pain */}
          <div style={{ padding:'8px 10px', background:'var(--surface-2)', display:'flex',
              flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase',
                letterSpacing:'.04em', marginBottom:4 }}>Max Pain</div>
            <div style={{ fontSize:16, fontWeight:700,
                fontFamily:'var(--font-data)', lineHeight:1, whiteSpace:'nowrap',
                color: computedMaxPain ? (
                  spot && computedMaxPain < spot ? 'var(--down-field-ink)' :
                  spot && computedMaxPain > spot ? 'var(--up-field-ink)' : 'var(--ink)'
                ) : 'var(--ink-3)' }}>
              {computedMaxPain ? computedMaxPain.toLocaleString('en-IN') : '—'}
            </div>
            {computedMaxPain && spot ? (
              <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:3, whiteSpace:'nowrap' }}>
                {computedMaxPain < spot
                  ? `↓ ${(spot - computedMaxPain).toFixed(0)} pts`
                  : computedMaxPain > spot
                  ? `↑ ${(computedMaxPain - spot).toFixed(0)} pts`
                  : 'at spot'}
              </div>
            ) : null}
          </div>
        </div>

        {/* ── Phase-2: PCR / OI History timeline ── */}
        {pcrHistory.length > 1 && (
          <OIPCRHistoryChart history={pcrHistory} niftyIntraday={niftyIntraday} symbol={symbol} />
        )}

        {/* ── Max Pain History (last 5 sessions) ── */}
        {maxPainHistory.length > 0 && (
          <MaxPainHistoryChart history={maxPainHistory} symbol={symbol} />
        )}

        {/* ── OI Walls (Support & Resistance) ── */}
        {mergedRows.length > 0 && (
          <OIWallsChart rows={mergedRows} spot={spot} atm={ATM} />
        )}

        {/* ── IV / HV Chart ── */}
        {ivHistory.length > 10 && (
          <IVHVChart history={ivHistory} meta={ivMeta}
            onSyncHover={setIvHoverDate} syncHoverDate={ivHoverDate} />
        )}

        {/* ── IV Percentile Chart ── */}
        {ivHistory.length > 10 && (
          <IVPercentileChart history={ivHistory} meta={ivMeta}
            onSyncHover={setIvHoverDate} syncHoverDate={ivHoverDate} />
        )}

        {/* ── Premium (LTP) Bar Chart ── */}
        {(() => {
          const noLive    = !rows?.length;
          const noHistory = !hasPriceHistory;

          if (noLive && noHistory) {
            return (
              <div style={{ marginTop:10, padding:'8px 12px', fontSize:10,
                  color:'var(--ink-3)', background:'var(--paper-2)',
                  border:'.5px solid var(--hair)', borderRadius:4, textAlign:'center' }}>
                No price history for {historyDate || '—'}.
              </div>
            );
          }

          const priceScrubberActive = !priceSliderIsDefault && priceSnaps.length > 1;
          const intervalNoPriceData = priceScrubberActive && sliderActivePriceRows === null;
          const pRows               = priceShowRows ?? [];
          const pN                  = pRows.length || 1;

          const P_SVG_W = premiumSvgW, P_SVG_H = _mob ? 220 : 325;
          const P_PAD   = { l:52, r:40, t:22, b:54 };
          const pChartW = P_SVG_W - P_PAD.l - P_PAD.r;
          const pChartH = P_SVG_H - P_PAD.t - P_PAD.b;
          const pGroupW = pChartW / pN;
          const pBarW   = Math.max(2, Math.min(12, pGroupW * 0.25));
          const pGap    = Math.max(1.5, (pGroupW - pBarW * 2) / 3);
          const py0     = P_PAD.t + pChartH;

          const ltpVals = pRows.flatMap(r => {
            const vals = [];
            if (r.ceLTP != null && r.ceLTP > 0) vals.push(r.ceLTP);
            if (r.peLTP != null && r.peLTP > 0) vals.push(r.peLTP);
            if (r.ceLTP != null && r.ceLTPChg != null) vals.push(Math.max(0, r.ceLTP - r.ceLTPChg));
            if (r.peLTP != null && r.peLTPChg != null) vals.push(Math.max(0, r.peLTP - r.peLTPChg));
            return vals;
          });
          const maxLTP  = Math.max(1, ...ltpVals.filter(v => v > 0));
          const pyLTP   = v => P_PAD.t + pChartH * (1 - Math.max(0, v) / maxLTP);

          const rawStep  = maxLTP / 4;
          const niceSteps = [0.25,0.5,1,2,5,10,20,25,50,100,200,250,500,1000,2000];
          const pStep    = niceSteps.find(s => s >= rawStep) || niceSteps[niceSteps.length - 1];
          const pTicks   = [];
          for (let v = 0; v <= maxLTP * 1.05; v += pStep) pTicks.push({ v, y: pyLTP(v) });

          const fmtLTP   = v => v >= 100 ? Math.round(v).toString() : v.toFixed(1);
          const pAtmIdx      = pRows.findIndex(r => Number(r.strike) === ATM);
          const pDisplayAtmIdx = pRows.findIndex(r => Number(r.strike) === priceDisplayATM);

          const baselineLabel = intervalNoPriceData
            ? 'Showing vs D-1 (no price data for this interval)'
            : priceScrubberActive
            ? `Δ vs ${priceSnaps[priceSliderFrom]?.time ?? '—'}`
            : 'Change vs D-1 close';

          const barParts = (ltp, ltpChg, peX, ceX, isCe, pBarW_) => {
            if (ltp == null || ltp <= 0) return null;
            const topY = pyLTP(ltp);
            const barH = Math.max(1, py0 - topY);
            const x    = isCe ? ceX : peX;
            const col  = isCe ? 'var(--cebar)' : 'var(--pebar)';
            // Zero/null change → no overlay (plain solid bar, distinct from nonzero).
            // Nonzero change → overlay at its true proportional height; sub-pixel = sub-pixel.
            if (ltpChg == null || ltpChg === 0) {
              return { bodyY: topY, bodyH: barH, overlay: null };
            }
            const prevTopY = pyLTP(Math.max(0, ltp - ltpChg));
            if (ltpChg > 0) {
              const capH     = Math.max(0, prevTopY - topY);
              const bodyTopY = topY + capH;
              return {
                bodyY: bodyTopY, bodyH: Math.max(0, py0 - bodyTopY),
                overlay: <rect x={x} y={topY} width={pBarW_} height={capH} fill={col} opacity=".35" rx="1"/>,
              };
            } else {
              const ovH = Math.max(0, topY - prevTopY);
              return {
                bodyY: topY, bodyH: barH,
                overlay: <rect x={x + 0.5} y={topY - ovH} width={pBarW_ - 1} height={ovH}
                            fill="none" stroke={col} strokeWidth="1" opacity=".5"/>,
              };
            }
          };

          return (
            <div style={{ marginTop:14 }}>
              {/* header + own ±N pills */}
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                <span style={{ fontSize:11, fontWeight:600 }}>Premium Chart</span>
                <span style={{ fontSize:9, color:'var(--ink-3)' }}>{baselineLabel}</span>
                {/* Disabled state is shown via the OITimeSlider below the chart */}
                {/* CE / PE legend */}
                <div style={{ display:'flex', gap:10, fontSize:10, color:'var(--ink-3)',
                    alignItems:'center', marginLeft:'auto' }}>
                  <span style={{ display:'flex', alignItems:'center', gap:3 }}>
                    <svg width="20" height="10">
                      <rect x="0" y="1" width="10" height="8" fill="var(--pebar)" opacity=".85"/>
                      <rect x="10" y="1" width="10" height="8" fill="var(--pebar)" opacity=".35"/>
                    </svg>
                    PE
                  </span>
                  <span style={{ display:'flex', alignItems:'center', gap:3 }}>
                    <svg width="20" height="10">
                      <rect x="0" y="1" width="10" height="8" fill="var(--cebar)" opacity=".85"/>
                      <rect x="10" y="1" width="10" height="8" fill="var(--cebar)" opacity=".35"/>
                    </svg>
                    CE
                  </span>
                </div>
                {/* Independent strike filter pills */}
                <div style={{ display:'flex', gap:4 }}>
                  {(['all', 5, 10, 15]).map(v => {
                    const active = priceStrikeFilter === v;
                    return (
                      <button key={v} onClick={() => setPriceStrikeFilter(v)}
                        style={{ fontSize:11, fontWeight:600, padding:'2px 9px', cursor:'pointer',
                          borderRadius:10, border: active ? 'none' : '.5px solid var(--hair-strong)',
                          background: active ? 'var(--amber-ink)' : 'transparent',
                          color: active ? '#fff' : 'var(--ink-2)', lineHeight:1.6 }}>
                        {v === 'all' ? 'All' : `±${v}`}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div ref={premiumChartRef}>
              <svg viewBox={`0 0 ${P_SVG_W} ${P_SVG_H}`} height={P_SVG_H}
                style={{ width:'100%', minWidth:400, display:'block' }}
                onMouseMove={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const svgX  = (e.clientX - rect.left) / rect.width * P_SVG_W;
                  const idx   = Math.floor((svgX - P_PAD.l) / pGroupW);
                  setPriceHoveredIdx(idx >= 0 && idx < pN ? idx : null);
                }}
                onMouseLeave={() => setPriceHoveredIdx(null)}>

                {pTicks.map(({ v, y }) => (
                  <g key={v}>
                    <line x1={P_PAD.l} x2={P_SVG_W - P_PAD.r} y1={y} y2={y}
                      stroke="var(--hair)" strokeWidth=".5" opacity=".6"/>
                    <text x={P_PAD.l - 4} y={y + 3} textAnchor="end"
                      fontSize={11} fill="var(--ink-3)">{fmtLTP(v)}</text>
                  </g>
                ))}
                <text x={P_SVG_W - P_PAD.r + 4} y={P_PAD.t + 4}
                  fontSize={11} fill="var(--ink-3)">₹</text>
                <line x1={P_PAD.l} x2={P_SVG_W - P_PAD.r} y1={py0} y2={py0}
                  stroke="var(--hair-strong)" strokeWidth=".8"/>

                {pRows.map((row, i) => {
                  const isATM   = Number(row.strike) === ATM;
                  const showLbl = row.strike % 100 === 0 || isATM;
                  const xStart  = P_PAD.l + i * pGroupW;
                  const peX     = xStart + pGap;
                  const ceX     = peX + pBarW + pGap * 0.5;

                  if (row.ceLTP == null && row.peLTP == null) {
                    return (
                      <g key={row.strike}>
                        {isATM && <rect x={xStart} y={P_PAD.t} width={pGroupW} height={pChartH}
                          fill="rgba(254,249,231,.35)" rx="1"/>}
                        {showLbl && (
                          <text x={xStart + pGroupW / 2} y={P_SVG_H - P_PAD.b + 14}
                            textAnchor="middle" fontSize={isATM ? 12 : 11}
                            fontWeight={isATM ? 700 : 400}
                            fill={isATM ? 'var(--amber)' : 'var(--ink-3)'}
                            transform={`rotate(-40,${xStart + pGroupW / 2},${P_SVG_H - P_PAD.b + 14})`}>
                            {row.strike}
                          </text>
                        )}
                      </g>
                    );
                  }

                  const pe = barParts(row.peLTP, row.peLTPChg, peX, ceX, false, pBarW);
                  const ce = barParts(row.ceLTP, row.ceLTPChg, peX, ceX, true,  pBarW);

                  return (
                    <g key={row.strike}>
                      {isATM && <rect x={xStart} y={P_PAD.t} width={pGroupW} height={pChartH}
                        fill="rgba(254,249,231,.35)" rx="1"/>}
                      {pe && <>
                        <rect x={peX} y={pe.bodyY} width={pBarW} height={pe.bodyH}
                          fill="var(--pebar)" opacity=".85" rx="1"/>
                        {pe.overlay}
                      </>}
                      {ce && <>
                        <rect x={ceX} y={ce.bodyY} width={pBarW} height={ce.bodyH}
                          fill="var(--cebar)" opacity=".85" rx="1"/>
                        {ce.overlay}
                      </>}
                      {showLbl && (
                        <text x={xStart + pGroupW / 2} y={P_SVG_H - P_PAD.b + 14}
                          textAnchor="middle" fontSize={isATM ? 12 : 11}
                          fontWeight={isATM ? 700 : 400}
                          fill={isATM ? 'var(--amber)' : 'var(--ink-3)'}
                          transform={`rotate(-40,${xStart + pGroupW / 2},${P_SVG_H - P_PAD.b + 14})`}>
                          {row.strike}
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* ATM dashed vertical (tracks scrubber/replay) */}
                {(pDisplayAtmIdx !== -1 || pAtmIdx !== -1) && (() => {
                  const useIdx = pDisplayAtmIdx !== -1 ? pDisplayAtmIdx : pAtmIdx;
                  const ax = P_PAD.l + (useIdx + 0.5) * pGroupW;
                  return (
                    <g>
                      <line x1={ax} y1={P_PAD.t} x2={ax} y2={py0}
                        stroke="var(--amber)" strokeWidth=".6" strokeDasharray="2,3" opacity=".5"/>
                      {priceDisplaySpot > 0 && (
                        <text x={ax} y={P_PAD.t - 4}
                          textAnchor="middle" fontSize={11} fill="var(--amber)" opacity=".8">
                          {Math.round(priceDisplaySpot).toLocaleString('en-IN')}
                        </text>
                      )}
                    </g>
                  );
                })()}

                {/* ── Premium chart hover crosshair + tooltip ── */}
                {priceHoveredIdx !== null && (() => {
                  const hrow = pRows[priceHoveredIdx];
                  if (!hrow) return null;
                  const hx = P_PAD.l + (priceHoveredIdx + 0.5) * pGroupW;

                  // values
                  const ceLTP    = hrow.ceLTP ?? null;
                  const peLTP    = hrow.peLTP ?? null;
                  const ceLTPChg = hrow.ceLTPChg ?? null;
                  const peLTPChg = hrow.peLTPChg ?? null;
                  const cePrev   = ceLTP != null && ceLTPChg != null ? ceLTP - ceLTPChg : null;
                  const pePrev   = peLTP != null && peLTPChg != null ? peLTP - peLTPChg : null;
                  const cePct    = cePrev != null && cePrev > 0 ? (ceLTPChg / cePrev * 100) : null;
                  const pePct    = pePrev != null && pePrev > 0 ? (peLTPChg / pePrev * 100) : null;

                  const fmtChg = (chg, pct) => {
                    if (chg == null) return '—';
                    const sign = chg >= 0 ? '+' : '';
                    const pctStr = pct != null ? ` (${chg >= 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
                    return `${sign}${fmtLTP(chg)}${pctStr}`;
                  };

                  // Tooltip sizing — 2 sections (CE + PE), 3 rows each + header
                  const TW = 132, TH = 168;
                  const tx = Math.round(Math.max(P_PAD.l, Math.min(P_SVG_W - P_PAD.r - TW, hx - TW / 2)));
                  const ty = P_PAD.t + 4;

                  const baseShort = priceScrubberActive
                    ? (priceSnaps[priceSliderFrom]?.time ?? '—')
                    : 'D-1 close';

                  return (
                    <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
                      {/* crosshair */}
                      <line x1={hx} y1={P_PAD.t} x2={hx} y2={py0}
                        stroke="var(--hair-strong)" strokeWidth=".6" opacity=".5"/>
                      {/* box */}
                      <rect x={tx} y={ty} width={TW} height={TH}
                        fill="var(--surface-2)" fillOpacity="0.7"
                        stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
                        style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
                      {/* Strike title */}
                      <text x={tx + 7} y={ty + 16} fontSize={11} fontWeight={700} fill="var(--ink)">
                        {Number(hrow.strike).toLocaleString('en-IN')}{Number(hrow.strike) === ATM ? ' (ATM)' : ''}
                      </text>
                      {/* baseline sub-label */}
                      <text x={tx + 7} y={ty + 28} fontSize={9} fill="var(--ink-3)">
                        vs {baseShort}
                      </text>

                      {/* CE section */}
                      <text x={tx + 7} y={ty + 44} fontSize={11} fontWeight={700} fill="var(--cebar)">CE</text>
                      <text x={tx + 7} y={ty + 58} fontSize={11} fill="var(--ink-2)">Current</text>
                      <text x={tx + TW - 7} y={ty + 58} textAnchor="end" fontSize={11} fontWeight={600} fill="var(--ink)">
                        {ceLTP != null ? `₹${fmtLTP(ceLTP)}` : '—'}
                      </text>
                      <text x={tx + 7} y={ty + 72} fontSize={11} fill="var(--ink-2)">{baseShort}</text>
                      <text x={tx + TW - 7} y={ty + 72} textAnchor="end" fontSize={11} fontWeight={600} fill="var(--ink-3)">
                        {cePrev != null ? `₹${fmtLTP(cePrev)}` : '—'}
                      </text>
                      <text x={tx + 7} y={ty + 86} fontSize={11} fill="var(--ink-2)">Change</text>
                      <text x={tx + TW - 7} y={ty + 86} textAnchor="end" fontSize={11} fontWeight={600}
                        fill={ceLTPChg == null ? 'var(--ink-3)' : ceLTPChg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}>
                        {fmtChg(ceLTPChg, cePct)}
                      </text>

                      {/* divider */}
                      <line x1={tx + 6} x2={tx + TW - 6} y1={ty + 94} y2={ty + 94}
                        stroke="var(--hair)" strokeWidth=".5"/>

                      {/* PE section */}
                      <text x={tx + 7} y={ty + 108} fontSize={11} fontWeight={700} fill="var(--pebar)">PE</text>
                      <text x={tx + 7} y={ty + 122} fontSize={11} fill="var(--ink-2)">Current</text>
                      <text x={tx + TW - 7} y={ty + 122} textAnchor="end" fontSize={11} fontWeight={600} fill="var(--ink)">
                        {peLTP != null ? `₹${fmtLTP(peLTP)}` : '—'}
                      </text>
                      <text x={tx + 7} y={ty + 136} fontSize={11} fill="var(--ink-2)">{baseShort}</text>
                      <text x={tx + TW - 7} y={ty + 136} textAnchor="end" fontSize={11} fontWeight={600} fill="var(--ink-3)">
                        {pePrev != null ? `₹${fmtLTP(pePrev)}` : '—'}
                      </text>
                      <text x={tx + 7} y={ty + 150} fontSize={11} fill="var(--ink-2)">Change</text>
                      <text x={tx + TW - 7} y={ty + 150} textAnchor="end" fontSize={11} fontWeight={600}
                        fill={peLTPChg == null ? 'var(--ink-3)' : peLTPChg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}>
                        {fmtChg(peLTPChg, pePct)}
                      </text>
                    </g>
                  );
                })()}
              </svg>
              </div>
              {intervalNoPriceData && (
                <div style={{ fontSize:9, color:'var(--ink-3)', textAlign:'center', marginTop:2 }}>
                  No intraday price snapshots for this interval — showing vs D-1 baseline.
                </div>
              )}

              {/* ── Premium chart's own independent scrubber ── */}
              {/* snaps=priceSnaps (price-only): indices, pill-span checks, and disabled gate
                  all based on PRICE history, never on the OI snapshot count. */}
              <OITimeSlider
                snaps={priceSnaps}
                fromIdx={priceSliderFrom}
                toIdx={priceSliderTo}
                onFrom={setPriceSliderFrom}
                onTo={setPriceSliderTo}
                replayActive={priceReplayActive}
                replayPaused={priceReplayPaused}
                onReplay={startPriceReplay}
                onStop={stopPriceReplay}
                onPause={pausePriceReplay}
                onResume={resumePriceReplay}
                disabled={priceSnaps.length <= 1}
                availableSpanMinutes={priceAvailableSpanMinutes}
              />
            </div>
          );
        })()}

      </div>
    </div>
  );
}


// ── OI Walls Chart ────────────────────────────────────────────────────────────
// Horizontal bar chart: strikes on X-axis, OI bars vertical, LTP centred.
// Shows top-5 CE + top-5 PE walls. Equal strikes each side of LTP.
function OIWallsChart({ rows, spot, atm }) {
  const svgRef       = useRef(null);
  const [hoverI, setHoverI] = useState(null);

  const spotVal = spot || atm || 0;

  const allRows = useMemo(() =>
    [...rows].filter(r => r.ceOI > 0 || r.peOI > 0).sort((a, b) => a.strike - b.strike), [rows]);

  const top5CE = useMemo(() =>
    new Set([...rows].filter(r => r.ceOI > 0).sort((a,b) => b.ceOI - a.ceOI).slice(0,5).map(r => r.strike)), [rows]);
  const top5PE = useMemo(() =>
    new Set([...rows].filter(r => r.peOI > 0).sort((a,b) => b.peOI - a.peOI).slice(0,5).map(r => r.strike)), [rows]);
  const top1CE = useMemo(() =>
    rows.filter(r => r.ceOI > 0).sort((a,b) => b.ceOI - a.ceOI)[0]?.strike ?? null, [rows]);
  const top1PE = useMemo(() =>
    rows.filter(r => r.peOI > 0).sort((a,b) => b.peOI - a.peOI)[0]?.strike ?? null, [rows]);

  const atmIdx = useMemo(() => {
    let best = 0, bestDist = Infinity;
    allRows.forEach((r, i) => { const d = Math.abs(r.strike - spotVal); if (d < bestDist) { bestDist = d; best = i; } });
    return best;
  }, [allRows, spotVal]);

  const maxCEDist = useMemo(() => { let m=0; allRows.forEach((r,i)=>{ if(top5CE.has(r.strike)) m=Math.max(m,Math.abs(i-atmIdx)); }); return m; }, [allRows,top5CE,atmIdx]);
  const maxPEDist = useMemo(() => { let m=0; allRows.forEach((r,i)=>{ if(top5PE.has(r.strike)) m=Math.max(m,Math.abs(i-atmIdx)); }); return m; }, [allRows,top5PE,atmIdx]);

  const halfN    = Math.max(maxCEDist, maxPEDist) + 1;
  const loIdx    = Math.max(0, atmIdx - halfN);
  const hiIdx    = Math.min(allRows.length - 1, atmIdx + halfN);
  const showRows = allRows.slice(loIdx, hiIdx + 1);

  // SVG layout — PAD_R=44 matches PCR/volatility charts for right-edge alignment
  const [W, containerRef] = useContainerWidth(820); const H = 222;
  const PAD_L = 12, PAD_R = 44, PAD_T = 10, PAD_B = 42;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const n      = showRows.length;
  const groupW = chartW / Math.max(n, 1);

  // Mouse handler — map to group index (hooks must come before early return)
  const handleMouseMove = useCallback((e) => {
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) / rect.width * W;
    const i = Math.floor((svgX - PAD_L) / groupW);
    setHoverI(i >= 0 && i < n ? i : null);
  }, [n, groupW]);
  const handleMouseLeave = useCallback(() => setHoverI(null), []);

  if (!showRows.length) return null;

  const maxOI = Math.max(...showRows.flatMap(r => [r.ceOI ?? 0, r.peOI ?? 0]), 1);

  // Strike spacing (for ATM detection tolerance)
  const strikeSpacing = allRows.length > 1 ? (allRows[1].strike - allRows[0].strike) : 50;

  const barW   = Math.max(3, Math.min(22, groupW * 0.38));
  const gap    = (groupW - barW * 2) / 3;
  const y0     = PAD_T + chartH;
  const yBar   = v => PAD_T + chartH * (1 - v / maxOI);

  // Y-axis OI ticks
  const _step = Math.ceil(maxOI / 3 / 100_000) * 100_000 || 500_000;
  const oiTicks = [];
  for (let v = _step; v <= maxOI * 1.05; v += _step) oiTicks.push(v);

  // ATM visible index
  const atmVisIdx = showRows.findIndex(r => Math.abs(r.strike - spotVal) < strikeSpacing * 0.5);
  const ltpX = atmVisIdx >= 0 ? PAD_L + (atmVisIdx + 0.5) * groupW : null;

  // Tooltip
  let tooltipEl = null;
  if (hoverI !== null) {
    const row = showRows[hoverI];
    const tx0 = PAD_L + hoverI * groupW;
    const TW = 104, TH = 64;
    // Show tooltip to the right in the left half of the chart, left in the right half
    const tx = Math.round(hoverI >= n / 2 ? tx0 - TW - 4 : tx0 + groupW + 2);
    const ty = PAD_T + 4;
    tooltipEl = (
      <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
        <rect x={tx0} y={PAD_T} width={groupW} height={chartH}
          fill="rgba(255,255,255,0.04)"/>
        <rect x={tx} y={ty} width={TW} height={TH}
          fill="var(--surface-2)" fillOpacity="0.85" stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
          style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
        <text x={tx + TW/2} y={ty + 15} textAnchor="middle"
          fontSize={11} fontWeight={700} fill="var(--ink)">{row.strike}</text>
        <text x={tx + 7}      y={ty + 31} fontSize={11} fill="var(--ink-2)">PE OI</text>
        <text x={tx + TW - 7} y={ty + 31} textAnchor="end"
          fontSize={11} fontWeight={600} fill="var(--pebar)">{fmtOI(row.peOI || 0)}</text>
        <text x={tx + 7}      y={ty + 49} fontSize={11} fill="var(--ink-2)">CE OI</text>
        <text x={tx + TW - 7} y={ty + 49} textAnchor="end"
          fontSize={11} fontWeight={600} fill="var(--cebar)">{fmtOI(row.ceOI || 0)}</text>
      </g>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4, paddingLeft:2, flexWrap:'wrap' }}>
        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, fontWeight:600, color:'var(--ink-2)' }}>OI Walls</span>
          <InfoBtn align="right">
            Top-5 CE OI strikes (right of LTP) = likely resistance. Top-5 PE OI strikes (left of LTP) = likely support. Clustered moderate walls can be as significant as a single large wall.
          </InfoBtn>
        </span>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>Top-5 each side · LTP centred</span>
        <div style={{ marginLeft:'auto', display:'flex', gap:12, fontSize:11, color:'var(--ink-3)', alignItems:'center' }}>
          <span style={{ display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ width:10, height:10, borderRadius:2, background:'var(--pebar)', opacity:.8, display:'inline-block' }}/>PE
          </span>
          <span style={{ display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ width:10, height:10, borderRadius:2, background:'var(--cebar)', opacity:.8, display:'inline-block' }}/>CE
          </span>
          <span style={{ display:'flex', alignItems:'center', gap:4 }}>
            <svg width="20" height="8">
              <line x1="0" y1="4" x2="20" y2="4" stroke="var(--accent-blue)"
                strokeWidth="1" strokeDasharray="3,2" opacity=".6"/>
            </svg>
            LTP
          </span>
        </div>
      </div>

      <div ref={containerRef} style={{ overflowX:'auto' }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} height={H}
          style={{ width:'100%', minWidth:320, display:'block', cursor:'crosshair' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}>

          {/* Y grid lines */}
          {oiTicks.map(v => (
            <line key={v} x1={PAD_L} x2={W - PAD_R} y1={yBar(v)} y2={yBar(v)}
              stroke="var(--hair)" strokeWidth=".4" strokeDasharray="3,3"/>
          ))}

          {/* Bars */}
          {showRows.map((row, i) => {
            const { strike, ceOI = 0, peOI = 0 } = row;
            const xBase    = PAD_L + i * groupW;
            const peX      = xBase + gap;
            const ceX      = peX + barW + gap * 0.5;
            const isTop5CE = top5CE.has(strike);
            const isTop5PE = top5PE.has(strike);
            // Non-top5 bars are grey; top5 use brand colour
            const peColor  = isTop5PE ? 'var(--pebar)' : 'var(--ink-3)';
            const ceColor  = isTop5CE ? 'var(--cebar)' : 'var(--ink-3)';
            const peOp     = isTop5PE ? 0.75 : 0.25;
            const ceOp     = isTop5CE ? 0.75 : 0.25;
            // X-axis label: only multiples of 100 (no ATM label if it's X50)
            const showLbl  = strike % 100 === 0;
            const peH      = Math.max(1, y0 - yBar(peOI));
            const ceH      = Math.max(1, y0 - yBar(ceOI));

            return (
              <g key={strike}>
                {peOI > 0 && <rect x={peX} y={yBar(peOI)} width={barW} height={peH}
                  fill={peColor} opacity={peOp} rx={1}/>}
                {ceOI > 0 && <rect x={ceX} y={yBar(ceOI)} width={barW} height={ceH}
                  fill={ceColor} opacity={ceOp} rx={1}/>}
                {(isTop5CE || isTop5PE) && (
                  <text x={xBase + groupW / 2} y={y0 + 12}
                    textAnchor="middle" fontSize={11} fontWeight={600}
                    fill={isTop5CE && isTop5PE ? 'var(--ink-2)' : isTop5CE ? 'var(--cebar)' : 'var(--pebar)'}
                    opacity={0.9}>
                    {strike}
                  </text>
                )}
                {strike === top1CE && ceOI > 0 && (
                  <text x={xBase + groupW / 2} y={y0 + 21}
                    textAnchor="middle" fontSize={6} fill="var(--cebar)" opacity={0.75}>
                    {fmtOI(ceOI)}
                  </text>
                )}
                {strike === top1PE && peOI > 0 && (
                  <text x={xBase + groupW / 2} y={y0 + (strike === top1CE ? 30 : 21)}
                    textAnchor="middle" fontSize={6} fill="var(--pebar)" opacity={0.75}>
                    {fmtOI(peOI)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Baseline */}
          <line x1={PAD_L} x2={W - PAD_R} y1={y0} y2={y0}
            stroke="var(--hair-strong)" strokeWidth=".8"/>

          {/* LTP dashed vertical */}
          {ltpX != null && (
            <line x1={ltpX} y1={PAD_T} x2={ltpX} y2={y0}
              stroke="var(--accent-blue)" strokeWidth=".9" strokeDasharray="3,2" opacity=".55"/>
          )}

          {/* Tooltip */}
          {tooltipEl}
        </svg>
      </div>
    </div>
  );
}


// ── IV / HV History Chart ─────────────────────────────────────────────────────
// 4 lines: ATM IV (purple), HV30 (green), HV10 (orange), IV-RV spread (red dashed)
// Data: /api/options/iv-history — uses India VIX as NIFTY 30D IV proxy.
function IVHVChart({ history, meta, onSyncHover, syncHoverDate }) {
  const svgRef       = useRef(null);
  const [W, containerRef] = useContainerWidth(820); const H = 240;
  const PAD_L = 44, PAD_R = 52, PAD_T = 20, PAD_B = 28;
  const cW = W - PAD_L - PAD_R;
  const cH = H - PAD_T - PAD_B;

  const [hoverIdx, setHoverIdx] = useState(null);
  const [vis, setVis] = useState(() => new Set(['iv','hv30','hv10','iv_rv']));
  const toggleLine = key => setVis(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const n = history.length;

  // Ranges
  const allVals = history.flatMap(r => [
    r.iv, r.hv30, r.hv10,
  ].filter(v => v != null && isFinite(v)));
  const spreadVals = history.map(r => r.iv_rv).filter(v => v != null && isFinite(v));

  const _lo = Math.min(...allVals, ...spreadVals, 0);
  const _hi = Math.max(...allVals, 1);
  const pad  = (_hi - _lo) * 0.08;
  const yMin = _lo - pad;
  const yMax = _hi + pad;

  const yOf  = v => (v == null || !isFinite(v)) ? null
    : PAD_T + cH * (1 - (v - yMin) / Math.max(0.01, yMax - yMin));
  const xOf  = i => PAD_L + (i / Math.max(1, n - 1)) * cW;

  // Build polyline points for each series (skip nulls)
  const buildPts = key => history
    .map((r, i) => { const y = yOf(r[key]); return y != null ? `${xOf(i).toFixed(1)},${y.toFixed(1)}` : null; })
    .filter(Boolean).join(' ');

  const ivPts   = buildPts('iv');
  const hv30Pts = buildPts('hv30');
  const hv10Pts = buildPts('hv10');
  const ivRvPts = buildPts('iv_rv');

  // X-axis labels: ~8 evenly spaced
  const step    = Math.max(1, Math.floor(n / 8));
  const xTicks  = history.map((r, i) => (i % step === 0 || i === n - 1) ? { i, d: r.date } : null).filter(Boolean);

  // Y-axis reference lines
  const yZero   = yOf(0);
  const yGrids  = [0, 10, 20, 30, 40].map(v => ({ v, y: yOf(v) })).filter(g => g.y != null && g.y >= PAD_T && g.y <= PAD_T + cH);

  // Hover — fires onSyncHover so the sibling chart follows
  const handleMouseMove = useCallback(e => {
    if (n < 2) return;
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) / rect.width * W;
    if (svgX < PAD_L || svgX > W - PAD_R) { setHoverIdx(null); onSyncHover?.(null); return; }
    const idx = Math.max(0, Math.min(n - 1, Math.round((svgX - PAD_L) / cW * (n - 1))));
    setHoverIdx(idx);
    onSyncHover?.(history[idx]?.date ?? null);
  }, [n, history, onSyncHover]);
  const handleMouseLeave = useCallback(() => { setHoverIdx(null); onSyncHover?.(null); }, [onSyncHover]);

  // effectiveIdx: own hover takes priority; fall back to sibling's date
  const syncIdx = syncHoverDate ? history.findIndex(r => r.date === syncHoverDate) : -1;
  const effectiveIdx = hoverIdx !== null ? hoverIdx : syncIdx >= 0 ? syncIdx : null;

  if (n < 5) return null;

  let cursorEls = null;
  if (effectiveIdx !== null) {
    const r  = history[effectiveIdx];
    const cx = xOf(effectiveIdx);
    const TW = 130, TH = 96;
    const tx = Math.round(cx + 8 + TW > W - PAD_R ? cx - 8 - TW : cx + 8);
    const ty = PAD_T + 3;
    const rows_ = [
      { l:'IV (VIX)',  v: r.iv   != null ? r.iv.toFixed(1)   + '%' : '—', c:'#a78bfa' },
      { l:'HV30',      v: r.hv30 != null ? r.hv30.toFixed(1) + '%' : '—', c:'var(--up-field-ink)' },
      { l:'HV10',      v: r.hv10 != null ? r.hv10.toFixed(1) + '%' : '—', c:'#fb923c' },
      { l:'IV-RV',     v: r.iv_rv!= null ? r.iv_rv.toFixed(1)+ '%' : '—', c:'var(--down-field-ink)' },
    ];
    cursorEls = (
      <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
        <line x1={cx} x2={cx} y1={PAD_T} y2={PAD_T + cH}
          stroke="var(--ink-3)" strokeWidth=".8" strokeDasharray="2,2" opacity=".5"/>
        {[['iv','#a78bfa'],['hv30','var(--up-field-ink)'],['hv10','#fb923c']].map(([k,c]) => {
          if (!vis.has(k)) return null;
          const y = yOf(r[k]); return y != null ? <circle key={k} cx={cx} cy={y} r="2.5" fill={c}/> : null;
        })}
        <rect x={tx} y={ty} width={TW} height={TH}
          fill="var(--surface-2)" fillOpacity=".85" stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
          style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
        <text x={tx + TW/2} y={ty+16} textAnchor="middle"
          fontSize={11} fontWeight={700} fill="var(--ink)">{r.date}</text>
        {rows_.map((item, k) => (
          <g key={k}>
            <text x={tx+7}      y={ty+30+k*16} fontSize={11} fill="var(--ink-2)">{item.l}</text>
            <text x={tx+TW-7}   y={ty+30+k*16} textAnchor="end"
              fontSize={11} fontWeight={600} fill={item.c}>{item.v}</text>
          </g>
        ))}
      </g>
    );
  }

  return (
    <div style={{ marginTop:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4, paddingLeft:2 }}>
        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, fontWeight:600, color:'var(--ink-2)' }}>IV &amp; Historical Volatility</span>
          <InfoBtn align="right">
            IV = India VIX (NIFTY 30D implied vol proxy). HV30 = realised 30-day historical vol. IV−HV spread: positive = options expensive relative to realised moves.
          </InfoBtn>
        </span>
        {meta && (
          <span style={{ fontSize:11, color:'var(--ink-3)' }}>
            IV {meta.latest_iv?.toFixed(1)}% · HV30 {meta.latest_hv30?.toFixed(1)}% · Spread {meta.latest_iv_rv?.toFixed(1)}%
          </span>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:10, fontSize:11, color:'var(--ink-3)', alignItems:'center' }}>
          {[['iv','IV (VIX)','#a78bfa',false],['hv30','HV30','var(--up-field-ink)',false],['hv10','HV10','#fb923c',false],['iv_rv','IV-RV','var(--down-field-ink)',true]].map(([key,l,c,dashed]) => {
            const active = vis.has(key);
            return (
              <span key={key} onClick={() => toggleLine(key)}
                style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer',
                  opacity: active ? 1 : 0.35, transition:'opacity .15s',
                  userSelect:'none', padding:'2px 4px', borderRadius:3,
                  background: active ? 'transparent' : 'var(--surface-2)' }}>
                <svg width="20" height="8">
                  <line x1="0" y1="4" x2="20" y2="4" stroke={c} strokeWidth={active ? 1.5 : 1}
                    strokeDasharray={dashed ? '3,2' : 'none'}/>
                </svg>{l}
              </span>
            );
          })}
        </div>
      </div>
      <div ref={containerRef} style={{ overflowX:'auto' }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} height={H}
          style={{ width:'100%', minWidth:340, display:'block', cursor:'crosshair' }}
          onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>

          {/* Grid lines */}
          {yGrids.map(({ v, y }) => (
            <g key={v}>
              <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                stroke="var(--hair)" strokeWidth={v === 0 ? '.8' : '.35'} opacity=".5"
                strokeDasharray={v === 0 ? '3,3' : 'none'}/>
              <text x={PAD_L - 3} y={y + 3} textAnchor="end"
                fontSize={11} fill="var(--ink-3)">{v}</text>
            </g>
          ))}
          {/* Zero reference line */}
          {yZero != null && yZero >= PAD_T && yZero <= PAD_T + cH && (
            <line x1={PAD_L} x2={W - PAD_R} y1={yZero} y2={yZero}
              stroke="var(--hair-strong)" strokeWidth=".8" strokeDasharray="3,3" opacity=".6"/>
          )}

          {/* Series lines */}
          {vis.has('iv_rv') && <polyline points={ivRvPts} fill="none" stroke="var(--down-focus)" strokeWidth="1"
            strokeDasharray="4,2" strokeLinejoin="round" strokeLinecap="round" opacity=".7"/>}
          {vis.has('hv10') && <polyline points={hv10Pts} fill="none" stroke="#fb923c" strokeWidth="1"
            strokeLinejoin="round" strokeLinecap="round" opacity=".8"/>}
          {vis.has('hv30') && <polyline points={hv30Pts} fill="none" stroke="var(--up-focus)" strokeWidth="1.2"
            strokeLinejoin="round" strokeLinecap="round" opacity=".85"/>}
          {vis.has('iv')   && <polyline points={ivPts}   fill="none" stroke="#a78bfa" strokeWidth="1.6"
            strokeLinejoin="round" strokeLinecap="round"/>}

          {/* Latest dot for IV — hidden while any cursor is active */}
          {effectiveIdx === null && vis.has('iv') && (() => {
            const last = history[n - 1];
            const y = yOf(last?.iv);
            return y != null ? (
              <>
                <circle cx={xOf(n-1)} cy={y} r="2.5" fill="#a78bfa"/>
                <text x={xOf(n-1) - 4} y={y - 5} textAnchor="end"
                  fontSize={7.5} fontWeight={700} fill="#a78bfa">
                  {last.iv?.toFixed(1)}%
                </text>
              </>
            ) : null;
          })()}

          {cursorEls}

          {/* X-axis date labels — MMM-YY */}
          {xTicks.map(({ i, d }) => {
            const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const label = d ? (() => { const [y,m] = d.split('-'); return `${MONTHS[+m-1]}-${y.slice(2)}`; })() : '';
            return (
            <text key={i} x={xOf(i)} y={H - PAD_B + 11} textAnchor="middle"
              fontSize={11} fill="var(--ink-3)">
              {label}
            </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}


// ── IV Rank & Percentile Chart ─────────────────────────────────────────────────
// IV Rank     = (current IV − 52w low) / (52w high − 52w low) × 100  [amber line]
// IV Percentile = % of past 252 days where IV was BELOW today's level [purple line]
// Bands: <20 = low IV (good for debit), >80 = high IV (expensive).
// Both lines toggleable via legend.
function IVPercentileChart({ history, meta, onSyncHover, syncHoverDate }) {
  const svgRef       = useRef(null);
  const [W, containerRef] = useContainerWidth(820); const H = 171;
  const PAD_L = 44, PAD_R = 52, PAD_T = 16, PAD_B = 28;
  const cW = W - PAD_L - PAD_R;
  const cH = H - PAD_T - PAD_B;

  const [hoverIdx, setHoverIdx] = useState(null);
  const [vis, setVis] = useState(() => new Set(['rank','pctile']));
  const toggleLine = key => setVis(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // Filter to rows that have at least one of the two metrics
  const valid = useMemo(() =>
    history.filter(r => r.iv_pct != null || r.iv_pctile != null),
    [history]
  );
  const n = valid.length;

  const yOf = v => PAD_T + cH * (1 - v / 100);
  const xOf = i => PAD_L + (i / Math.max(1, n - 1)) * cW;

  const rankPts   = valid.map((r, i) => r.iv_pct    != null ? `${xOf(i).toFixed(1)},${yOf(r.iv_pct).toFixed(1)}`    : null).filter(Boolean).join(' ');
  const pctilePts = valid.map((r, i) => r.iv_pctile != null ? `${xOf(i).toFixed(1)},${yOf(r.iv_pctile).toFixed(1)}` : null).filter(Boolean).join(' ');

  const step   = Math.max(1, Math.floor(n / 8));
  const xTicks = valid.map((r, i) => (i % step === 0 || i === n - 1) ? { i, d: r.date } : null).filter(Boolean);

  const handleMouseMove = useCallback(e => {
    if (n < 2) return;
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) / rect.width * W;
    if (svgX < PAD_L || svgX > W - PAD_R) { setHoverIdx(null); onSyncHover?.(null); return; }
    const idx = Math.max(0, Math.min(n - 1, Math.round((svgX - PAD_L) / cW * (n - 1))));
    setHoverIdx(idx);
    onSyncHover?.(valid[idx]?.date ?? null);
  }, [n, valid, onSyncHover]);
  const handleMouseLeave = useCallback(() => { setHoverIdx(null); onSyncHover?.(null); }, [onSyncHover]);

  const syncIdx = syncHoverDate ? valid.findIndex(r => r.date === syncHoverDate) : -1;
  const effectiveIdx = hoverIdx !== null ? hoverIdx : syncIdx >= 0 ? syncIdx : null;

  if (n < 5) return null;

  const latest      = valid[n - 1];
  const latestRank  = meta?.latest_iv_pct    ?? latest?.iv_pct;
  const latestPctile= meta?.latest_iv_pctile ?? latest?.iv_pctile;
  // For the status badge, prefer percentile if visible, else rank
  const badgeVal    = vis.has('pctile') ? latestPctile : vis.has('rank') ? latestRank : null;

  const y20 = yOf(20);
  const y80 = yOf(80);

  let cursorEls = null;
  if (effectiveIdx !== null) {
    const r  = valid[effectiveIdx];
    const cx = xOf(effectiveIdx);
    const TW = 136, TH = 70;
    const tx = Math.round(cx + 8 + TW > W - PAD_R ? cx - 8 - TW : cx + 8);
    const ty = PAD_T + 2;
    cursorEls = (
      <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
        <line x1={cx} x2={cx} y1={PAD_T} y2={PAD_T+cH}
          stroke="var(--ink-3)" strokeWidth=".8" strokeDasharray="2,2" opacity=".5"/>
        {vis.has('rank')   && r.iv_pct    != null && <circle cx={cx} cy={yOf(r.iv_pct)}    r="2.5" fill="#f59e0b"/>}
        {vis.has('pctile') && r.iv_pctile != null && <circle cx={cx} cy={yOf(r.iv_pctile)} r="2.5" fill="#a78bfa"/>}
        <rect x={tx} y={ty} width={TW} height={TH}
          fill="var(--surface-2)" fillOpacity=".85" stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
          style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
        <text x={tx+TW/2} y={ty+16} textAnchor="middle"
          fontSize={11} fontWeight={700} fill="var(--ink)">{r.date}</text>
        {r.iv_pct    != null && <g>
          <text x={tx+7}    y={ty+32} fontSize={11} fill="var(--ink-2)">IV Rank</text>
          <text x={tx+TW-7} y={ty+32} textAnchor="end" fontSize={11} fontWeight={700} fill="#f59e0b">{r.iv_pct.toFixed(1)}</text>
        </g>}
        {r.iv_pctile != null && <g>
          <text x={tx+7}    y={ty+50} fontSize={11} fill="var(--ink-2)">IV %ile</text>
          <text x={tx+TW-7} y={ty+50} textAnchor="end" fontSize={11} fontWeight={700} fill="#a78bfa">{r.iv_pctile.toFixed(1)}</text>
        </g>}
      </g>
    );
  }

  return (
    <div style={{ marginTop:12, marginBottom:4 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4, paddingLeft:2, flexWrap:'wrap' }}>
        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, fontWeight:600, color:'var(--ink-2)' }}>IV Rank &amp; Percentile (52-week)</span>
          <InfoBtn align="right">
            IV Rank = linear position of current IV in the 52-week range. IV Percentile = % of past days in the window where IV was lower than today's. &gt;80 = expensive (sell premium); &lt;20 = cheap (buy premium).
          </InfoBtn>
        </span>
        {badgeVal != null && (
          <span style={{
            fontSize:11, fontWeight:700, padding:'1px 6px', borderRadius:3,
            background: badgeVal < 20 ? 'rgba(34,197,94,.2)' : badgeVal > 80 ? 'rgba(239,68,68,.2)' : 'rgba(167,139,250,.15)',
            color: badgeVal < 20 ? 'var(--up-field-ink)' : badgeVal > 80 ? 'var(--down-field-ink)' : '#a78bfa',
          }}>
            {badgeVal.toFixed(1)} — {badgeVal < 20 ? 'IV Cheap · Favour Debit' : badgeVal > 80 ? 'IV Expensive · Favour Credit' : 'IV Moderate'}
          </span>
        )}
        <div style={{ marginLeft:'auto', display:'flex', gap:10, fontSize:11, color:'var(--ink-3)', alignItems:'center' }}>
          {/* Toggleable legend */}
          {[['rank','IV Rank','#f59e0b',false],['pctile','IV %ile','#a78bfa',false]].map(([key,l,c]) => {
            const active = vis.has(key);
            return (
              <span key={key} onClick={() => toggleLine(key)}
                style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer',
                  opacity: active ? 1 : 0.35, userSelect:'none', padding:'2px 4px', borderRadius:3,
                  background: active ? 'transparent' : 'var(--surface-2)', transition:'opacity .15s' }}>
                <svg width="20" height="8">
                  <line x1="0" y1="4" x2="20" y2="4" stroke={c} strokeWidth="1.5"/>
                </svg>{l}
              </span>
            );
          })}
          <span style={{ color:'var(--up-field-ink)', opacity:.7, marginLeft:4 }}>▬ &lt;20 Cheap</span>
          <span style={{ color:'var(--down-field-ink)', opacity:.7 }}>▬ &gt;80 Expensive</span>
        </div>
      </div>
      <div ref={containerRef} style={{ overflowX:'auto' }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} height={H}
          style={{ width:'100%', minWidth:340, display:'block', cursor:'crosshair' }}
          onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>

          {/* Low-IV band (0–20) — green tint */}
          <rect x={PAD_L} y={yOf(20)} width={cW} height={yOf(0) - yOf(20)} fill="rgba(34,197,94,.06)"/>
          {/* High-IV band (80–100) — red tint */}
          <rect x={PAD_L} y={PAD_T} width={cW} height={yOf(80) - PAD_T} fill="rgba(239,68,68,.06)"/>

          {/* Reference lines at 20 and 80 */}
          <line x1={PAD_L} x2={W-PAD_R} y1={y20} y2={y20}
            stroke="var(--up-focus)" strokeWidth=".6" strokeDasharray="3,3" opacity=".5"/>
          <text x={PAD_L-3} y={y20+3} textAnchor="end" fontSize={11} fill="var(--up-focus)" opacity=".7">20</text>
          <line x1={PAD_L} x2={W-PAD_R} y1={y80} y2={y80}
            stroke="var(--down-focus)" strokeWidth=".6" strokeDasharray="3,3" opacity=".5"/>
          <text x={PAD_L-3} y={y80+3} textAnchor="end" fontSize={11} fill="var(--down-focus)" opacity=".7">80</text>

          {/* Y-axis labels */}
          <text x={PAD_L-3} y={PAD_T+4}    textAnchor="end" fontSize={11} fill="var(--ink-3)">100</text>
          <text x={PAD_L-3} y={PAD_T+cH+3} textAnchor="end" fontSize={11} fill="var(--ink-3)">0</text>
          <text x={PAD_L-3} y={yOf(50)+3}  textAnchor="end" fontSize={11} fill="var(--ink-3)" opacity=".5">50</text>

          {/* IV Rank — amber */}
          {vis.has('rank') && rankPts && (
            <polyline points={rankPts} fill="none" stroke="#f59e0b" strokeWidth="1.4"
              strokeLinejoin="round" strokeLinecap="round" opacity=".9"/>
          )}
          {/* IV Percentile — purple */}
          {vis.has('pctile') && pctilePts && (
            <polyline points={pctilePts} fill="none" stroke="#a78bfa" strokeWidth="1.4"
              strokeLinejoin="round" strokeLinecap="round"/>
          )}

          {/* Latest dots + labels (hidden while cursor is active) */}
          {effectiveIdx === null && (() => {
            const rankY   = latest?.iv_pct    != null ? yOf(latest.iv_pct)    : null;
            const pctileY = latest?.iv_pctile != null ? yOf(latest.iv_pctile) : null;
            return (
              <>
                {vis.has('rank')   && rankY   != null && (
                  <>
                    <circle cx={xOf(n-1)} cy={rankY} r="2.5" fill="#f59e0b"/>
                    <text x={xOf(n-1)+4} y={rankY+3} fontSize={7.5} fontWeight={700} fill="#f59e0b">
                      {latest.iv_pct.toFixed(1)}
                    </text>
                  </>
                )}
                {vis.has('pctile') && pctileY != null && (
                  <>
                    <circle cx={xOf(n-1)} cy={pctileY} r="2.5" fill="#a78bfa"/>
                    <text x={xOf(n-1)-4} y={pctileY-5} textAnchor="end" fontSize={7.5} fontWeight={700} fill="#a78bfa">
                      {latest.iv_pctile.toFixed(1)}
                    </text>
                  </>
                )}
              </>
            );
          })()}

          {cursorEls}

          {/* X-axis labels — MMM-YY */}
          {xTicks.map(({ i, d }) => {
            const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const label = d ? (() => { const [y,m] = d.split('-'); return `${MONTHS[+m-1]}-${y.slice(2)}`; })() : '';
            return (
              <text key={i} x={xOf(i)} y={H-PAD_B+11} textAnchor="middle"
                fontSize={11} fill="var(--ink-3)">{label}</text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}


// ── Shared x-axis tick builder for intraday charts ────────────────────────────
// Returns [{i, label}] — always includes first and last data points.
// Middle labels are rounded to 15/30/60-min grid; enforces min pixel gap.
// Shared time-to-minutes parser used by both intraday charts and tick builder
const _toMins = t => {
  if (!t || t === 'Now') return NaN;
  const [h, m] = t.split(':').map(Number);
  return isNaN(h) ? NaN : h * 60 + m;
};

function buildIntraDayXTicks(data, totalW, padL, padR, timeKey = 'time', overrideFirstMin = null) {
  const n = data.length;
  if (n < 2) return n === 1 ? [{ mins: NaN, label: data[0][timeKey] }] : [];
  const cW      = totalW - padL - padR;
  const dataFirstMin = _toMins(data[0][timeKey]);
  const lastMin  = _toMins(data[n - 1][timeKey]);
  if (isNaN(dataFirstMin) || isNaN(lastMin)) return [{ mins: NaN, label: data[0][timeKey] }, { mins: NaN, label: data[n-1][timeKey] }];

  const firstMin = overrideFirstMin != null ? Math.min(overrideFirstMin, dataFirstMin) : dataFirstMin;

  // Time-proportional pixel position (uses the effective firstMin for spacing decisions)
  const xOfMins = mins => padL + ((mins - firstMin) / Math.max(1, lastMin - firstMin)) * cW;

  const rangeMin = lastMin - firstMin;
  const step = rangeMin > 180 ? 60 : rangeMin > 60 ? 30 : 15;
  const MIN_PX = 44;

  const firstLabel = `${String(Math.floor(firstMin / 60)).padStart(2, '0')}:${String(firstMin % 60).padStart(2, '0')}`;
  const ticks = [{ mins: firstMin, label: firstLabel }];

  const startRound = Math.ceil((firstMin + 1) / step) * step;
  for (let t = startRound; t < lastMin - step / 2; t += step) {
    const label = `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
    const prevMins = ticks[ticks.length - 1].mins;
    if (xOfMins(t) - xOfMins(prevMins) >= MIN_PX) ticks.push({ mins: t, label });
  }

  // Always add last — replace previous if too close
  const prevMins = ticks[ticks.length - 1].mins;
  if (xOfMins(lastMin) - xOfMins(prevMins) < MIN_PX / 2) {
    ticks[ticks.length - 1] = { mins: lastMin, label: data[n - 1][timeKey] };
  } else {
    ticks.push({ mins: lastMin, label: data[n - 1][timeKey] });
  }
  return ticks;
}


// ── OI Change Chart ────────────────────────────────────────────────────────────
// Shows cumulative CE and PE OI change since the first intraday snapshot.
// Both lines start at 0; positive = OI added, negative = OI unwound.
// Also overlays PCR on secondary right axis.
// ── EMA window labelling ──────────────────────────────────────────────────────
// The EMAs are computed over ARRAY INDEX (calcEMA walks data points), but the
// x-axis is wall-clock minutes.  Those agree only when the series is one point
// per minute.  NIFTY and BANKNIFTY publish OI every minute (1.00 min/pt); BSE
// publishes SENSEX on a ~3-minute cycle, so SENSEX arrives at ~2.64 min/pt and an
// "EMA 9" there actually spans ~24 minutes.  That is a publication clock, not
// illiquidity, and the point-based EMA is the right estimator for it — only the
// LABEL was wrong.  These helpers derive the effective window for display.
// Nothing here touches any series value.
const _hhmmToMin = t => {
  if (typeof t !== 'string') return null;
  const [h, m] = t.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

// MEAN gap, not median.  The label turns a COUNT into a TOTAL — "9 points back
// is how many minutes back?" — and that elapsed time is the SUM of the last 9
// gaps, whose expected value is n x MEAN.  The median answers a different
// question ("what does a typical gap look like") and is the wrong estimator:
// SENSEX gaps are bimodal (56 x 2min, 90 x 3min), so the median lands on 3 while
// true average spacing is 385 / 146 = 2.64, overstating every label by 14%.  The
// robustness a median buys is worth less than that — one 40-minute outage among
// 146 gaps moves the mean only 2.64 -> 2.89.
//
// Computed from the endpoints, so it is exact by construction: no gap array, no
// sort, and missing interior points cannot bias it.
function meanGapMinutes(history) {
  if (!history || history.length < 3) return null;
  const first = _hhmmToMin(history[0]?.time);
  const last  = _hhmmToMin(history[history.length - 1]?.time);
  if (first == null || last == null || last <= first) return null;
  return (last - first) / (history.length - 1);
}

// 'EMA 9' -> 'EMA 9 · 24m'.  Returns the bare label when the rate is unknown.
const emaLabel = (base, period, minPerPt) =>
  minPerPt == null ? base : `${base} · ${Math.round(period * minPerPt)}m`;

function OIChangeChart({ history, vis = new Set(['pe_chg','ce_chg','pcr']), minPerPt = null }) {
  // Match OIPCRChart dimensions exactly so both charts align
  const svgRef       = useRef(null);
  const [W, containerRef] = useContainerWidth(820); const H = 460;
  const PAD_L = 58, PAD_R = 44, PAD_T = 18, PAD_B = 28;
  const cW = W - PAD_L - PAD_R;
  const cH = H - PAD_T - PAD_B;

  const [hoverIdx, setHoverIdx] = useState(null);

  const n = history.length;

  // Compute cumulative changes from first snapshot
  const data = useMemo(() => {
    if (!history.length) return [];
    const baseCe = history[0].total_ce ?? 0;
    const basePe = history[0].total_pe ?? 0;
    const pts = history.map(p => {
      const ce_chg = (p.total_ce ?? 0) - baseCe;
      const pe_chg = (p.total_pe ?? 0) - basePe;
      return { time: p.time, ce_chg, pe_chg, net_chg: ce_chg - pe_chg, pcr: p.pcr ?? 0 };
    });
    const alpha = 2 / (9 + 1);
    let ema = pts[0].pcr;
    return pts.map(p => { ema = alpha * p.pcr + (1 - alpha) * ema; return { ...p, pcr_ema9: ema }; });
  }, [history]);

  // Time-proportional x scale — always anchored to 9:15 so the full trading day is shown
  const MARKET_OPEN = 9 * 60 + 15;
  const times = useMemo(() => data.map(d => _toMins(d.time)), [data]);
  const tMin  = useMemo(() => { const v = times.filter(isFinite); return v.length ? Math.min(MARKET_OPEN, Math.min(...v)) : 0; }, [times]);
  const tMax  = useMemo(() => { const v = times.filter(isFinite); return v.length ? Math.max(...v) : 1; }, [times]);

  const handleMouseMove = useCallback(e => {
    if (n < 2) return;
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) / rect.width * W;
    if (svgX < PAD_L || svgX > W - PAD_R) { setHoverIdx(null); return; }
    const rawMins = tMin + ((svgX - PAD_L) / cW) * (tMax - tMin);
    let best = 0, bestD = Infinity;
    times.forEach((m, i) => { const d = Math.abs(m - rawMins); if (d < bestD) { bestD = d; best = i; } });
    setHoverIdx(best);
  }, [n, cW, tMin, tMax, times]);
  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);

  if (n < 2) return null;

  // Time-proportional x mapper: minutes → pixel x
  const xOf = mins => PAD_L + ((mins - tMin) / Math.max(1, tMax - tMin)) * cW;

  // Left Y scale: symmetric around 0 for OI change
  const allChg = data.flatMap(d => [d.ce_chg, d.pe_chg]).filter(v => isFinite(v));
  const absMax = Math.max(1, ...allChg.map(Math.abs));
  const yMax =  absMax * 1.1;
  const yMin = -absMax * 1.1;
  const yOf  = v => PAD_T + cH * (1 - (v - yMin) / Math.max(1, yMax - yMin));
  const yZero = yOf(0);

  // Right Y scale: PCR (tight range like OIPCRChart)
  const pcrVals = data.map(d => d.pcr).filter(v => v > 0);
  const _pcrLo  = pcrVals.length ? Math.min(...pcrVals) : 0;
  const _pcrHi  = pcrVals.length ? Math.max(...pcrVals) : 2;
  const _pcrPad = Math.max(0.06, (_pcrHi - _pcrLo) * 0.25);
  const pcrMin  = Math.max(0, _pcrLo - _pcrPad);
  const pcrMax  = _pcrHi + _pcrPad;
  const yPCR    = v => PAD_T + cH * (1 - (Math.min(v, pcrMax) - pcrMin) / Math.max(0.01, pcrMax - pcrMin));

  // X-axis ticks (time-proportional, anchored to 9:15)
  const xTicks = buildIntraDayXTicks(data, W, PAD_L, PAD_R, 'time', MARKET_OPEN);

  // Polyline points — x mapped by actual time
  const cePts  = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yOf(d.ce_chg).toFixed(1)}`).join(' ');
  const pePts  = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yOf(d.pe_chg).toFixed(1)}`).join(' ');
  const pcrPts  = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yPCR(d.pcr).toFixed(1)}`).join(' ');
  const ema9Pts = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yPCR(d.pcr_ema9).toFixed(1)}`).join(' ');

  // Filled areas above/below zero
  const buildArea = pts => {
    const base = yZero.toFixed(1);
    return `M ${xOf(tMin).toFixed(1)},${base} ` + pts + ` L ${xOf(tMax).toFixed(1)},${base} Z`;
  };

  // Left Y-axis ticks
  const niceTicks = [0];
  const rawStep = absMax / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(rawStep, 1))));
  const step_ = [1, 2, 2.5, 5, 10].map(f => f * mag).find(s => s >= rawStep) ?? (mag * 10);
  for (let v = step_; v <= yMax * 0.95; v += step_) { niceTicks.push(v); niceTicks.push(-v); }
  const yTicks = niceTicks
    .map(v => ({ v, y: yOf(v) }))
    .filter(t => t.y >= PAD_T && t.y <= PAD_T + cH);

  // PCR = 1 reference
  const pcrTick1 = yPCR(1).toFixed(1);

  let cursorEls = null;
  if (hoverIdx !== null) {
    const d = data[hoverIdx];
    const cx = xOf(times[hoverIdx]);
    const TW = 138, TH = 92;
    const tx = Math.round(cx + 8 + TW > W - PAD_R ? cx - 8 - TW : cx + 8);
    const ty = PAD_T + 3;
    cursorEls = (
      <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
        <line x1={cx} x2={cx} y1={PAD_T} y2={PAD_T+cH}
          stroke="var(--ink-3)" strokeWidth=".8" strokeDasharray="2,2" opacity=".5"/>
        <circle cx={cx} cy={yOf(d.ce_chg)} r="2.5" fill="var(--cebar)"/>
        <circle cx={cx} cy={yOf(d.pe_chg)} r="2.5" fill="var(--pebar)"/>
        {vis.has('pcr')      && <circle cx={cx} cy={yPCR(d.pcr)}      r="2.5" fill="var(--ink)"/>}
        {vis.has('pcr_ema9') && <circle cx={cx} cy={yPCR(d.pcr_ema9)} r="2.5" fill="var(--amber-ink)"/>}
        <rect x={tx} y={ty} width={TW} height={TH}
          fill="var(--surface-2)" fillOpacity=".82" stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
          style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
        <text x={tx+TW/2} y={ty+16} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--ink)">{d.time}</text>
        {[
          { l:'CE Chg',  v: fmtOISign(d.ce_chg),    c: d.ce_chg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' },
          { l:'PE Chg',  v: fmtOISign(d.pe_chg),    c: d.pe_chg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' },
          { l:'PCR',     v: d.pcr.toFixed(3),        c: 'var(--ink)' },
          { l: emaLabel('PCR EMA9', 9, minPerPt),v: d.pcr_ema9.toFixed(3),  c: 'var(--amber-ink)', hide: !vis.has('pcr_ema9') },
        ].filter(r => !r.hide).map((item, k) => (
          <g key={k}>
            <text x={tx+7}     y={ty+32+k*18} fontSize={11} fill="var(--ink-2)">{item.l}</text>
            <text x={tx+TW-7}  y={ty+32+k*18} textAnchor="end" fontSize={11} fontWeight={600} fill={item.c}>{item.v}</text>
          </g>
        ))}
      </g>
    );
  }

  return (
    <div ref={containerRef}>
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} height={H}
      style={{ width:'100%', minWidth:340, display:'block', cursor:'crosshair' }}
      onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <defs>
        <linearGradient id="grad-ce-chg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--cebar)" stopOpacity=".35"/>
          <stop offset="100%" stopColor="var(--cebar)" stopOpacity=".04"/>
        </linearGradient>
        <linearGradient id="grad-pe-chg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--pebar)" stopOpacity=".35"/>
          <stop offset="100%" stopColor="var(--pebar)" stopOpacity=".04"/>
        </linearGradient>
      </defs>

      {/* Left Y-axis ticks + grid */}
      {yTicks.map(({ v, y }) => (
        <g key={v}>
          <line x1={PAD_L} x2={W-PAD_R} y1={y} y2={y}
            stroke={v === 0 ? 'var(--hair-strong)' : 'var(--hair)'}
            strokeWidth={v === 0 ? '.8' : '.35'}
            strokeDasharray={v === 0 ? '3,3' : 'none'} opacity=".6"/>
          <text x={PAD_L-4} y={y+3} textAnchor="end" fontSize={11} fill="var(--ink-3)">
            {v >= 0 ? '+' : '-'}{fmtOI(Math.abs(Math.round(v)))}
          </text>
        </g>
      ))}

      {/* PCR = 1 reference (only when PCR visible) */}
      {vis.has('pcr') && pcrMin < 1.0 && pcrMax > 1.0 && (
        <>
          <line x1={PAD_L} x2={W-PAD_R} y1={pcrTick1} y2={pcrTick1}
            stroke="var(--hair-strong)" strokeWidth=".5" strokeDasharray="3,3" opacity=".5"/>
          <text x={W-PAD_R+3} y={Number(pcrTick1)+3.5} fontSize={11} fill="var(--ink-3)" opacity=".7">1.0</text>
        </>
      )}

      {/* Right axis PCR range labels */}
      {vis.has('pcr') && <text x={W-PAD_R+3} y={PAD_T+4}    fontSize={11} fill="var(--ink-3)" opacity=".7">{pcrMax.toFixed(2)}</text>}
      {vis.has('pcr') && <text x={W-PAD_R+3} y={PAD_T+cH+3} fontSize={11} fill="var(--ink-3)" opacity=".7">{pcrMin.toFixed(2)}</text>}

      {/* CE filled area + line */}
      {vis.has('ce_chg') && <path d={buildArea(cePts)} fill="url(#grad-ce-chg)" opacity=".5"/>}
      {vis.has('ce_chg') && <polyline points={cePts} fill="none" stroke="var(--cebar)" strokeWidth="1.2"
        strokeLinejoin="round" strokeLinecap="round" opacity=".85"/>}

      {/* PE filled area + line */}
      {vis.has('pe_chg') && <path d={buildArea(pePts)} fill="url(#grad-pe-chg)" opacity=".5"/>}
      {vis.has('pe_chg') && <polyline points={pePts} fill="none" stroke="var(--pebar)" strokeWidth="1.2"
        strokeLinejoin="round" strokeLinecap="round" opacity=".85"/>}

      {/* PCR line overlay */}
      {vis.has('pcr') && <polyline points={pcrPts} fill="none" stroke="var(--ink)" strokeWidth="1.4"
        strokeLinejoin="round" strokeLinecap="round" opacity=".8"/>}
      {/* PCR EMA9 overlay */}
      {vis.has('pcr_ema9') && <polyline points={ema9Pts} fill="none" stroke="var(--amber-ink)" strokeWidth="1.4"
        strokeLinejoin="round" strokeLinecap="round" opacity=".9"/>}

      {/* Latest value dots + labels — only for visible series */}
      {hoverIdx === null && (() => {
        const last = data[n - 1];
        const lastX = xOf(times[n - 1]);
        return (
          <>
            {vis.has('ce_chg') && <>
              <circle cx={lastX} cy={yOf(last.ce_chg)} r="2.5" fill="var(--cebar)"/>
              <text x={lastX-4} y={yOf(last.ce_chg)-4} textAnchor="end"
                fontSize={7.5} fontWeight={700} fill="var(--cebar)">
                {fmtOISign(last.ce_chg)}
              </text>
            </>}
            {vis.has('pe_chg') && <>
              <circle cx={lastX} cy={yOf(last.pe_chg)} r="2.5" fill="var(--pebar)"/>
              <text x={lastX-4} y={yOf(last.pe_chg)+10} textAnchor="end"
                fontSize={7.5} fontWeight={700} fill="var(--pebar)">
                {fmtOISign(last.pe_chg)}
              </text>
            </>}
            {vis.has('pcr') && <>
              <circle cx={lastX} cy={yPCR(last.pcr)} r="2.5" fill="var(--ink)"/>
              <text x={W-PAD_R+3} y={yPCR(last.pcr)+3}
                fontSize={8} fontWeight={700} fill="var(--ink)">{last.pcr.toFixed(3)}</text>
            </>}
            {vis.has('pcr_ema9') && <>
              <circle cx={lastX} cy={yPCR(last.pcr_ema9)} r="2.5" fill="var(--amber-ink)"/>
            </>}
          </>
        );
      })()}

      {cursorEls}

      {/* X-axis time labels */}
      {xTicks.map(({ mins, label }) => (
        <text key={mins} x={xOf(mins)} y={H-PAD_B+11} textAnchor="middle" fontSize={11} fill="var(--ink-3)">{label}</text>
      ))}
    </svg>
    </div>
  );
}


// ── CE−PE Net OI Change Chart ─────────────────────────────────────────────────
// Shows (cumulative CE OI change) − (cumulative PE OI change) since open.
// Green fill above zero = calls building faster; red fill below zero = puts building faster.
// Also overlays 10 EMA, 20 EMA, and a per-tick delta histogram, all toggleable.
function OINetChart({ history, niftyIntraday = [], vis = new Set(['net_chg','hist','ema10','ema20']),
                     minPerPt = null, symbol = 'NIFTY' }) {
  const svgRef = useRef(null);
  const [W, containerRef] = useContainerWidth(820); const H = 460;
  const PAD_L = 58, PAD_R = 104, PAD_T = 18, PAD_B = 28;
  const cW = W - PAD_L - PAD_R;
  const cH = H - PAD_T - PAD_B;

  const [hoverIdx, setHoverIdx] = useState(null);

  const data = useMemo(() => {
    if (!history.length) return [];
    const baseCe = history[0].total_ce ?? 0;
    const basePe = history[0].total_pe ?? 0;
    // EMA helper: returns array of same length
    const calcEMA = (vals, period) => {
      const alpha = 2 / (period + 1);
      const out = [];
      vals.forEach((v, i) => {
        if (i === 0) { out.push(v); return; }
        out.push(alpha * v + (1 - alpha) * out[i - 1]);
      });
      return out;
    };
    const points = history.map(p => {
      const ce_chg = (p.total_ce ?? 0) - baseCe;
      const pe_chg = (p.total_pe ?? 0) - basePe;
      return { time: p.time, ce_chg, pe_chg, net_chg: pe_chg - ce_chg, pcr: p.pcr ?? null };
    });
    const nets   = points.map(p => p.net_chg);
    const ema3v  = calcEMA(nets, 3);
    const ema9v  = calcEMA(nets, 9);
    const ema21v = calcEMA(nets, 21);
    return points.map((p, i) => ({
      ...p,
      ema3:  ema3v[i],
      ema9:  ema9v[i],
      ema21: ema21v[i],
      delta: i === 0 ? 0 : nets[i] - nets[i - 1],
      hist_net_ema9:    nets[i]   - ema9v[i],
      hist_ema3_ema9:   ema3v[i]  - ema9v[i],
      hist_ema3_ema21:  ema3v[i]  - ema21v[i],
      hist_ema9_ema21:  ema9v[i]  - ema21v[i],
    }));
  }, [history]);

  // NIFTY spot: build a minute→close lookup from the intraday data, then align to OI time axis
  const niftyByTime = useMemo(() => {
    const m = {};
    niftyIntraday.forEach(p => { m[p.time] = p.close; });
    return m;
  }, [niftyIntraday]);

  // For each OI data point, find closest NIFTY minute (exact match preferred, then ±1 min)
  const niftyAligned = useMemo(() => data.map(d => {
    if (niftyByTime[d.time] != null) return niftyByTime[d.time];
    // Try ±1 minute
    const [h, m] = d.time.split(':').map(Number);
    const mins = h * 60 + m;
    for (const delta of [-1, 1, -2, 2]) {
      const t = mins + delta;
      const key = `${String(Math.floor(t/60)).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`;
      if (niftyByTime[key] != null) return niftyByTime[key];
    }
    return null;
  }), [data, niftyByTime]);

  // PCR overlay scale (same right column as NIFTY; key 'pcr_overlay' to avoid clashing with OIPCRChart)
  const pcrOvVals = useMemo(() => data.map(d => d.pcr).filter(v => v != null && v > 0), [data]);
  const pcrOvMin  = pcrOvVals.length ? Math.min(...pcrOvVals) : 0.5;
  const pcrOvMax  = pcrOvVals.length ? Math.max(...pcrOvVals) : 1.5;
  const pcrOvRange = Math.max(0.01, pcrOvMax - pcrOvMin);
  const pcrOvPad   = pcrOvRange * 0.1;
  const yPCROv = v => PAD_T + cH * (1 - (v - (pcrOvMin - pcrOvPad)) / (pcrOvRange + 2 * pcrOvPad));
  const pcrOvTickStep = (() => {
    const raw = pcrOvRange / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 0.001))));
    return [1, 2, 2.5, 5, 10].map(f => f * mag).find(s => s >= raw) ?? (mag * 10);
  })();
  const pcrOvTicksFiltered = (() => {
    const base = Math.floor(pcrOvMin / pcrOvTickStep) * pcrOvTickStep;
    const ticks = [];
    for (let v = base; v <= pcrOvMax + pcrOvTickStep; v += pcrOvTickStep) ticks.push(parseFloat(v.toFixed(4)));
    return ticks.map(v => ({ v, y: yPCROv(v) })).filter(t => t.y >= PAD_T && t.y <= PAD_T + cH);
  })();

  // NIFTY spot scale (third axis — far right)
  // Use full intraday data for NIFTY scale so the range covers 9:15 onwards
  const niftyVals = useMemo(() => niftyIntraday.map(p => p.close).filter(v => v != null && v > 0), [niftyIntraday]);
  const niftyMin  = niftyVals.length ? Math.min(...niftyVals) : 0;
  const niftyMax  = niftyVals.length ? Math.max(...niftyVals) : 1;

  // Always anchor to 9:15 so NIFTY (and the full trading day) shows from market open
  const MARKET_OPEN = 9 * 60 + 15;
  const niftyTimes = useMemo(() => niftyIntraday.map(p => _toMins(p.time)).filter(isFinite), [niftyIntraday]);
  const times = useMemo(() => data.map(d => _toMins(d.time)), [data]);
  const tMin  = useMemo(() => {
    const v = times.filter(isFinite);
    const base = v.length ? Math.min(...v) : MARKET_OPEN;
    const niftyStart = vis.has('nifty') && niftyTimes.length ? Math.min(...niftyTimes) : base;
    return Math.min(MARKET_OPEN, base, niftyStart);
  }, [times, vis, niftyTimes]);
  const tMax  = useMemo(() => { const v = times.filter(isFinite); return v.length ? Math.max(...v) : 1; }, [times]);

  const handleMouseMove = useCallback(e => {
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) / rect.width * W;
    if (svgX < PAD_L || svgX > W - PAD_R) { setHoverIdx(null); return; }
    const rawMins = tMin + ((svgX - PAD_L) / cW) * (tMax - tMin);
    let best = 0, bestD = Infinity;
    times.forEach((m, i) => { const d = Math.abs(m - rawMins); if (d < bestD) { bestD = d; best = i; } });
    setHoverIdx(best);
  }, [cW, tMin, tMax, times, W]);
  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);

  const n = data.length;
  if (n < 2) return null;

  const xOf = mins => PAD_L + ((mins - tMin) / Math.max(1, tMax - tMin)) * cW;

  // Y scale includes net_chg AND ema values so EMAs never clip
  const allMainVals = data.flatMap(d => [d.net_chg, d.ema3, d.ema9, d.ema21]).filter(isFinite);
  const absMax  = Math.max(1, ...allMainVals.map(Math.abs));
  const yMax =  absMax * 1.15;
  const yMin = -absMax * 1.15;
  const yOf  = v => PAD_T + cH * (1 - (v - yMin) / Math.max(1, yMax - yMin));
  const yZero = yOf(0);

  // Bar width: even spacing across chart width
  const barW = Math.max(1, (cW / n) * 0.7);

  // NIFTY y-scale: maps spot value → pixel y, padded ±5% of range
  const niftyRange = Math.max(1, niftyMax - niftyMin);
  const niftyPad   = niftyRange * 0.08;
  const yNifty = v => PAD_T + cH * (1 - (v - (niftyMin - niftyPad)) / (niftyRange + 2 * niftyPad));

  // NIFTY nice ticks (second right-axis column, starting at W-PAD_R+56)
  const niftyTickStep = (() => {
    const raw = niftyRange / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
    return [1, 2, 2.5, 5, 10].map(f => f * mag).find(s => s >= raw) ?? (mag * 10);
  })();
  const niftyTicksFiltered = (() => {
    const base = Math.floor(niftyMin / niftyTickStep) * niftyTickStep;
    const ticks = [];
    for (let v = base; v <= niftyMax + niftyTickStep; v += niftyTickStep) ticks.push(v);
    return ticks.map(v => ({ v, y: yNifty(v) })).filter(t => t.y >= PAD_T && t.y <= PAD_T + cH);
  })();

  // Right-axis histogram scale — dynamic, symmetric around zero, fills full chart height
  // Only counts the histogram series that are currently visible (avoids one hidden outlier
  // compressing the visible bars to nothing).
  const visHistKeys = ['hist_net_ema9','hist_ema3_ema9','hist_ema3_ema21','hist_ema9_ema21'];
  const activeHistVals = data.flatMap(d =>
    [d.hist_net_ema9, d.hist_ema3_ema9, d.hist_ema3_ema21, d.hist_ema9_ema21]
  ).filter(isFinite);
  const histAbsMax = Math.max(1, ...activeHistVals.map(Math.abs));
  const histMax =  histAbsMax * 1.15;
  const histMin = -histAbsMax * 1.15;
  // yHist maps a histogram value to the same pixel space as the main chart (centred on yZero)
  const yHist = v => yZero - ((v / histAbsMax) * (cH / 2)) * 0.9;

  // Right-axis nice ticks for histograms
  const histRawStep = histAbsMax / 3;
  const histMag  = Math.pow(10, Math.floor(Math.log10(Math.max(histRawStep, 1))));
  const histStep = [1, 2, 2.5, 5, 10].map(f => f * histMag).find(s => s >= histRawStep) ?? (histMag * 10);
  const histTicks = [0];
  for (let v = histStep; v <= histMax * 0.95; v += histStep) { histTicks.push(v); histTicks.push(-v); }
  const histTicksFiltered = histTicks
    .map(v => ({ v, y: yHist(v) }))
    .filter(t => t.y >= PAD_T && t.y <= PAD_T + cH);

  const xTicks = buildIntraDayXTicks(data, W, PAD_L, PAD_R, 'time', MARKET_OPEN);

  const netPts   = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yOf(d.net_chg).toFixed(1)}`).join(' ');
  const ema3Pts  = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yOf(d.ema3).toFixed(1)}`).join(' ');
  const ema9Pts  = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yOf(d.ema9).toFixed(1)}`).join(' ');
  const ema21Pts = data.map((d, i) => `${xOf(times[i]).toFixed(1)},${yOf(d.ema21).toFixed(1)}`).join(' ');

  // Y ticks
  const niceTicks = [0];
  const rawStep = absMax / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(rawStep, 1))));
  const step_ = [1, 2, 2.5, 5, 10].map(f => f * mag).find(s => s >= rawStep) ?? (mag * 10);
  for (let v = step_; v <= yMax * 0.95; v += step_) { niceTicks.push(v); niceTicks.push(-v); }
  const yTicks = niceTicks.map(v => ({ v, y: yOf(v) })).filter(t => t.y >= PAD_T && t.y <= PAD_T + cH);

  // Build SVG path for area above/below zero with dual fill
  const posPath = (() => {
    const pts = data.map((d, i) => ({ x: xOf(times[i]), y: yOf(d.net_chg) }));
    if (!pts.length) return '';
    let p = `M ${pts[0].x.toFixed(1)},${yZero.toFixed(1)}`;
    pts.forEach(pt => { p += ` L ${pt.x.toFixed(1)},${Math.min(pt.y, yZero).toFixed(1)}`; });
    p += ` L ${pts[pts.length-1].x.toFixed(1)},${yZero.toFixed(1)} Z`;
    return p;
  })();
  const negPath = (() => {
    const pts = data.map((d, i) => ({ x: xOf(times[i]), y: yOf(d.net_chg) }));
    if (!pts.length) return '';
    let p = `M ${pts[0].x.toFixed(1)},${yZero.toFixed(1)}`;
    pts.forEach(pt => { p += ` L ${pt.x.toFixed(1)},${Math.max(pt.y, yZero).toFixed(1)}`; });
    p += ` L ${pts[pts.length-1].x.toFixed(1)},${yZero.toFixed(1)} Z`;
    return p;
  })();

  let cursorEls = null;
  if (hoverIdx !== null) {
    const d  = data[hoverIdx];
    const cx = xOf(times[hoverIdx]);
    const rows = [
      { l:'CE Chg',       v: fmtOISign(d.ce_chg),          c: d.ce_chg          >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' },
      { l:'PE Chg',       v: fmtOISign(d.pe_chg),          c: d.pe_chg          >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' },
      { l:'PE−CE',        v: fmtOISign(d.net_chg),         c: d.net_chg         >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' },
      { l: emaLabel('EMA 3', 3, minPerPt),      v: fmtOISign(d.ema3),            c: 'var(--cebar)' },
      { l: emaLabel('EMA 9', 9, minPerPt),      v: fmtOISign(d.ema9),            c: 'var(--amber-ink)' },
      { l: emaLabel('EMA 21', 21, minPerPt),    v: fmtOISign(d.ema21),           c: 'var(--pebar)' },
      { l: emaLabel('Net−EMA9', 9, minPerPt),   v: fmtOISign(d.hist_net_ema9),   c: d.hist_net_ema9    >= 0 ? 'var(--pebar)'    : 'var(--amber-ink)' },
      { l: emaLabel('EMA3−EMA9', 9, minPerPt),  v: fmtOISign(d.hist_ema3_ema9),  c: d.hist_ema3_ema9   >= 0 ? 'var(--pebar)'    : 'var(--cebar)' },
      { l: emaLabel('EMA3−EMA21', 21, minPerPt),v: fmtOISign(d.hist_ema3_ema21), c: d.hist_ema3_ema21  >= 0 ? 'var(--pebar)'    : 'var(--cebar)' },
      { l: emaLabel('EMA9−EMA21', 21, minPerPt),v: fmtOISign(d.hist_ema9_ema21), c: d.hist_ema9_ema21  >= 0 ? 'var(--pebar)'    : 'var(--cebar)' },
      ...(vis.has('pcr_overlay') && data[hoverIdx]?.pcr != null ? [{ l:'PCR', v: data[hoverIdx].pcr.toFixed(3), c:'var(--ink)' }] : []),
      ...(vis.has('nifty') && niftyAligned[hoverIdx] != null ? [{ l: symbol, v: niftyAligned[hoverIdx].toLocaleString('en-IN', {maximumFractionDigits:2}), c:'#a78bfa' }] : []),
    ];
    const TW = 148, TH = 20 + rows.length * 18 + 4;
    const tx = Math.round(cx + 8 + TW > W - PAD_R ? cx - 8 - TW : cx + 8);
    const ty = PAD_T + 3;
    cursorEls = (
      <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
        <line x1={cx} x2={cx} y1={PAD_T} y2={PAD_T+cH}
          stroke="var(--ink-3)" strokeWidth=".8" strokeDasharray="2,2" opacity=".5"/>
        {vis.has('net_chg') && <circle cx={cx} cy={yOf(d.net_chg)} r="2.5" fill={d.net_chg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}/>}
        {vis.has('ema3')    && <circle cx={cx} cy={yOf(d.ema3)}    r="2"   fill="var(--cebar)"/>}
        {vis.has('ema9')    && <circle cx={cx} cy={yOf(d.ema9)}    r="2"   fill="var(--amber-ink)"/>}
        {vis.has('ema21')   && <circle cx={cx} cy={yOf(d.ema21)}   r="2"   fill="var(--pebar)"/>}
        {vis.has('nifty') && niftyAligned[hoverIdx] != null && <circle cx={cx} cy={yNifty(niftyAligned[hoverIdx])} r="2.5" fill="#a78bfa"/>}
        {vis.has('pcr_overlay') && data[hoverIdx]?.pcr != null && <circle cx={cx} cy={yPCROv(data[hoverIdx].pcr)} r="2" fill="var(--ink)"/>}
        <rect x={tx} y={ty} width={TW} height={TH}
          fill="var(--surface-2)" fillOpacity=".82" stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
          style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
        <text x={tx+TW/2} y={ty+14} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--ink)">{d.time}</text>
        {rows.map((item, k) => (
          <g key={k}>
            <text x={tx+7}    y={ty+26+k*18} fontSize={11} fill="var(--ink-2)">{item.l}</text>
            <text x={tx+TW-7} y={ty+26+k*18} textAnchor="end" fontSize={11} fontWeight={600} fill={item.c}>{item.v}</text>
          </g>
        ))}
      </g>
    );
  }

  return (
    <div ref={containerRef}>
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} height={H}
      style={{ width:'100%', minWidth:340, display:'block', cursor:'crosshair' }}
      onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <defs>
        <linearGradient id="grad-net-pos" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--up-field-ink)" stopOpacity=".35"/>
          <stop offset="100%" stopColor="var(--up-field-ink)" stopOpacity=".04"/>
        </linearGradient>
        <linearGradient id="grad-net-neg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--down-field-ink)" stopOpacity=".04"/>
          <stop offset="100%" stopColor="var(--down-field-ink)" stopOpacity=".35"/>
        </linearGradient>
        <clipPath id="clip-net-pos"><rect x={PAD_L} y={PAD_T} width={cW} height={Math.max(0, yZero - PAD_T)}/></clipPath>
        <clipPath id="clip-net-neg"><rect x={PAD_L} y={yZero} width={cW} height={Math.max(0, PAD_T + cH - yZero)}/></clipPath>
      </defs>

      {/* Left Y grid + ticks (CE-PE / EMA scale) */}
      {yTicks.map(({ v, y }) => (
        <g key={v}>
          <line x1={PAD_L} x2={W-PAD_R} y1={y} y2={y}
            stroke={v === 0 ? 'var(--hair-strong)' : 'var(--hair)'}
            strokeWidth={v === 0 ? '.8' : '.35'}
            strokeDasharray={v === 0 ? '3,3' : 'none'} opacity=".6"/>
          <text x={PAD_L-4} y={y+3} textAnchor="end" fontSize={11} fill="var(--ink-3)">
            {v >= 0 ? '+' : '-'}{fmtOI(Math.abs(Math.round(v)))}
          </text>
        </g>
      ))}

      {/* Right Y ticks (histogram scale) */}
      {histTicksFiltered.map(({ v, y }) => (
        <g key={`h${v}`}>
          <text x={W-PAD_R+4} y={y+3} textAnchor="start" fontSize={10} fill="var(--ink-3)" opacity=".7">
            {v >= 0 ? '+' : '-'}{fmtOI(Math.abs(Math.round(v)))}
          </text>
        </g>
      ))}
      {/* Right axis label */}
      <text x={W-PAD_R+4} y={PAD_T-5} textAnchor="start" fontSize={9} fill="var(--ink-3)" opacity=".5">Δ Hist</text>

      {/* PCR overlay — second right column */}
      {vis.has('pcr_overlay') && pcrOvVals.length > 0 && <>
        {pcrOvTicksFiltered.map(({ v, y }) => (
          <text key={`p${v}`} x={W-PAD_R+56} y={y+3} textAnchor="start" fontSize={10} fill="var(--ink-2)" opacity=".75">
            {v.toFixed(2)}
          </text>
        ))}
        <text x={W-PAD_R+56} y={PAD_T-5} textAnchor="start" fontSize={9} fill="var(--ink-2)" opacity=".6">PCR</text>
        {!vis.has('nifty') && <line x1={W-PAD_R+52} x2={W-PAD_R+52} y1={PAD_T} y2={PAD_T+cH} stroke="var(--hair-strong)" strokeWidth=".4" opacity=".3"/>}
      </>}

      {/* NIFTY third axis — second right column */}
      {vis.has('nifty') && niftyVals.length > 0 && <>
        {niftyTicksFiltered.map(({ v, y }) => (
          <text key={`n${v}`} x={W-PAD_R+56} y={y+3} textAnchor="start" fontSize={10} fill="#a78bfa" opacity=".8">
            {v >= 1000 ? (v/1000).toFixed(1)+'K' : v.toFixed(0)}
          </text>
        ))}
        <text x={W-PAD_R+56} y={PAD_T-5} textAnchor="start" fontSize={9} fill="#a78bfa" opacity=".7">{symbol}</text>
        <line x1={W-PAD_R+52} x2={W-PAD_R+52} y1={PAD_T} y2={PAD_T+cH} stroke="#a78bfa" strokeWidth=".4" opacity=".3"/>
      </>}

      {/* Hist: PE-CE line vs EMA9 — inverted: pebar=bullish above, amber=bearish below */}
      {vis.has('hist_net_ema9') && data.map((d, i) => {
        const cx = xOf(times[i]);
        const y1 = yHist(d.hist_net_ema9); const y2 = yZero;
        return <rect key={i} x={cx - barW/2} y={Math.min(y1,y2)} width={barW}
          height={Math.max(1, Math.abs(y1-y2))}
          fill={d.hist_net_ema9 >= 0 ? 'var(--pebar)' : 'var(--amber-ink)'} opacity=".5"/>;
      })}

      {/* Hist: EMA3 vs EMA9 — inverted: pebar above, cebar below */}
      {vis.has('hist_ema3_ema9') && data.map((d, i) => {
        const cx = xOf(times[i]);
        const y1 = yHist(d.hist_ema3_ema9); const y2 = yZero;
        return <rect key={i} x={cx - barW/2} y={Math.min(y1,y2)} width={barW}
          height={Math.max(1, Math.abs(y1-y2))}
          fill={d.hist_ema3_ema9 >= 0 ? 'var(--pebar)' : 'var(--cebar)'} opacity=".55"/>;
      })}

      {/* Hist: EMA3 vs EMA21 — blue above zero (bullish), red below (bearish) */}
      {vis.has('hist_ema3_ema21') && data.map((d, i) => {
        const cx = xOf(times[i]);
        const y1 = yHist(d.hist_ema3_ema21); const y2 = yZero;
        return <rect key={i} x={cx - barW/2} y={Math.min(y1,y2)} width={barW}
          height={Math.max(1, Math.abs(y1-y2))}
          fill={d.hist_ema3_ema21 >= 0 ? 'var(--pebar)' : 'var(--cebar)'} opacity=".55"/>;
      })}

      {/* Hist: EMA9 vs EMA21 — red above zero, blue below */}
      {vis.has('hist_ema9_ema21') && data.map((d, i) => {
        const cx = xOf(times[i]);
        const y1 = yHist(d.hist_ema9_ema21); const y2 = yZero;
        return <rect key={i} x={cx - barW/2} y={Math.min(y1,y2)} width={barW}
          height={Math.max(1, Math.abs(y1-y2))}
          fill={d.hist_ema9_ema21 >= 0 ? 'var(--pebar)' : 'var(--cebar)'} opacity=".55"/>;
      })}

      {/* Filled area: green above zero, red below zero */}
      {vis.has('net_chg') && <path d={posPath} fill="url(#grad-net-pos)" clipPath="url(#clip-net-pos)" opacity=".5"/>}
      {vis.has('net_chg') && <path d={negPath} fill="url(#grad-net-neg)" clipPath="url(#clip-net-neg)" opacity=".5"/>}

      {/* Net line */}
      {vis.has('net_chg') && <polyline points={netPts} fill="none" stroke="var(--cebar)" strokeWidth="1.4"
        strokeLinejoin="round" strokeLinecap="round" opacity=".9"/>}

      {/* EMA lines */}
      {vis.has('ema21') && <polyline points={ema21Pts} fill="none" stroke="var(--pebar)" strokeWidth="1.2"
        strokeLinejoin="round" strokeLinecap="round" opacity=".85" strokeDasharray="6,2"/>}
      {vis.has('ema9')  && <polyline points={ema9Pts}  fill="none" stroke="var(--amber-ink)" strokeWidth="1.2"
        strokeLinejoin="round" strokeLinecap="round" opacity=".85" strokeDasharray="4,2"/>}
      {vis.has('ema3')  && <polyline points={ema3Pts}  fill="none" stroke="var(--cebar)" strokeWidth="1"
        strokeLinejoin="round" strokeLinecap="round" opacity=".8"  strokeDasharray="2,2"/>}

      {/* NIFTY spot line (third axis) — drawn from full intraday data starting 9:15 */}
      {vis.has('nifty') && niftyIntraday.length > 1 && (() => {
        const pts = niftyIntraday
          .map(p => {
            const x = xOf(_toMins(p.time));
            return x >= PAD_L ? `${x.toFixed(1)},${yNifty(p.close).toFixed(1)}` : null;
          })
          .filter(Boolean).join(' ');
        return <polyline points={pts} fill="none" stroke="#a78bfa" strokeWidth="1.4"
          strokeLinejoin="round" strokeLinecap="round" opacity=".85"/>;
      })()}

      {/* PCR overlay line (third axis, same column) */}
      {vis.has('pcr_overlay') && pcrOvVals.length > 1 && (() => {
        const pts = data
          .map((d, i) => d.pcr != null ? `${xOf(times[i]).toFixed(1)},${yPCROv(d.pcr).toFixed(1)}` : null)
          .filter(Boolean).join(' ');
        return <polyline points={pts} fill="none" stroke="var(--ink)" strokeWidth="1.2"
          strokeLinejoin="round" strokeLinecap="round" opacity=".7" strokeDasharray="4,2"/>;
      })()}

      {/* Latest dot + label */}
      {hoverIdx === null && (() => {
        const last = data[n-1];
        const lx = xOf(times[n-1]);
        return (
          <>
            {vis.has('net_chg') && <>
              <circle cx={lx} cy={yOf(last.net_chg)} r="2.5"
                fill={last.net_chg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}/>
              <text x={lx-4} y={yOf(last.net_chg)-4} textAnchor="end" fontSize={7.5} fontWeight={700}
                fill={last.net_chg >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}>
                {fmtOISign(last.net_chg)}
              </text>
            </>}
            {vis.has('ema3')   && <circle cx={lx} cy={yOf(last.ema3)}  r="2" fill="var(--cebar)"/>}
            {vis.has('ema9')   && <circle cx={lx} cy={yOf(last.ema9)}  r="2" fill="var(--amber-ink)"/>}
            {vis.has('ema21')  && <circle cx={lx} cy={yOf(last.ema21)} r="2" fill="var(--pebar)"/>}
            {vis.has('nifty') && niftyAligned[n-1] != null && <circle cx={lx} cy={yNifty(niftyAligned[n-1])} r="2.5" fill="#a78bfa"/>}
            {vis.has('pcr_overlay') && data[n-1]?.pcr != null && <circle cx={lx} cy={yPCROv(data[n-1].pcr)} r="2" fill="var(--ink)"/>}
          </>
        );
      })()}

      {cursorEls}

      {xTicks.map(({ mins, label }) => (
        <text key={mins} x={xOf(mins)} y={H-PAD_B+11} textAnchor="middle" fontSize={11} fill="var(--ink-3)">{label}</text>
      ))}
    </svg>
    </div>
  );
}


// ── PCR / OI Intraday History Chart ───────────────────────────────────────────
// Tabbed: Tab 1 = PCR + total CE/PE OI area; Tab 2 = CE/PE cumulative OI change.

function OIPCRHistoryChart({ history, niftyIntraday = [], symbol = 'NIFTY' }) {
  const [tab, setTab] = useState('pcr'); // 'pcr' | 'change' | 'net'
  // Per-tab visibility sets — click legend items to toggle series
  const [visPCR,    setVisPCR]    = useState(() => new Set(['pe_oi','ce_oi','pcr']));
  const [visChange, setVisChange] = useState(() => new Set(['pe_chg','ce_chg','pcr']));
  const [visNet,    setVisNet]    = useState(() => new Set(['ema3','ema9','hist_net_ema9']));
  const vis    = tab === 'pcr' ? visPCR : tab === 'change' ? visChange : visNet;
  const setVis = tab === 'pcr' ? setVisPCR : tab === 'change' ? setVisChange : setVisNet;
  const toggle = key => setVis(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  // Must be before early return — hook ordering
  const minPerPt  = useMemo(() => meanGapMinutes(history), [history]);

  if (history.length < 2) return null;

  const firstTime = history[0]?.time;
  const lastTime  = history[history.length - 1]?.time;

  // Legend item style — dim when hidden
  const legItem = (key, color, swatch, label) => (
    <span key={key} onClick={() => toggle(key)}
      style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer',
        opacity: vis.has(key) ? 1 : 0.3, userSelect:'none' }}>
      <svg width="20" height="8">{swatch(color)}</svg>{label}
    </span>
  );
  const area = c => <rect x="0" y="2" width="20" height="4" fill={c} opacity=".35"/>;
  const line = c => <line x1="0" y1="4" x2="20" y2="4" stroke={c} strokeWidth="1.5"/>;

  return (
    <div style={{ marginTop:10 }}>
      {/* Header row with tab toggle */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, paddingLeft:2, flexWrap:'wrap' }}>
        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, fontWeight:600, color:'var(--ink-2)' }}>
            {tab === 'pcr' ? 'PCR / OI History' : tab === 'change' ? 'CE & PE OI Change' : 'PE−CE OI Change'}
          </span>
          <InfoBtn align="right">
            PCR (PE÷CE) and total CE/PE OI tracked since market open. PCR &gt; 1 = more put writers (bullish signal); PCR &lt; 1 = more call writers (bearish signal). OI Change view shows cumulative build/unwind since open. PE−CE tab shows net difference (PE Chg minus CE Chg) — rising = put writers gaining (bullish), falling = call writers gaining (bearish).{' '}
            <a href="#methodology" style={{ color:'var(--amber-ink)' }}>View full methodology →</a>
          </InfoBtn>
        </span>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>
          {firstTime} → {lastTime} · {history.length} pts{minPerPt != null ? ` · ${minPerPt.toFixed(1)} min/pt` : ''}
        </span>

        {/* Tab pills */}
        <div style={{ display:'flex', gap:1, borderRadius:4, overflow:'hidden',
          border:'.5px solid var(--hair-strong)', marginLeft:8 }}>
          {[['pcr','PCR / OI'],['change','OI Change'],['net','PE−CE Chg']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ fontSize:11, fontWeight:700, padding:'2px 9px', cursor:'pointer', border:'none',
                background: tab===key ? 'var(--amber-ink)' : 'var(--surface-2)',
                color:      tab===key ? '#fff' : 'var(--ink-2)' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Clickable legend — per tab */}
        <div style={{ marginLeft:'auto', display:'flex', gap:8, fontSize:11, color:'var(--ink-3)', alignItems:'center' }}>
          {tab === 'pcr' ? (
            <>
              {legItem('pe_oi',    'var(--pebar)',     area, 'PE OI')}
              {legItem('ce_oi',    'var(--cebar)',     area, 'CE OI')}
              {legItem('pcr',      'var(--ink)',       line, 'PCR')}
              {legItem('pcr_ema9', 'var(--amber-ink)', line, emaLabel('EMA 9', 9, minPerPt))}
            </>
          ) : tab === 'change' ? (
            <>
              {legItem('pe_chg',   'var(--pebar)',     line, 'PE Chg')}
              {legItem('ce_chg',   'var(--cebar)',     line, 'CE Chg')}
              {legItem('pcr',      'var(--ink)',       line, 'PCR')}
              {legItem('pcr_ema9', 'var(--amber-ink)', line, emaLabel('EMA 9', 9, minPerPt))}
              <span style={{ fontSize:11, color:'var(--ink-3)', opacity:.7 }}>· from {firstTime} open</span>
            </>
          ) : (
            <>
              {legItem('net_chg',         'var(--cebar)',       line, 'PE−CE')}
              {legItem('ema3',            'var(--cebar)',       line, emaLabel('EMA 3', 3, minPerPt))}
              {legItem('ema9',            'var(--amber-ink)',   line, emaLabel('EMA 9', 9, minPerPt))}
              {legItem('ema21',           'var(--pebar)',       line, emaLabel('EMA 21', 21, minPerPt))}
              {legItem('hist_net_ema9',   'var(--pebar)',        area, emaLabel('Net−E9', 9, minPerPt))}
              {legItem('hist_ema3_ema9',  'var(--pebar)',        area, emaLabel('E3−E9', 9, minPerPt))}
              {legItem('hist_ema3_ema21', 'var(--pebar)',        area, emaLabel('E3−E21', 21, minPerPt))}
              {legItem('hist_ema9_ema21', 'var(--pebar)',        area, emaLabel('E9−E21', 21, minPerPt))}
              {legItem('pcr_overlay',     'var(--ink)',          line, 'PCR')}
              {legItem('nifty',           '#a78bfa',            line, symbol)}
              <span style={{ fontSize:11, color:'var(--ink-3)', opacity:.7 }}>· from {firstTime} open</span>
            </>
          )}
        </div>
      </div>

      <div style={{ overflowX:'auto' }}>
        {tab === 'pcr'    && <OIPCRChart    history={history} vis={visPCR}    minPerPt={minPerPt}/>}
        {tab === 'change' && <OIChangeChart history={history} vis={visChange} minPerPt={minPerPt}/>}
        {tab === 'net'    && (
          <OINetChart
            key="net"
            history={history}
            niftyIntraday={niftyIntraday}
            vis={visNet}
            minPerPt={minPerPt}
            symbol={symbol}
          />
        )}
      </div>
    </div>
  );
}


// ── Max Pain History Chart ────────────────────────────────────────────────────
// Shows max pain + NIFTY spot as a 5-min intraday time-series across last 5 days.
// history: [{date, time, expiry, maxPain, spot, pcr}, ...] ordered oldest → newest.
function MaxPainHistoryChart({ history, symbol = 'NIFTY' }) {
  const svgRef       = useRef(null);
  const [hoverIdx,  setHoverIdx]  = useState(null);
  const [visLines,  setVisLines]  = useState(() => new Set(['mp', 'spot', 'pcr']));
  const toggleLine = key => setVisLines(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });

  // PAD_R=52 to fit right PCR axis labels
  const [W, containerRef] = useContainerWidth(820); const H = 205;
  const PAD_L = 52, PAD_R = 52, PAD_T = 20, PAD_B = 28;
  const cW = W - PAD_L - PAD_R;
  const cH = H - PAD_T - PAD_B;

  const n = history?.length ?? 0;

  const handleMouseMove = useCallback((e) => {
    if (n < 2) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) / rect.width * W;
    if (svgX < PAD_L || svgX > W - PAD_R) { setHoverIdx(null); return; }
    const raw = (svgX - PAD_L) / cW * (n - 1);
    setHoverIdx(Math.max(0, Math.min(n - 1, Math.round(raw))));
  }, [n, cW]);

  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);

  if (!history || history.length < 2) return null;

  const validSpot = r => r.spot != null && r.spot > 0 && isFinite(r.spot);
  const validPcr  = r => r.pcr  != null && r.pcr  > 0 && isFinite(r.pcr);

  // Left Y scale — price (Max Pain + Spot)
  const allVals = history.flatMap(r => [
    r.maxPain != null && isFinite(r.maxPain) ? r.maxPain : null,
    validSpot(r) ? r.spot : null,
  ].filter(v => v != null));
  if (!allVals.length) return null;

  const rawMin = Math.min(...allVals);
  const rawMax = Math.max(...allVals);
  const pad    = Math.max((rawMax - rawMin) * 0.18, 30);
  const yMin   = rawMin - pad;
  const yMax   = rawMax + pad;
  const yOf    = v => PAD_T + cH * (1 - (v - yMin) / Math.max(1, yMax - yMin));

  // Right Y scale — PCR
  const pcrVals = history.filter(validPcr).map(r => r.pcr);
  const hasPcr  = pcrVals.length > 1;
  const pcrRawMin = hasPcr ? Math.min(...pcrVals) : 0;
  const pcrRawMax = hasPcr ? Math.max(...pcrVals) : 3;
  const pcrPad    = Math.max((pcrRawMax - pcrRawMin) * 0.25, 0.15);
  const pcrMin    = Math.max(0, pcrRawMin - pcrPad);
  const pcrMax    = pcrRawMax + pcrPad;
  const yPcr      = v => PAD_T + cH * (1 - (v - pcrMin) / Math.max(0.01, pcrMax - pcrMin));

  const xOf = i => PAD_L + (n < 2 ? cW / 2 : (i / (n - 1)) * cW);

  const mpPts  = history.map((r, i) => r.maxPain != null ? `${xOf(i).toFixed(1)},${yOf(r.maxPain).toFixed(1)}` : null).filter(Boolean).join(' ');
  const sptPts = history.map((r, i) => validSpot(r) ? `${xOf(i).toFixed(1)},${yOf(r.spot).toFixed(1)}` : null).filter(Boolean).join(' ');
  const pcrPts = history.map((r, i) => validPcr(r)  ? `${xOf(i).toFixed(1)},${yPcr(r.pcr).toFixed(1)}` : null).filter(Boolean).join(' ');

  // Left Y-axis ticks (price)
  const tickStep = Math.ceil((yMax - yMin) / 4 / 25) * 25 || 25;
  const yTicks = [];
  for (let v = Math.ceil(yMin / tickStep) * tickStep; v <= yMax + tickStep * 0.1; v += tickStep) {
    yTicks.push({ v, y: yOf(v) });
  }

  // Right Y-axis ticks (PCR)
  const pcrTicks = [];
  if (hasPcr) {
    const pStep = Math.ceil((pcrMax - pcrMin) / 4 * 10) / 10 || 0.2;
    for (let v = Math.ceil(pcrMin / pStep) * pStep; v <= pcrMax + pStep * 0.1; v += pStep) {
      const vr = Math.round(v * 10) / 10;
      pcrTicks.push({ v: vr, y: yPcr(vr) });
    }
  }

  // Day boundaries
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtDate = d => { if (!d) return ''; const [,mm,dd] = d.split('-'); return `${parseInt(dd,10)} ${MONTHS[parseInt(mm,10)-1]}`; };
  const dayBoundaries = [];
  let prevDateVal = null;
  history.forEach((r, i) => {
    if (r.date !== prevDateVal) { dayBoundaries.push({ i, x: xOf(i), label: fmtDate(r.date) }); prevDateVal = r.date; }
  });

  const first = history[0];
  const last  = history[n - 1];

  const fmtDT = r => r ? `${r.time} (${fmtDate(r.date)})` : '';
  const rangeLabel = `${fmtDT(first)} → ${fmtDT(last)} · ${n} pts`;

  // Hover tooltip
  const PCR_COLOR = 'var(--pebar)';
  let cursorEls = null;
  if (hoverIdx !== null) {
    const r  = history[hoverIdx];
    const cx = xOf(hoverIdx);
    const TW = 150, TH = hasPcr ? 86 : 68;
    const tx = Math.round(cx + 8 + TW > W - PAD_R ? cx - 8 - TW : cx + 8);
    const ty = PAD_T + 2;
    cursorEls = (
      <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
        <line x1={cx} x2={cx} y1={PAD_T} y2={PAD_T + cH}
          stroke="var(--ink-3)" strokeWidth=".8" strokeDasharray="2,2" opacity=".5"/>
        {r.maxPain != null && <circle cx={cx} cy={yOf(r.maxPain)}  r="2.5" fill="var(--accent-blue)"/>}
        {validSpot(r)      && <circle cx={cx} cy={yOf(r.spot)}     r="2.5" fill="var(--ink-2)"/>}
        {validPcr(r)       && <circle cx={cx} cy={yPcr(r.pcr)}     r="2.5" fill={PCR_COLOR}/>}
        <rect x={tx} y={ty} width={TW} height={TH}
          fill="var(--surface-2)" fillOpacity="0.85" stroke="var(--hair-strong)" strokeWidth=".8" rx="5"
          style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
        <text x={tx + TW / 2} y={ty + 16} textAnchor="middle"
          fontSize={11} fontWeight={700} fill="var(--ink)">{r.time} · {fmtDate(r.date)}</text>
        <text x={tx + 7}      y={ty + 32} fontSize={11} fill="var(--ink-2)">Max Pain</text>
        <text x={tx + TW - 7} y={ty + 32} textAnchor="end"
          fontSize={11} fontWeight={600} fill="var(--accent-blue)">
          {r.maxPain?.toLocaleString('en-IN') ?? '—'}
        </text>
        <text x={tx + 7}      y={ty + 50} fontSize={11} fill="var(--ink-2)">{symbol}</text>
        <text x={tx + TW - 7} y={ty + 50} textAnchor="end"
          fontSize={11} fontWeight={600} fill="var(--ink)">
          {validSpot(r) ? r.spot.toLocaleString('en-IN') : '—'}
        </text>
        {hasPcr && <>
          <text x={tx + 7}      y={ty + 68} fontSize={11} fill="var(--ink-2)">PCR</text>
          <text x={tx + TW - 7} y={ty + 68} textAnchor="end"
            fontSize={11} fontWeight={600} fill={PCR_COLOR}>
            {validPcr(r) ? r.pcr.toFixed(2) : '—'}
          </text>
        </>}
      </g>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, paddingLeft:2, flexWrap:'wrap' }}>
        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, fontWeight:600, color:'var(--ink-2)' }}>PCR + Max Pain History</span>
          <InfoBtn align="left">
            Tracks three signals over the last 5 trading days. Max Pain (dashed blue) = the strike where option writers lose least; spot gravitates toward it near expiry. {symbol} Spot (grey) = underlying price. PCR (blue, right axis) = total Put/Call OI ratio; above 1 signals put-heavy positioning, below 1 signals call-heavy. Click legend items to show/hide individual lines.
          </InfoBtn>
        </span>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>{rangeLabel}</span>
        <div style={{ marginLeft:'auto', display:'flex', gap:12, fontSize:11, color:'var(--ink-3)', alignItems:'center' }}>
          {[
            { key:'mp',   stroke:'var(--accent-blue)', dash:'5,2', label:'Max Pain',  w:1.5 },
            { key:'spot', stroke:'var(--ink-2)',       dash:null,   label:'NIFTY Spot', w:1.5 },
            ...(hasPcr ? [{ key:'pcr', stroke:PCR_COLOR, dash:null, label:'PCR', w:1.5 }] : []),
          ].map(({ key, stroke, dash, label, w }) => (
            <span key={key} onClick={() => toggleLine(key)}
              style={{ display:'flex', alignItems:'center', gap:4, cursor:'pointer',
                opacity: visLines.has(key) ? 1 : 0.35, userSelect:'none' }}>
              <svg width="20" height="8">
                <line x1="0" y1="4" x2="20" y2="4" stroke={stroke} strokeWidth={w}
                  {...(dash ? { strokeDasharray: dash } : {})}/>
              </svg>
              {label}
            </span>
          ))}
        </div>
      </div>

      <div ref={containerRef} style={{ overflowX:'auto' }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} height={H}
          style={{ width:'100%', minWidth:320, display:'block', cursor:'crosshair' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}>

          {/* Left Y grid + labels (price) */}
          {yTicks.map(({ v, y }) => (
            <g key={v}>
              <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                stroke="var(--hair)" strokeWidth=".5" strokeDasharray="3,3"/>
              <text x={PAD_L - 4} y={y + 3.5} textAnchor="end" fontSize={11} fill="var(--ink-3)">
                {v.toLocaleString('en-IN')}
              </text>
            </g>
          ))}

          {/* Right Y axis labels (PCR) */}
          {pcrTicks.map(({ v, y }) => (
            <text key={v} x={W - PAD_R + 5} y={y + 3.5} textAnchor="start"
              fontSize={10} fill={PCR_COLOR} opacity=".8">
              {v.toFixed(1)}
            </text>
          ))}

          {/* Day boundary lines + date labels */}
          {dayBoundaries.map(({ i, x, label }) => (
            <g key={label + i}>
              {i > 0 && (
                <line x1={x} y1={PAD_T} x2={x} y2={PAD_T + cH}
                  stroke="var(--hair-strong)" strokeWidth=".8" opacity=".6"/>
              )}
              <text x={x + (i === 0 ? 0 : 3)} y={H - 4} textAnchor="start"
                fontSize={11} fill="var(--ink-3)" opacity=".8">{label}</text>
            </g>
          ))}

          {/* PCR line — drawn before price lines so it sits behind */}
          {hasPcr && visLines.has('pcr') && pcrPts && <polyline points={pcrPts} fill="none"
            stroke={PCR_COLOR} strokeWidth="1.2" opacity=".7"/>}

          {/* Max Pain line — dashed accent */}
          {visLines.has('mp') && mpPts && <polyline points={mpPts} fill="none"
            stroke="var(--accent-blue)" strokeWidth="1.5" strokeDasharray="5,3" opacity=".85"/>}

          {/* Spot line — solid */}
          {visLines.has('spot') && sptPts && <polyline points={sptPts} fill="none"
            stroke="var(--ink-2)" strokeWidth="1.2" opacity=".7"/>}

          {/* Latest value dots */}
          {visLines.has('mp')   && last?.maxPain != null && <circle cx={xOf(n-1)} cy={yOf(last.maxPain)} r={3} fill="var(--accent-blue)" opacity=".9"/>}
          {visLines.has('spot') && last?.spot    != null && <circle cx={xOf(n-1)} cy={yOf(last.spot)}    r={3} fill="var(--ink-2)" opacity=".8"/>}
          {hasPcr && visLines.has('pcr') && validPcr(last) && <circle cx={xOf(n-1)} cy={yPcr(last.pcr)} r={3} fill={PCR_COLOR} opacity=".9"/>}

          {/* Hover cursor + tooltip */}
          {cursorEls}
        </svg>
      </div>
    </div>
  );
}


// ── PCR / OI core chart (inner, no wrapping div) ───────────────────────────────
function OIPCRChart({ history, vis = new Set(['pe_oi','ce_oi','pcr']), minPerPt = null }) {
  // Hover cursor state
  const svgRef       = useRef(null);
  const [W, containerRef] = useContainerWidth(820); const H = 460;
  const PAD_L = 48, PAD_R = 44, PAD_T = 18, PAD_B = 28;
  const cW = W - PAD_L - PAD_R;
  const cH = H - PAD_T - PAD_B;

  const [hoverIdx, setHoverIdx] = useState(null);

  const n = history.length;

  // Time-proportional x scale — anchored to 9:15 so the full trading day is shown
  const MARKET_OPEN = 9 * 60 + 15;
  const times = useMemo(() => history.map(d => _toMins(d.time)), [history]);
  const tMin  = useMemo(() => { const v = times.filter(isFinite); return v.length ? Math.min(MARKET_OPEN, Math.min(...v)) : 0; }, [times]);
  const tMax  = useMemo(() => { const v = times.filter(isFinite); return v.length ? Math.max(...v) : 1; }, [times]);

  const handleMouseMove = useCallback((e) => {
    if (n < 2) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) / rect.width * W;
    if (svgX < PAD_L || svgX > W - PAD_R) { setHoverIdx(null); return; }
    const rawMins = tMin + ((svgX - PAD_L) / cW) * (tMax - tMin);
    let best = 0, bestD = Infinity;
    times.forEach((m, i) => { const d = Math.abs(m - rawMins); if (d < bestD) { bestD = d; best = i; } });
    setHoverIdx(best);
  }, [n, cW, tMin, tMax, times]);

  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);

  // EMA9 of PCR (must be before early return — hook ordering)
  const pcrEma9 = useMemo(() => {
    const alpha = 2 / (9 + 1);
    let ema = history[0]?.pcr ?? 1;
    return history.map(p => { ema = alpha * p.pcr + (1 - alpha) * ema; return ema; });
  }, [history]);

  if (n < 2) return null;

  // Time-proportional x mapper: minutes → pixel x
  const xOf = mins => PAD_L + ((mins - tMin) / Math.max(1, tMax - tMin)) * cW;

  // PCR y-scale (right axis) — tight range around actual values for max fidelity
  const pcrVals  = history.map(p => p.pcr).filter(v => v > 0);
  const _pcrLo   = Math.min(...pcrVals);
  const _pcrHi   = Math.max(...pcrVals);
  const _pcrPad  = Math.max(0.06, (_pcrHi - _pcrLo) * 0.25);
  const pcrMin   = Math.max(0, _pcrLo - _pcrPad);
  const pcrMax   = _pcrHi + _pcrPad;
  const yPCR     = v => PAD_T + cH * (1 - (Math.min(v, pcrMax) - pcrMin) / Math.max(0.01, pcrMax - pcrMin));

  // OI totals y-scale (left axis — zoomed to actual data range for fidelity)
  const _oiAllVals = history.flatMap(p => [p.total_ce, p.total_pe]).filter(v => v > 0);
  const oiMin = _oiAllVals.length > 0 ? Math.min(..._oiAllVals) * 0.996 : 0;
  const oiMax = Math.max(1, ..._oiAllVals) * 1.002;
  const yOI   = v => PAD_T + cH * (1 - (v - oiMin) / Math.max(1, oiMax - oiMin));

  // PCR polyline — x mapped by actual time
  const pcrPts  = history.map((p, i) => `${xOf(times[i]).toFixed(1)},${yPCR(p.pcr).toFixed(1)}`).join(' ');
  const ema9Pts = history.map((p, i) => `${xOf(times[i]).toFixed(1)},${yPCR(pcrEma9[i]).toFixed(1)}`).join(' ');

  // CE area path
  const cePts  = history.map((p, i) => `${xOf(times[i]).toFixed(1)},${yOI(p.total_ce).toFixed(1)}`).join(' ');
  const ceArea = `M ${xOf(tMin).toFixed(1)},${(PAD_T + cH).toFixed(1)} `
    + history.map((p, i) => `L ${xOf(times[i]).toFixed(1)},${yOI(p.total_ce).toFixed(1)}`).join(' ')
    + ` L ${xOf(tMax).toFixed(1)},${(PAD_T + cH).toFixed(1)} Z`;

  // PE area path
  const pePts  = history.map((p, i) => `${xOf(times[i]).toFixed(1)},${yOI(p.total_pe).toFixed(1)}`).join(' ');
  const peArea = `M ${xOf(tMin).toFixed(1)},${(PAD_T + cH).toFixed(1)} `
    + history.map((p, i) => `L ${xOf(times[i]).toFixed(1)},${yOI(p.total_pe).toFixed(1)}`).join(' ')
    + ` L ${xOf(tMax).toFixed(1)},${(PAD_T + cH).toFixed(1)} Z`;

  // X-axis ticks (time-proportional)
  const xTicks = buildIntraDayXTicks(history, W, PAD_L, PAD_R, 'time', MARKET_OPEN);

  // PCR = 1 reference line y-position
  const pcrTick1 = yPCR(1).toFixed(1);

  // ── Hover cursor elements ───────────────────────────────────────────────────
  let cursorEls = null;
  if (hoverIdx !== null) {
    const p  = history[hoverIdx];
    const cx = xOf(times[hoverIdx]);
    const rows_ = [
      { l:'CE OI',    v: fmtOI(p.total_ce),           c:'var(--cebar)',      vis: vis.has('ce_oi')    },
      { l:'PE OI',    v: fmtOI(p.total_pe),           c:'var(--pebar)',      vis: vis.has('pe_oi')    },
      { l:'PCR',      v: p.pcr.toFixed(3),             c:'var(--ink)',        vis: vis.has('pcr')      },
      { l: emaLabel('PCR EMA9', 9, minPerPt), v: pcrEma9[hoverIdx].toFixed(3), c:'var(--amber-ink)', vis: vis.has('pcr_ema9') },
    ].filter(r => r.vis);
    const rowH = 16, TW = 120, TH = rows_.length * rowH + 28;
    const tx = Math.round(cx + 8 + TW > W - PAD_R ? cx - 8 - TW : cx + 8);
    const ty = PAD_T + 3;

    cursorEls = (
      <g style={{ pointerEvents:'none' }} textRendering="geometricPrecision">
        {/* Vertical crosshair */}
        <line x1={cx} x2={cx} y1={PAD_T} y2={PAD_T + cH}
          stroke="var(--ink-3)" strokeWidth=".8" strokeDasharray="2,2" opacity=".5"/>
        {/* Dots on visible series only */}
        {vis.has('ce_oi')    && <circle cx={cx} cy={yOI(p.total_ce)}          r="2.5" fill="var(--cebar)"/>}
        {vis.has('pe_oi')    && <circle cx={cx} cy={yOI(p.total_pe)}          r="2.5" fill="var(--pebar)"/>}
        {vis.has('pcr')      && <circle cx={cx} cy={yPCR(p.pcr)}              r="2.5" fill="var(--ink)"/>}
        {vis.has('pcr_ema9') && <circle cx={cx} cy={yPCR(pcrEma9[hoverIdx])} r="2.5" fill="var(--amber-ink)"/>}
        <rect x={tx} y={ty} width={TW} height={TH}
          fill="var(--surface-2)" fillOpacity="0.88" stroke="var(--hair-strong)" strokeWidth=".8" rx="4"
          style={{ filter:'drop-shadow(0 2px 6px rgba(0,0,0,.22))' }}/>
        {/* Time header */}
        <text x={tx + TW / 2} y={ty + 16} textAnchor="middle"
          fontSize={11} fontWeight={700} fill="var(--ink)">{p.time}</text>
        {rows_.map((item, k) => (
          <g key={k}>
            <text x={tx + 6}      y={ty + 28 + k * rowH} fontSize={11} fill={item.c}>{item.l}</text>
            <text x={tx + TW - 6} y={ty + 28 + k * rowH} textAnchor="end"
              fontSize={11} fontWeight={700} fill={item.c}>{item.v}</text>
          </g>
        ))}
      </g>
    );
  }

  return (
    <div ref={containerRef}>
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} height={H}
          style={{ width:'100%', minWidth:340, display:'block', cursor:'crosshair' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}>
          <defs>
            <linearGradient id="grad-pe-hist" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--pebar)" stopOpacity=".4"/>
              <stop offset="100%" stopColor="var(--pebar)" stopOpacity=".04"/>
            </linearGradient>
            <linearGradient id="grad-ce-hist" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--cebar)" stopOpacity=".4"/>
              <stop offset="100%" stopColor="var(--cebar)" stopOpacity=".04"/>
            </linearGradient>
          </defs>

          {/* Grid */}
          <line x1={PAD_L} x2={W - PAD_R} y1={PAD_T} y2={PAD_T}
            stroke="var(--hair)" strokeWidth=".4"/>
          <line x1={PAD_L} x2={W - PAD_R} y1={PAD_T + cH} y2={PAD_T + cH}
            stroke="var(--hair)" strokeWidth=".4"/>
          {/* PCR = 1 reference — only render if 1.0 is within the visible range */}
          {pcrMin < 1.0 && pcrMax > 1.0 && (
            <>
              <line x1={PAD_L} x2={W - PAD_R} y1={pcrTick1} y2={pcrTick1}
                stroke="var(--hair-strong)" strokeWidth=".6" strokeDasharray="3,3" opacity=".6"/>
              <text x={W - PAD_R + 3} y={Number(pcrTick1) + 3.5} fontSize={11} fill="var(--ink-3)" opacity=".7">1.0</text>
            </>
          )}

          {/* Left axis label — shows zoomed range (min at bottom, max at top) */}
          <text x={PAD_L - 4} y={PAD_T + 4} textAnchor="end" fontSize={11} fill="var(--ink-3)">
            {fmtOI(oiMax)}
          </text>
          <text x={PAD_L - 4} y={PAD_T + cH + 3} textAnchor="end" fontSize={11} fill="var(--ink-3)">
            {fmtOI(oiMin)}
          </text>

          {/* Right axis PCR — show max and min of tight range */}
          <text x={W - PAD_R + 3} y={PAD_T + 4} fontSize={11} fill="var(--ink-3)" opacity=".7">
            {pcrMax.toFixed(2)}
          </text>
          <text x={W - PAD_R + 3} y={PAD_T + cH + 3} fontSize={11} fill="var(--ink-3)" opacity=".7">
            {pcrMin.toFixed(2)}
          </text>

          {/* CE area */}
          {vis.has('ce_oi') && <path d={ceArea} fill="url(#grad-ce-hist)"/>}
          {vis.has('ce_oi') && <polyline points={cePts} fill="none" stroke="var(--cebar)" strokeWidth="1"
            strokeLinejoin="round" strokeLinecap="round" opacity=".7"/>}

          {/* PE area */}
          {vis.has('pe_oi') && <path d={peArea} fill="url(#grad-pe-hist)"/>}
          {vis.has('pe_oi') && <polyline points={pePts} fill="none" stroke="var(--pebar)" strokeWidth="1"
            strokeLinejoin="round" strokeLinecap="round" opacity=".7"/>}

          {/* PCR line */}
          {vis.has('pcr') && <polyline points={pcrPts} fill="none" stroke="var(--ink)" strokeWidth="1.5"
            strokeLinejoin="round" strokeLinecap="round"/>}
          {/* PCR EMA9 line */}
          {vis.has('pcr_ema9') && <polyline points={ema9Pts} fill="none" stroke="var(--amber-ink)" strokeWidth="1.5"
            strokeLinejoin="round" strokeLinecap="round" opacity=".9"/>}
          {/* Latest PCR dot */}
          {vis.has('pcr') && hoverIdx === null && (
            <>
              <circle cx={xOf(tMax)} cy={yPCR(history[n - 1].pcr)} r="2.5" fill="var(--ink)"/>
              <text x={xOf(tMax) + 4} y={yPCR(history[n - 1].pcr) + 3}
                fontSize={8} fontWeight={700} fill="var(--ink)">
                {history[n - 1].pcr.toFixed(3)}
              </text>
            </>
          )}
          {/* Latest PCR EMA9 dot */}
          {vis.has('pcr_ema9') && hoverIdx === null && (
            <circle cx={xOf(tMax)} cy={yPCR(pcrEma9[n - 1])} r="2.5" fill="var(--amber-ink)"/>
          )}

          {/* Hover cursor + tooltip (rendered on top of everything) */}
          {cursorEls}

          {/* X-axis time labels */}
          {xTicks.map(({ mins, label }) => (
            <text key={mins} x={xOf(mins)} y={H - PAD_B + 11} textAnchor="middle"
              fontSize={11} fill="var(--ink-3)">
              {label}
            </text>
          ))}
        </svg>
    </div>
  );
}


// ── Compact strike picker with -/+ steppers ────────────────────────────────────

function StrikePicker({ value, onChange, options }) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const ref     = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const filtered = useMemo(() =>
    !query ? options : options.filter(s => String(s).includes(query)),
    [options, query]
  );

  const idx = options.indexOf(value);

  const step = dir => {
    const next = options[idx + dir];
    if (next !== undefined) onChange(next);
  };

  return (
    <div ref={ref} style={{ position:'relative', display:'flex', alignItems:'center', gap:2 }}>
      <button className="step-btn" onClick={() => step(-1)}>−</button>
      <div className="inp"
        onClick={() => { setOpen(o => !o); setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); }}
        style={{ display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', minWidth:50, padding:'2px 3px' }}>
        {open
          ? <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
              style={{ width:52, fontSize:11, background:'transparent', border:'none', outline:'none', color:'var(--ink)', textAlign:'center' }}
              onKeyDown={e => { if (e.key==='Escape') setOpen(false); }}/>
          : <span style={{ fontSize:11, fontWeight:600 }}>{value || '—'}</span>
        }
      </div>
      <button className="step-btn" onClick={() => step(1)}>+</button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, zIndex:400,
          background:'var(--surface)', border:'.5px solid var(--hair-strong)',
          borderRadius:4, boxShadow:'0 4px 16px rgba(0,0,0,.16)',
          maxHeight:180, overflowY:'auto', minWidth:76 }}>
          {filtered.length === 0
            ? <div style={{ padding:'6px 10px', fontSize:11, color:'var(--ink-3)' }}>No match</div>
            : filtered.map(s => (
                <div key={s} onClick={() => { onChange(s); setOpen(false); setQuery(''); }}
                  style={{ padding:'4px 10px', fontSize:11, cursor:'pointer', whiteSpace:'nowrap',
                    background: s===value ? 'rgba(26,90,170,.1)' : 'transparent',
                    color: s===value ? 'var(--amber-ink)' : 'var(--ink)',
                    fontWeight: s===value ? 600 : 400 }}
                  onMouseEnter={e => { if(s!==value) e.currentTarget.style.background='var(--surface-2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = s===value?'rgba(26,90,170,.1)':'transparent'; }}>
                  {s}
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}


// ── Trading-day stepper helpers ────────────────────────────────────────────────

function prevTradingDay(ds) {
  const d = new Date(ds + 'T12:00:00Z');
  do { d.setUTCDate(d.getUTCDate() - 1); } while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}
function nextTradingDay(ds) {
  const d = new Date(ds + 'T12:00:00Z');
  do { d.setUTCDate(d.getUTCDate() + 1); } while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}
const todayISO = () => new Date().toISOString().slice(0, 10);


// ── Black-Scholes pricing ─────────────────────────────────────────────────────

function normCDF(x) {
  const a = [0.319381530, -0.356563782, 1.781477937, -1.821255978, 1.330274429];
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  let p   = t * (a[0] + t * (a[1] + t * (a[2] + t * (a[3] + t * a[4]))));
  p      *= 0.39894228 * Math.exp(-0.5 * x * x);
  return x >= 0 ? 1 - p : p;
}

/** Standard-normal PDF N'(x). */
function normPDF(x) { return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI); }

/** Black-Scholes option price. sigma is decimal (0.15 = 15%). T is years. */
function bsPrice(S, K, T, r, sigma, type) {
  if (T <= 1e-6 || !S || !K || sigma <= 0) {
    return type === 'CE' ? Math.max(0, S - K) : Math.max(0, K - S);
  }
  const sqT = Math.sqrt(T);
  const d1  = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqT);
  const d2  = d1 - sigma * sqT;
  if (type === 'CE') return S * normCDF(d1)  - K * Math.exp(-r * T) * normCDF(d2);
  return               K * Math.exp(-r * T) * normCDF(-d2) - S * normCDF(-d1);
}

/**
 * Black-Scholes Greeks.
 * sigma = decimal IV (0.15 = 15%), T = years to expiry, r = risk-free rate.
 * Returns { delta, gamma, theta (per day), vega (per 1% IV move) }.
 */
function bsGreeks(S, K, T, r, sigma, type) {
  if (T <= 1e-6 || !S || !K || sigma <= 0) {
    return { delta: type === 'CE' ? (S >= K ? 1 : 0) : (S <= K ? -1 : 0),
             gamma: 0, theta: 0, vega: 0 };
  }
  const sqT  = Math.sqrt(T);
  const d1   = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqT);
  const d2   = d1 - sigma * sqT;
  const nd1  = normPDF(d1);
  const Ke_rT = K * Math.exp(-r * T);

  const delta = type === 'CE' ? normCDF(d1) : normCDF(d1) - 1;
  const gamma = nd1 / (S * sigma * sqT);
  // Theta: annualised → per-day (divide by 365)
  const thetaAnn = type === 'CE'
    ? -S * nd1 * sigma / (2 * sqT) - r * Ke_rT * normCDF(d2)
    : -S * nd1 * sigma / (2 * sqT) + r * Ke_rT * normCDF(-d2);
  const theta = thetaAnn / 365;
  // Vega: per unit σ → per 1% (divide by 100)
  const vega = S * nd1 * sqT / 100;

  return { delta, gamma, theta, vega };
}

/**
 * Normalise IV to decimal fraction (0.132 = 13.2%).
 * Defends against stale component state where iv was stored as a percentage
 * (e.g. 13.2) before the backend batch endpoint was fixed to return decimals.
 * Any realistic IV is < 200% (= 2.0 decimal); index options never exceed ~100%.
 */
function safeIV(iv) { return iv > 2.0 ? iv / 100.0 : iv; }

/** Years between two YYYY-MM-DD strings, clamped ≥ 0. */
function yearsBetween(from, to) {
  return Math.max(0,
    (new Date(to + 'T00:00:00Z') - new Date(from + 'T00:00:00Z')) / (365.25 * 86400000)
  );
}


// ── SVG Payoff Chart (zoomable + pannable) ─────────────────────────────────────

// ── LTPInput: local string state lets users type decimals without losing '.' ──
function LTPInput({ value, color, onChange, style: extraStyle }) {
  const [str, setStr] = useState(value != null ? String(value) : '');
  // Sync only when the external value is numerically different (e.g. Pull LTPs).
  // This prevents the '.' from being erased mid-typing: "45." → parseFloat=45 →
  // parent sets ltp=45 → without this guard, useEffect would reset str to "45".
  useEffect(() => {
    setStr(prev => {
      const prevNum = parseFloat(prev);
      if (!isNaN(prevNum) && prevNum === value) return prev;   // same number — keep user's string
      return value != null ? String(value) : '';
    });
  }, [value]);
  return (
    <input className="inp"
      value={str}
      onChange={e => {
        const raw = e.target.value;
        setStr(raw);
        // Only push numeric value upstream when parseable and complete (not mid-decimal)
        if (!raw.endsWith('.') && !raw.endsWith('-')) {
          const num = parseFloat(raw);
          if (!isNaN(num)) onChange(num);
          else if (raw === '' || raw === '0') onChange(0);
        }
      }}
      style={{ width:50, fontFamily:'var(--font-data)', fontWeight:600, color, ...extraStyle }}
    />
  );
}


function PayoffSVG({ strategies, spot, lotSize = 1, payoffDate, nearestExpiry, symbol = 'NIFTY' }) {
  const [zoom,       setZoom]       = useState(1);
  const [pan,        setPan]        = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverIdx,   setHoverIdx]   = useState(null);
  const [hoverScreenX, setHoverScreenX] = useState(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  // Unique clip ID so multiple PayoffSVG instances don't conflict
  const clipId  = useRef(`pclip-${Math.random().toString(36).slice(2)}`).current;

  // Re-center whenever the underlying spot changes
  useEffect(() => { setZoom(1); setPan(0); }, [spot]);

  const [SVG_W, _sizeRef] = useContainerWidth(540); const SVG_H = 360;
  // Merge: keep wrapRef in sync so drag handlers can call wrapRef.current.getBoundingClientRect()
  const wrapCombinedRef = useCallback(node => { wrapRef.current = node; _sizeRef(node); }, [_sizeRef]);
  const PAD   = { l:38, r:10, t:12, b:34 };
  const cW    = SVG_W - PAD.l - PAD.r;
  const cH    = SVG_H - PAD.t - PAD.b;
  const N     = 121;

  // Default view: lowest_strike − 1000 to highest_strike + 1000.
  // Falls back to spot ± 15% when no active legs have strike prices.
  const activeStrikes = strategies
    .filter(s => s.active)
    .flatMap(s => s.legs.filter(l => l.active !== false && l.strike > 0).map(l => l.strike));
  const viewLo        = activeStrikes.length > 0 ? Math.min(...activeStrikes) - 1000 : (spot || 1) * 0.85;
  const viewHi        = activeStrikes.length > 0 ? Math.max(...activeStrikes) + 1000 : (spot || 1) * 1.15;
  const viewHalfRange = (viewHi - viewLo) / 2;
  const viewCenter    = (viewLo + viewHi) / 2;
  const halfRange     = viewHalfRange / zoom;
  const lo            = viewCenter + pan - halfRange;
  const hi            = viewCenter + pan + halfRange;
  const prices = useMemo(() =>
    Array.from({length:N}, (_, i) => lo + (hi-lo)*i/(N-1)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lo, hi]
  );

  const STR_COLORS = ['#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10','#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10','#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10','#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10'];

  const evalDate = payoffDate || nearestExpiry;

  const activeSeries = useMemo(() =>
    strategies
      .filter(s => s.active && s.legs?.some(l => l.active !== false))
      .map((s, si) => {
        // Closed legs: fixed realized P&L offset (exit at leg.ltp)
        const closedOffset = s.legs
          .filter(l => l.active !== false && l.closed)
          .reduce((sum, leg) => {
            const sign = leg.bs === 'Buy' ? 1 : -1;
            return sum + sign * ((leg.ltp||0) - (leg.entry||0)) * (leg.lots||1) * (leg.lot_size||lotSize);
          }, 0);
        return {
          name: s.name, color: STR_COLORS[si % 20],
          payoffs: prices.map(px =>
            closedOffset + s.legs.reduce((sum, leg) => {
              if (leg.active === false || leg.closed) return sum;
              const remT  = (evalDate && leg.expiry) ? yearsBetween(evalDate, leg.expiry) : 0;
              const iv    = safeIV(leg.iv) || 0.15;
              const ls    = leg.lot_size || lotSize;
              const value = remT > 0
                ? bsPrice(px, leg.strike, remT, 0.07, iv, leg.type)
                : (leg.type==='CE' ? Math.max(0, px-leg.strike) : Math.max(0, leg.strike-px));
              return sum + (leg.bs==='Buy'?1:-1) * (value - (leg.entry||leg.ltp||0)) * (leg.lots||1) * ls;
            }, 0)
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [strategies, prices, lotSize, evalDate]
  );

  // Combined payoff (sum of all active strategies)
  const combined = useMemo(() =>
    activeSeries.length === 0
      ? null
      : prices.map((_, i) => activeSeries.reduce((s, ser) => s + ser.payoffs[i], 0)),
    [activeSeries, prices]
  );

  // ── Render empty state ──
  if (!spot) return (
    <div style={{background:'var(--surface-2)',borderRadius:6,height:180,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <span style={{fontSize:11,color:'var(--ink-3)'}}>No spot price — select an instrument</span>
    </div>
  );
  if (!combined) return (
    <div style={{background:'var(--surface-2)',borderRadius:6,height:180,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <span style={{fontSize:11,color:'var(--ink-3)'}}>Enable a strategy and add legs with LTPs</span>
    </div>
  );

  // ── Y-axis scale — must fit combined AND all individual strategy lines ──
  // When strategies partially offset each other, combined is nearly flat but
  // individual lines swing far — using only combined range clips them badly.
  const allPayoffValues = [
    ...combined,
    ...activeSeries.flatMap(s => s.payoffs),
  ];
  const rawMin = Math.min(...allPayoffValues);
  const rawMax = Math.max(...allPayoffValues);
  const yPad   = Math.max(Math.abs(rawMax), Math.abs(rawMin)) * 0.12 || 200;
  const yLo    = rawMin - yPad;
  const yHi    = rawMax + yPad;
  const yR     = yHi - yLo || 1;

  const xS = px => PAD.l + (px - lo) / (hi - lo) * cW;
  const yS = v  => PAD.t + (1 - (v - yLo) / yR) * cH;
  const y0  = Math.max(PAD.t, Math.min(PAD.t + cH, yS(0)));

  // ── Nice y-ticks ──
  const niceStep = range => {
    const rough = range / 5;
    const mag   = Math.pow(10, Math.floor(Math.log10(Math.max(1, Math.abs(rough)))));
    return [1,2,5,10].map(s => s*mag).find(s => rough <= s) || mag;
  };
  const yStep  = niceStep(yR);
  const yStart = Math.ceil(yLo / yStep) * yStep;
  const yTicks = [];
  for (let y = yStart; y <= yHi + yStep*0.01; y += yStep) yTicks.push(y);

  const xTicks = Array.from({length:7}, (_, i) => lo + (hi-lo)*i/6);
  // Both axes use XX,XXX comma format — no k abbreviation
  const fmt    = v => Math.round(v).toLocaleString('en-IN');
  const fmtX   = v => Math.round(v).toLocaleString('en-IN');

  // ── Fill areas: use path so the shape precisely follows the payoff curve ──
  // Green: area between y=0 and positive payoff
  const greenPath = (() => {
    let d = `M ${PAD.l.toFixed(1)} ${y0.toFixed(1)}`;
    combined.forEach((v, i) => {
      d += ` L ${xS(prices[i]).toFixed(1)} ${yS(Math.max(0, v)).toFixed(1)}`;
    });
    d += ` L ${(PAD.l + cW).toFixed(1)} ${y0.toFixed(1)} Z`;
    return d;
  })();

  // Red: area between y=0 and negative payoff
  const redPath = (() => {
    let d = `M ${PAD.l.toFixed(1)} ${y0.toFixed(1)}`;
    combined.forEach((v, i) => {
      d += ` L ${xS(prices[i]).toFixed(1)} ${yS(Math.min(0, v)).toFixed(1)}`;
    });
    d += ` L ${(PAD.l + cW).toFixed(1)} ${y0.toFixed(1)} Z`;
    return d;
  })();

  // ── Breakevens ──
  const bes = [];
  for (let i=1; i<combined.length; i++) {
    if ((combined[i-1]<0) !== (combined[i]<0)) {
      const frac = -combined[i-1]/(combined[i]-combined[i-1]);
      bes.push(prices[i-1] + frac*(prices[i]-prices[i-1]));
    }
  }

  const spotInView = spot >= lo && spot <= hi;

  // ── Zoom / pan / hover event handlers ──
  const onWheel = e => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.3 : 1/1.3;
    setZoom(z => Math.max(0.3, Math.min(15, z*factor)));
  };

  const onMouseDown = e => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startPan: pan, viewWidth: rect.width };
    setIsDragging(true);
    setHoverIdx(null);
  };

  const onMouseMove = e => {
    if (dragRef.current) {
      // Panning
      const { startX, startPan, viewWidth } = dragRef.current;
      const chartPx = viewWidth * cW / SVG_W;
      const priceShift = (e.clientX - startX) / chartPx * (hi - lo);
      setPan(startPan - priceShift);
    } else {
      // Hover — map screen X → price index
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const svgX = (e.clientX - rect.left) / rect.width * SVG_W;
      if (svgX < PAD.l || svgX > PAD.l + cW) { setHoverIdx(null); setHoverScreenX(null); return; }
      const frac = (svgX - PAD.l) / cW;
      setHoverIdx(Math.max(0, Math.min(N - 1, Math.round(frac * (N - 1)))));
      // Store relative X (0..1 across chart area) for HTML tooltip positioning
      setHoverScreenX(frac);
    }
  };

  const onMouseUp = () => { dragRef.current = null; setIsDragging(false); };
  const onMouseLeave = () => { dragRef.current = null; setIsDragging(false); setHoverIdx(null); setHoverScreenX(null); };

  const isModified = zoom !== 1 || Math.abs(pan) > 1;

  // ── Hover crosshair + dot (SVG layer — lightweight) ──
  let hoverEls = null;
  let hoverTooltipData = null;
  if (hoverIdx !== null && !isDragging) {
    const px   = prices[hoverIdx];
    const hx   = xS(px);
    const cval = combined[hoverIdx];

    hoverEls = (
      <g style={{ pointerEvents:'none' }}>
        <line x1={hx} x2={hx} y1={PAD.t} y2={PAD.t + cH}
          stroke="var(--ink-3)" strokeWidth=".8" strokeDasharray="3,3" opacity=".5"/>
        {activeSeries.map((s, i) => (
          <circle key={s.name} cx={hx} cy={yS(s.payoffs[hoverIdx])} r="3"
            fill={s.color} stroke="var(--paper)" strokeWidth="1.2" opacity=".9"/>
        ))}
        {activeSeries.length > 1 && (
          <circle cx={hx} cy={yS(cval)} r="3.5"
            fill="var(--ink)" stroke="var(--paper)" strokeWidth="1.5"/>
        )}
      </g>
    );

    // Data for the HTML tooltip overlay
    hoverTooltipData = {
      frac: hoverScreenX,
      px,
      rows: [
        ...activeSeries.map((s, i) => ({ label: s.name, value: s.payoffs[hoverIdx], color: s.color })),
        ...(activeSeries.length > 1
          ? [{ label: 'Combined', value: cval, color: 'var(--ink)' }]
          : []),
      ],
    };
  }

  // ── SVG X-axis left-edge fraction (for tooltip positioning) ──
  const chartLeftFrac  = PAD.l / SVG_W;
  const chartRightFrac = (PAD.l + cW) / SVG_W;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {activeSeries.length > 1 && activeSeries.map((s,i) => (
            <span key={s.name} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11 }}>
              <span style={{ display:'inline-block', width:16, height:2.5, background:s.color, borderRadius:1 }}/>
              <span style={{ color:s.color, fontWeight:600 }}>{s.name}</span>
            </span>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, color:'var(--ink-3)' }}>⊕ scroll · ↔ drag</span>
          <button onClick={() => setZoom(z => Math.min(15, z*1.3))}
            title="Zoom in"
            style={{ fontSize:13, lineHeight:1, padding:'0 5px 1px', borderRadius:3, border:'.5px solid var(--hair-strong)', background:'transparent', color:'var(--ink-2)', cursor:'pointer' }}>
            +
          </button>
          <button onClick={() => setZoom(z => Math.max(0.3, z/1.3))}
            title="Zoom out"
            style={{ fontSize:13, lineHeight:1, padding:'0 5px 2px', borderRadius:3, border:'.5px solid var(--hair-strong)', background:'transparent', color:'var(--ink-2)', cursor:'pointer' }}>
            −
          </button>
          <button onClick={() => { if (isModified) { setZoom(1); setPan(0); } }}
            title="Reset zoom"
            style={{ fontSize:11, padding:'1px 7px', borderRadius:3,
              border:`.5px solid ${isModified ? 'var(--hair-strong)' : 'transparent'}`,
              background:'transparent',
              color: isModified ? 'var(--amber-ink)' : 'var(--ink-3)',
              cursor: isModified ? 'pointer' : 'default',
              opacity: isModified ? 1 : 0.3,
              transition:'opacity .15s, color .15s' }}>
            ↺ Reset
          </button>
        </div>
      </div>

      <div ref={wrapCombinedRef} onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove}
        onMouseUp={onMouseUp} onMouseLeave={onMouseLeave}
        style={{ position:'relative', cursor: isDragging ? 'grabbing' : 'crosshair', userSelect:'none', borderRadius:4, overflow:'visible' }}>

        {/* ── HTML hover tooltip — readable font, proper box ── */}
        {hoverTooltipData && !isDragging && (() => {
          const { frac, px, rows } = hoverTooltipData;
          // Position: convert chart-area fraction to % of wrapper width
          const xPct = (chartLeftFrac + frac * (chartRightFrac - chartLeftFrac)) * 100;
          const onRight = frac < 0.55;
          return (
            <div style={{
              position:'absolute', top:0, pointerEvents:'none', zIndex:20,
              left: onRight ? `${xPct}%` : undefined,
              right: onRight ? undefined : `${100 - xPct}%`,
              transform: onRight ? 'translateX(8px)' : 'translateX(-8px)',
              background:'var(--paper)', border:'1px solid var(--hair-strong)',
              borderRadius:6, padding:'6px 10px', minWidth:130,
              boxShadow:'0 4px 18px rgba(0,0,0,.35)',
              fontSize:11, lineHeight:'1.6', opacity:.72,
            }}>
              <div style={{ fontFamily:'var(--font-data)', fontWeight:700,
                color:'var(--ink)', borderBottom:'.5px solid var(--hair)', paddingBottom:3, marginBottom:4, fontSize:11 }}>
                {symbol} @ {fmtX(px)}
              </div>
              {rows.map((row, k) => {
                const isCombined = row.label === 'Combined';
                return (
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:12, alignItems:'center',
                    ...(isCombined ? { borderTop:'.5px solid var(--hair)', paddingTop:3, marginTop:2 } : {}) }}>
                    <span style={{ color: isCombined ? 'var(--ink)' : 'var(--ink-2)',
                      fontSize:11, fontWeight: isCombined ? 700 : 400 }}>{row.label}</span>
                    <span style={{
                      fontFamily:'var(--font-data)',
                      fontWeight: 700,
                      fontSize: isCombined ? 12 : 11,
                      color: row.value >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                    }}>
                      {row.value >= 0 ? '+' : ''}₹{Math.abs(row.value) >= 1000
                        ? (row.value >= 0 ? '' : '-') + (Math.abs(row.value)/1000).toFixed(1) + 'k'
                        : row.value.toFixed(0)}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}

        <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} height={SVG_H} style={{ width:'100%', display:'block' }}>

          {/* Grid lines */}
          {yTicks.map((v, i) => (
            <g key={i}>
              <line x1={PAD.l} x2={PAD.l+cW} y1={yS(v)} y2={yS(v)}
                stroke="var(--hair)" strokeWidth=".6" strokeDasharray="4,5"/>
              <text x={PAD.l-4} y={yS(v)+3.5} textAnchor="end" fontSize={11}
                fill={v===0?'var(--ink-2)':'var(--ink-3)'}
                fontWeight={v===0?600:400}
                fontFamily="inherit">
                {fmt(v)}
              </text>
            </g>
          ))}

          {/* Clip rect for fills (inside chart area only) */}
          <defs>
            <clipPath id={clipId}>
              <rect x={PAD.l} y={PAD.t} width={cW} height={cH}/>
            </clipPath>
          </defs>

          {/* Green fill (profit zone) */}
          <path d={greenPath} fill="var(--up-focus)" opacity=".13" clipPath={`url(#${clipId})`}/>
          {/* Red fill (loss zone) */}
          <path d={redPath} fill="var(--down-focus)" opacity=".13" clipPath={`url(#${clipId})`}/>

          {/* Zero / P&L baseline */}
          <line x1={PAD.l} x2={PAD.l+cW} y1={y0} y2={y0}
            stroke="var(--hair-strong)" strokeWidth="1.5"/>

          {/* Breakeven — inverted triangle at axis + floating label just inside chart */}
          {bes.map((be, i) => {
            const bx = xS(be);
            if (bx < PAD.l + 2 || bx > PAD.l + cW - 2) return null;
            const ty = PAD.t + cH;            // chart bottom edge
            const th = 5, tw = 6;            // triangle height / half-width
            return (
              <g key={i} style={{ pointerEvents:'none' }}>
                {/* Inverted triangle sitting on the x-axis line */}
                <polygon
                  points={`${bx},${ty} ${bx-tw},${ty-th} ${bx+tw},${ty-th}`}
                  fill="var(--ink-3)" opacity=".55"/>
                {/* Label floats just inside chart, above the triangle */}
                <text x={bx} y={ty - th - 4} textAnchor="middle" fontSize={7}
                  fill="var(--ink-3)" opacity=".7" fontFamily="inherit">
                  BE{bes.length>1?i+1:''} {fmtX(be)}
                </text>
              </g>
            );
          })}

          {/* Spot line — thin grey, hidden when cursor is hovering (cursor takes over) */}
          {spotInView && hoverIdx === null && (
            <line x1={xS(spot)} x2={xS(spot)} y1={PAD.t} y2={PAD.t+cH}
              stroke="var(--ink-3)" strokeWidth=".7" strokeDasharray="4,4" opacity=".55"/>
          )}

          {/* Per-strategy payoff lines */}
          {activeSeries.map(s => (
            <polyline key={s.name}
              points={s.payoffs.map((v,i) => `${xS(prices[i]).toFixed(1)},${yS(v).toFixed(1)}`).join(' ')}
              fill="none" stroke={s.color} strokeWidth="1.4" strokeLinejoin="round"
              clipPath={`url(#${clipId})`} opacity=".95"/>
          ))}

          {/* Hover elements (rendered last — on top of everything) */}
          {hoverEls}

          {/* X-axis labels */}
          {xTicks.map((px, i) => (
            <text key={i} x={xS(px)} y={SVG_H-PAD.b+14} textAnchor="middle"
              fontSize={11} fill="var(--ink-3)" fontFamily="inherit">
              {fmtX(px)}
            </text>
          ))}

          {/* Chart border */}
          <rect x={PAD.l} y={PAD.t} width={cW} height={cH}
            fill="none" stroke="var(--hair)" strokeWidth=".7"/>

        </svg>
      </div>
    </div>
  );
}


// ── StrategyBuilderTab ─────────────────────────────────────────────────────────

const STRATEGY_TEMPLATES = [
  { col:'bullish', name:'Long Call',       legs:[{bs:'Buy',  type:'CE'}],                           desc:'Unlimited upside, defined risk, moderately–strongly bullish' },
  { col:'bullish', name:'Bull Call Spread',legs:[{bs:'Buy',  type:'CE'},{bs:'Sell', type:'CE'}],    desc:'Defined risk and reward, net debit, moderately bullish' },
  { col:'bullish', name:'Bull Put Spread', legs:[{bs:'Sell', type:'PE'},{bs:'Buy',  type:'PE'}],    desc:'Defined risk and reward, net credit, moderately bullish' },
  { col:'bullish', name:'Short Put',       legs:[{bs:'Sell', type:'PE'}],                           desc:'Premium income, bullish or neutral, assignment risk' },
  { col:'bearish', name:'Long Put',        legs:[{bs:'Buy',  type:'PE'}],                           desc:'Unlimited downside profit, defined risk, moderately–strongly bearish' },
  { col:'bearish', name:'Bear Put Spread', legs:[{bs:'Buy',  type:'PE'},{bs:'Sell', type:'PE'}],    desc:'Defined risk and reward, net debit, moderately bearish' },
  { col:'bearish', name:'Bear Call Spread',legs:[{bs:'Sell', type:'CE'},{bs:'Buy',  type:'CE'}],    desc:'Defined risk and reward, net credit, moderately bearish' },
  { col:'bearish', name:'Short Call',      legs:[{bs:'Sell', type:'CE'}],                           desc:'Premium income, bearish or neutral, unlimited risk' },
  { col:'neutral', name:'Iron Condor',     legs:[{bs:'Sell',type:'PE'},{bs:'Buy',type:'PE'},{bs:'Sell',type:'CE'},{bs:'Buy',type:'CE'}], desc:'Range-bound, defined risk, collects premium on both sides' },
  { col:'neutral', name:'Iron Fly',        legs:[{bs:'Sell',type:'PE'},{bs:'Sell',type:'CE'},{bs:'Buy',type:'PE'},{bs:'Buy',type:'CE'}], desc:'ATM sell, wider wings, range-bound with higher premium' },
  { col:'neutral', name:'Short Strangle',  legs:[{bs:'Sell',type:'PE'},{bs:'Sell',type:'CE'}],     desc:'OTM sell on both sides, undefined risk, range-bound' },
  { col:'neutral', name:'Short Straddle',  legs:[{bs:'Sell',type:'CE'},{bs:'Sell',type:'PE'}],     desc:'ATM sell on both sides, undefined risk, very range-bound' },
  { col:'volatility', name:'Long Straddle', legs:[{bs:'Buy',type:'CE'},{bs:'Buy',type:'PE'}],      desc:'ATM buy, unlimited profit if large move, debit' },
  { col:'volatility', name:'Long Strangle', legs:[{bs:'Buy',type:'PE'},{bs:'Buy',type:'CE'}],      desc:'OTM buy on both sides, cheaper than straddle, needs bigger move' },
  { col:'volatility', name:'Calendar Call', legs:[{bs:'Sell',type:'CE'},{bs:'Buy',type:'CE'}],     desc:'Sell near expiry, buy far expiry — time decay play' },
  { col:'volatility', name:'Calendar Put',  legs:[{bs:'Sell',type:'PE'},{bs:'Buy',type:'PE'}],     desc:'Sell near expiry, buy far expiry — time decay play, bearish tilt' },
];

const TEMPLATE_COLS = [
  { key:'bullish',    label:'BULLISH',    color:'var(--up-field-ink)',   arrow:'▲' },
  { key:'bearish',    label:'BEARISH',    color:'var(--down-field-ink)', arrow:'▼' },
  { key:'neutral',    label:'NEUTRAL',    color:'var(--ink-2)',          arrow:'─' },
  { key:'volatility', label:'VOLATILITY', color:'var(--amber-ink)',      arrow:'⇆' },
];

const PAYOFF_ICONS = {
  'Long Call':        <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,14 14,14 28,2" stroke="var(--up-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Bull Call Spread': <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,14 10,14 20,5 28,5" stroke="var(--up-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Bull Put Spread':  <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,5 10,5 20,14 28,14" stroke="var(--up-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Short Put':        <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,2 12,14 28,14" stroke="var(--up-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Long Put':         <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,2 16,14 28,14" stroke="var(--down-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Bear Put Spread':  <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,5 10,5 20,14 28,14" stroke="var(--down-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Bear Call Spread': <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,14 10,14 20,5 28,5" stroke="var(--down-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Short Call':       <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,14 16,14 28,2" stroke="var(--down-field-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Iron Condor':      <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,2 6,2 6,12 12,12 18,12 24,12 24,2 28,2" stroke="var(--ink-2)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Iron Fly':         <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,2 5,2 15,12 25,2 28,2" stroke="var(--ink-2)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Short Strangle':   <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,2 8,12 14,12 22,12 28,2" stroke="var(--ink-2)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Short Straddle':   <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,2 15,12 28,2" stroke="var(--ink-2)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Long Straddle':    <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,12 15,2 28,12" stroke="var(--amber-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Long Strangle':    <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,12 8,12 15,2 22,12 28,12" stroke="var(--amber-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Calendar Call':    <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,12 11,12 15,3 19,12 28,12" stroke="var(--amber-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
  'Calendar Put':     <svg width="30" height="16" viewBox="0 0 30 16"><polyline points="2,12 11,12 15,3 19,12 28,12" stroke="var(--amber-ink)" strokeWidth="1.5" fill="none"/><line x1="2" y1="14" x2="28" y2="14" stroke="var(--hair-strong)" strokeWidth=".5"/></svg>,
};

function StrategyTemplateDropdown({ onSelect, onClose, anchorRef }) {
  const [hoveredCell, setHoveredCell] = useState(null);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    const keyHandler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', keyHandler); };
  }, [onClose, anchorRef]);

  return (
    <div ref={dropRef} style={{
      position:'absolute', bottom:'calc(100% + 6px)', left:0,
      width:580, zIndex:200,
      background:'var(--surface)', border:'.5px solid var(--hair-strong)',
      borderRadius:6, boxShadow:'0 4px 24px rgba(0,0,0,.18)',
      padding:'10px 10px 8px',
    }}>
      <div style={{ position:'absolute', bottom:-6, left:24, width:12, height:6, overflow:'hidden' }}>
        <div style={{ width:8, height:8, background:'var(--surface)', border:'.5px solid var(--hair-strong)', transform:'rotate(45deg)', margin:'0 auto', marginTop:-4 }}/>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:4, marginBottom:6 }}>
        {TEMPLATE_COLS.map(col => (
          <div key={col.key} style={{ textAlign:'center', fontSize:9.5, fontWeight:700,
            color:col.color, letterSpacing:'.06em', padding:'2px 0' }}>
            {col.arrow} {col.label}
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:3 }}>
        {[0,1,2,3].map(rowIdx => (
          TEMPLATE_COLS.map(col => {
            const tpl = STRATEGY_TEMPLATES.filter(t => t.col === col.key)[rowIdx];
            if (!tpl) return <div key={`${col.key}-${rowIdx}`}/>;
            const isHov = hoveredCell === tpl.name;
            return (
              <div key={tpl.name}
                onMouseEnter={() => setHoveredCell(tpl.name)}
                onMouseLeave={() => setHoveredCell(null)}
                onClick={() => { onSelect(tpl); onClose(); }}
                title={tpl.desc}
                style={{
                  display:'flex', flexDirection:'column', alignItems:'center', gap:3,
                  padding:'6px 4px', borderRadius:4, cursor:'pointer',
                  background: isHov ? 'var(--surface-2)' : 'transparent',
                  border: isHov ? '.5px solid var(--hair-strong)' : '.5px solid transparent',
                  position:'relative', transition:'background .1s',
                }}>
                {PAYOFF_ICONS[tpl.name] ?? <svg width="30" height="16"/>}
                <span style={{ fontSize:9.5, fontWeight:500, color:'var(--ink-2)', textAlign:'center', lineHeight:1.2 }}>
                  {tpl.name}
                </span>
                {isHov && (
                  <div style={{
                    position:'absolute', bottom:'calc(100% + 4px)', left:'50%', transform:'translateX(-50%)',
                    background:'var(--surface-2)', border:'.5px solid var(--hair-strong)',
                    borderRadius:4, padding:'4px 8px', fontSize:9.5, color:'var(--ink-2)',
                    whiteSpace:'nowrap', zIndex:10, pointerEvents:'none',
                    boxShadow:'0 2px 8px rgba(0,0,0,.14)',
                  }}>
                    {tpl.desc}
                  </div>
                )}
              </div>
            );
          })
        ))}
      </div>

      <div style={{ borderTop:'.5px solid var(--hair-strong)', marginTop:8, paddingTop:6 }}>
        <div
          onClick={() => { onSelect(null); onClose(); }}
          style={{
            display:'flex', alignItems:'center', gap:8, padding:'5px 8px', borderRadius:4,
            cursor:'pointer', border:'.5px dashed var(--hair-strong)',
            background:'transparent', fontSize:9.5, color:'var(--ink-2)',
            transition:'background .1s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span style={{ fontSize:13 }}>＋</span>
          <span style={{ fontWeight:600 }}>Custom Strategy</span>
          <span style={{ color:'var(--ink-3)', marginLeft:4 }}>build from scratch — no preset legs</span>
        </div>
      </div>
    </div>
  );
}

function StrategyBuilderTab({ rows, meta, expiries = [], symbol: symbolProp, exchange: exchangeProp, refreshChain, chainLastUpdated, kiteOk }) {
  const ATM      = meta?.atm    ?? 0;
  const spot     = meta?.spot   ?? 0;
  const expiry   = meta?.expiry ?? '';
  const symbol   = symbolProp   || meta?.instrument || 'NIFTY';
  const exchange = exchangeProp || 'NFO';
  const lotSize  = meta?.lotSize ?? 1;

  const STR_COLORS = ['#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10','#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10','#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10','#1a5aaa','#c05a10','#1a6a1a','#7a4000','#881e10'];

  const makeLeg = useCallback((overrides = {}) => {
    const leg = {
      id: Date.now() + Math.random(),
      bs: 'Buy', type: 'CE',
      strike: ATM || 0,
      expiry: expiry,
      lots: 1, ltp: 0, entry: 0,
      closed: false,
      active: true,
      ...overrides,
    };
    // entry mirrors ltp at creation time (user can edit later)
    if (leg.entry === 0 && leg.ltp > 0) leg.entry = leg.ltp;
    return leg;
  }, [ATM, expiry]);

  // ── Persistence helpers ──
  const defaultStrats = () => [{
    id: 1, name: 'Strategy 1', active: true,
    legs: [
      { id:1, bs:'Buy',  type:'CE', strike:ATM||0, expiry, lots:1, ltp:0, active:true },
      { id:2, bs:'Sell', type:'CE', strike:ATM||0, expiry, lots:1, ltp:0, active:true },
    ],
  }];

  const [strategies, setStrategies] = useState(defaultStrats);
  const [nextId,     setNextId]     = useState(2);

  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const templateBtnRef = useRef(null);
  const [showLoadDropdown, setShowLoadDropdown] = useState(false);
  const loadBtnRef = useRef(null);

  // ── Mobile detection (≤600px) ─────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  useEffect(() => {
    const mq      = window.matchMedia('(max-width: 600px)');
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!showLoadDropdown) return;
    const handler = e => {
      if (loadBtnRef.current && !loadBtnRef.current.contains(e.target)) {
        setShowLoadDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLoadDropdown]);

  // ── Collapsible strategies ────────────────────────────────────────────────
  const [collapsedStrats, setCollapsedStrats] = useState(new Set());
  const toggleCollapse = id => setCollapsedStrats(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // ── Infer spot from leg strikes when Kite is disconnected ──
  // Falls back to median strike across all active legs so the payoff chart
  // can still render without a live spot price.
  const inferredSpot = useMemo(() => {
    if (spot) return spot;          // real spot available — use it
    if (ATM)  return ATM;           // ATM available — use it
    const strikes = strategies
      .flatMap(s => s.legs.filter(l => l.active !== false).map(l => l.strike))
      .filter(Boolean);
    if (!strikes.length) return 0;
    const sorted = [...strikes].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)]; // median strike
  }, [spot, ATM, strategies]);

  // ── Load from API on mount and when symbol changes ──
  //
  // loadingRef guards against a race condition:
  //   1. Component mounts → strategies = defaultStrats() (1 blank strategy)
  //   2. Load effect fires → starts async GET /api/strategies/NIFTY
  //   3. Save effect fires → starts 800ms debounce timer
  //   If the backend responds in > 800ms (e.g. just restarted), the debounced
  //   save fires FIRST and permanently overwrites saved strategies with the
  //   blank default state.  loadingRef = true blocks the save until load settles.
  const loadingRef = useRef(true);

  const applyLoaded = useCallback(data => {
    if (Array.isArray(data) && data.length) {
      setStrategies(data);
      setNextId(data.reduce((m, s) => Math.max(m, s.id), 0) + 1);
    } else {
      setStrategies(defaultStrats());
      setNextId(2);
    }
    loadingRef.current = false;   // load settled — saves are now allowed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadingRef.current = true;    // block saves while loading new symbol
    loadStrategiesAPI(symbol)
      .then(res => applyLoaded(res?.strategies))
      .catch(() => { loadingRef.current = false; });  // unblock on error too
  }, [symbol, applyLoaded]);

  // ── Debounced save to API on every strategies change ──
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!strategies.length) return;
    if (loadingRef.current) return;   // never save while initial load is in flight
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveStrategiesAPI(symbol, strategies).catch(() => {});
    }, 800);                               // 800 ms debounce — don't fire on every keystroke
    return () => clearTimeout(saveTimerRef.current);
  }, [strategies, symbol]);

  // ── Confirm modal state ──
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } | null
  const askConfirm = (message, onConfirm) => setConfirm({ message, onConfirm });
  const closeConfirm = () => setConfirm(null);

  // ── helpers ──
  const updLeg = useCallback((sid, lid, patch) =>
    setStrategies(st => st.map(s => s.id !== sid ? s : {
      ...s, legs: s.legs.map(l => l.id !== lid ? l : { ...l, ...patch }),
    })), []);

  const [ltpLoading, setLtpLoading] = useState(false);

  const refreshLTPs = async () => {
    // Collect all non-closed legs that have an expiry (across all strategies)
    // Build a parallel index array so we can map results back to strategy/leg ids.
    const legSpecs = [];   // [{symbol, exchange, expiry, strike, type}] sent to backend
    const legIndex = [];   // [{stratId, legId}] — same order as legSpecs

    strategies.forEach(s => {
      s.legs.forEach(leg => {
        if (leg.closed) return;
        const exp = leg.expiry || meta?.expiry;
        if (!exp) return;
        legSpecs.push({ symbol, exchange, expiry: exp,
                        strike: leg.strike || 0, type: leg.type || 'CE' });
        legIndex.push({ stratId: s.id, legId: leg.id });
      });
    });

    if (!legSpecs.length) return;
    setLtpLoading(true);

    try {
      // Single POST — backend makes ONE kite.quote() call for all legs regardless
      // of how many different expiries they span.  No parallel chain fetches,
      // no rate-limit risk for far-expiry (LEAP) options.
      const data = await getLegLtpBatch(legSpecs);
      if (!data?.results) return;

      // Build a fast lookup: "stratId_legId" → {ltp, iv}
      const ltpMap = {};
      data.results.forEach((r, i) => {
        const { stratId, legId } = legIndex[i];
        ltpMap[`${stratId}_${legId}`] = r;
      });

      setStrategies(st => st.map(s => ({
        ...s,
        legs: s.legs.map(leg => {
          if (leg.closed) return leg;
          const r = ltpMap[`${s.id}_${leg.id}`];
          if (!r?.ltp) return leg;
          const entry = leg.entry > 0 ? leg.entry : r.ltp;
          return { ...leg, ltp: r.ltp, entry,
                   ...(r.iv       > 0 ? { iv: safeIV(r.iv) } : {}),
                   ...(r.lot_size > 1 ? { lot_size: r.lot_size } : {}) };
        }),
      })));
    } finally {
      setLtpLoading(false);
    }
  };

  const strategiesRef = useRef(strategies);
  useEffect(() => { strategiesRef.current = strategies; }, [strategies]);

  // Keep a ref to the latest refreshLTPs so triggerAutoLTP (stable callback)
  // always calls the current version — avoids stale-closure bug.
  const refreshLTPsRef = useRef(refreshLTPs);
  useEffect(() => { refreshLTPsRef.current = refreshLTPs; });   // update every render

  const autoLtpTimerRef = useRef(null);
  const triggerAutoLTP = useCallback(() => {
    clearTimeout(autoLtpTimerRef.current);
    autoLtpTimerRef.current = setTimeout(() => {
      refreshLTPsRef.current();
    }, 600);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Margin state + calculator ─────────────────────────────────────────────
  // marginResults: { [strategyId]: { loading, span, exposure, initial, final, error, missing } }
  const [marginResults, setMarginResults] = useState({});

  const calcMargin = useCallback(async () => {
    const actives = strategies.filter(s => s.active);
    if (!actives.length) return;

    // Set all active strategies to loading
    setMarginResults(prev => {
      const next = { ...prev };
      actives.forEach(s => { next[s.id] = { loading: true }; });
      return next;
    });

    // Calculate per-strategy (in parallel)
    await Promise.all(actives.map(async s => {
      const legs = s.legs
        .filter(l => l.active !== false && !l.closed)
        .map(l => ({
          symbol:   symbol,
          exchange: l.expiry && l.expiry !== expiry ? exchange : exchange,
          expiry:   l.expiry || expiry,
          strike:   l.strike || 0,
          opt_type: l.type || 'CE',
          action:   l.bs === 'Buy' ? 'BUY' : 'SELL',
          lots:     l.lots || 1,
          lot_size: l.lot_size || lotSize,
        }));

      if (!legs.length) {
        setMarginResults(prev => ({ ...prev, [s.id]: { error: 'no_legs' } }));
        return;
      }

      try {
        const res = await getStrategyMargin(legs, lotSize);
        setMarginResults(prev => ({ ...prev, [s.id]: { loading: false, ...res } }));
      } catch (e) {
        setMarginResults(prev => ({ ...prev, [s.id]: { loading: false, error: 'fetch_error', message: e.message } }));
      }
    }));
  }, [strategies, symbol, exchange, expiry, lotSize]);

  // ── Greeks — computed via Black-Scholes from each leg's IV ──────────────────
  // This works for any expiry/strike regardless of which chain is currently loaded,
  // so LEAP or multi-expiry strategies show correct Greeks.
  // IV source priority: leg.iv (set by Pull LTPs) → meta.atmIV → 15% fallback.
  const computeNetGreeks = s => {
    const S = inferredSpot || 1;
    let d=0, g=0, t=0, v=0, p=0;
    for (const leg of s.legs) {
      if (leg.active === false) continue;
      const sign = leg.bs === 'Buy' ? 1 : -1;
      const lots = leg.lots || 1;
      // Net premium: use entry price (cost basis), fall back to ltp for unset legs
      const entryPx = leg.entry || leg.ltp || 0;
      const ls      = leg.lot_size || lotSize;
      p += -sign * entryPx * lots * ls;

      if (leg.type === 'FUT') {
        // Futures delta = 1 per lot, no gamma/theta/vega
        d += sign * lots;
        continue;
      }

      const iv = safeIV(leg.iv) || (meta?.atmIV ? meta.atmIV / 100 : 0.15);
      const T  = yearsBetween(todayISO(), leg.expiry || '');
      const gk = bsGreeks(S, leg.strike || 0, T, 0.07, iv, leg.type);

      d += sign * gk.delta * lots;
      g += sign * gk.gamma * lots;
      t += sign * gk.theta * lots;
      v += sign * gk.vega  * lots;
    }
    return { delta: d.toFixed(3), gamma: g.toFixed(5),
             theta: t.toFixed(2), vega: v.toFixed(2), premium: p.toFixed(2) };
  };

  // ── P&L summary — uses Black-Scholes for far legs evaluated at payoffDate ──
  const computePayoffSummary = s => {
    const activeLegs = s.legs.filter(l => l.active !== false && !l.closed);
    if (!activeLegs.length && !s.legs.some(l => l.active !== false && l.closed)) return null;
    const base = inferredSpot || 1;
    const pts   = Array.from({length:201}, (_, i) => base*0.7 + base*0.6*i/200);
    const evalDate = payoffDate || nearestExpiry;
    // Closed legs contribute only their realized P&L as a fixed offset (exit at leg.ltp)
    const closedOffset = s.legs
      .filter(l => l.active !== false && l.closed)
      .reduce((sum, leg) => {
        const sign = leg.bs === 'Buy' ? 1 : -1;
        return sum + sign * ((leg.ltp||0) - (leg.entry||0)) * (leg.lots||1) * (leg.lot_size||lotSize);
      }, 0);
    const payoffs = pts.map(px => closedOffset + activeLegs.reduce((sum, leg) => {
      const remT  = (evalDate && leg.expiry) ? yearsBetween(evalDate, leg.expiry) : 0;
      const iv    = safeIV(leg.iv) || (meta?.atmIV ? meta.atmIV / 100 : 0.15);
      const value = remT > 0
        ? bsPrice(px, leg.strike, remT, 0.07, iv, leg.type)
        : (leg.type==='CE' ? Math.max(0,px-leg.strike) : Math.max(0,leg.strike-px));
      return sum + (leg.bs==='Buy'?1:-1) * (value - (leg.entry||leg.ltp||0)) * (leg.lots||1) * (leg.lot_size||lotSize);
    }, 0));
    const maxP = Math.max(...payoffs), minP = Math.min(...payoffs);
    const bes = [];
    for (let i=1; i<payoffs.length; i++) {
      if ((payoffs[i-1]<0) !== (payoffs[i]<0)) {
        const frac = -payoffs[i-1]/(payoffs[i]-payoffs[i-1]);
        bes.push((pts[i-1]+frac*(pts[i]-pts[i-1])).toFixed(0));
      }
    }
    // Detect genuinely unbounded strategies by checking if the payoff is still
    // trending at the RIGHT edge of the chart (spot → ∞).
    //
    // Only the right edge matters:
    //   - Spot is bounded below by 0, so puts always have finite max profit/loss.
    //   - Spot can go to ∞, so naked long/short calls are truly unbounded.
    //
    // Long put at left edge looks like it's "still rising" — but its max profit
    // is capped at (strike − premium) when spot = 0, outside the chart range.
    // Do NOT flag it as ∞.
    const range = Math.max(Math.abs(maxP - minP), 1);
    const tol   = range * 0.02;   // 2% of range = "still meaningfully moving"
    const rightRise = payoffs[200] - payoffs[195];   // +ve = right edge still rising
    const maxUnbounded = maxP > 1e8
      || (rightRise >  tol && payoffs[200] >= maxP - tol);  // naked long call (or straddle call side)
    const minUnbounded = minP < -1e8
      || (rightRise < -tol && payoffs[200] <= minP + tol);  // naked short call
    return {
      maxProfit: maxUnbounded ? '∞' : maxP.toFixed(0),
      maxLoss:   minUnbounded ? '∞' : minP.toFixed(0),
      bes,
    };
  };

  // ── Combined (all active strategies merged) payoff summary ──
  const computeCombinedPayoffSummary = actives => {
    const allActiveLegs = actives.flatMap(s => s.legs.filter(l => l.active !== false && !l.closed));
    if (!allActiveLegs.length && !actives.some(s => s.legs.some(l => l.active !== false && l.closed))) return null;
    const base = inferredSpot || 1;
    const pts  = Array.from({length:201}, (_, i) => base*0.7 + base*0.6*i/200);
    const evalDate = payoffDate || nearestExpiry;
    // Sum realized P&L from all closed legs across all strategies
    const closedOffset = actives.flatMap(s => s.legs.filter(l => l.active !== false && l.closed))
      .reduce((sum, leg) => {
        const sign = leg.bs === 'Buy' ? 1 : -1;
        return sum + sign * ((leg.ltp||0) - (leg.entry||0)) * (leg.lots||1) * (leg.lot_size||lotSize);
      }, 0);
    const payoffs = pts.map(px => closedOffset + allActiveLegs.reduce((sum, leg) => {
      const remT  = (evalDate && leg.expiry) ? yearsBetween(evalDate, leg.expiry) : 0;
      const iv    = safeIV(leg.iv) || (meta?.atmIV ? meta.atmIV / 100 : 0.15);
      const value = remT > 0
        ? bsPrice(px, leg.strike, remT, 0.07, iv, leg.type)
        : (leg.type==='CE' ? Math.max(0,px-leg.strike) : Math.max(0,leg.strike-px));
      return sum + (leg.bs==='Buy'?1:-1) * (value - (leg.entry||leg.ltp||0)) * (leg.lots||1) * (leg.lot_size||lotSize);
    }, 0));
    const maxP = Math.max(...payoffs), minP = Math.min(...payoffs);
    const bes = [];
    for (let i=1; i<payoffs.length; i++) {
      if ((payoffs[i-1]<0) !== (payoffs[i]<0)) {
        const frac = -payoffs[i-1]/(payoffs[i]-payoffs[i-1]);
        bes.push((pts[i-1]+frac*(pts[i]-pts[i-1])).toFixed(0));
      }
    }
    // Same right-edge-only unbounded detection as per-strategy summary
    const range = Math.max(Math.abs(maxP - minP), 1);
    const tol   = range * 0.02;
    const rightRise = payoffs[200] - payoffs[195];
    const maxUnbounded = maxP > 1e8
      || (rightRise >  tol && payoffs[200] >= maxP - tol);
    const minUnbounded = minP < -1e8
      || (rightRise < -tol && payoffs[200] <= minP + tol);
    return {
      maxProfit: maxUnbounded ? '∞' : maxP.toFixed(0),
      maxLoss:   minUnbounded ? '∞' : minP.toFixed(0),
      bes,
    };
  };

  // ── Combined net greeks (sum across all active strategies) ──
  const computeCombinedGreeks = actives => {
    const results = actives.map(s => computeNetGreeks(s));
    if (results.every(r => r === null)) return null;
    let d=0, g=0, t=0, v=0, p=0;
    for (const r of results) {
      if (!r) continue;
      d += +r.delta; g += +r.gamma; t += +r.theta; v += +r.vega; p += +r.premium;
    }
    return { delta:d.toFixed(3), gamma:g.toFixed(5), theta:t.toFixed(2), vega:v.toFixed(2), premium:p.toFixed(2) };
  };

  const strikeOptions = useMemo(() =>
    rows ? [...new Set(rows.map(r => r.strike))].sort((a,b) => a-b) : (ATM ? [ATM] : []),
    [rows, ATM]
  );

  const strikeStep = useMemo(() => {
    if (strikeOptions.length < 2) return 50;
    const diffs = strikeOptions.slice(1).map((s, i) => s - strikeOptions[i]).filter(d => d > 0);
    return diffs.length ? Math.min(...diffs) : 50;
  }, [strikeOptions]);

  const handleSelectTemplate = useCallback((tpl) => {
    const newId = nextId;
    setNextId(n => n + 1);
    const name = tpl ? tpl.name : `Strategy ${newId}`;
    const step = strikeStep || 50;
    const legs = tpl ? tpl.legs.map((l, i) => {
      let strike = ATM || 0;
      if (tpl.legs.length > 1) {
        if (tpl.name === 'Bull Call Spread' || tpl.name === 'Bear Call Spread') {
          strike = l.bs === 'Buy' ? ATM : ATM + step * 4;
        } else if (tpl.name === 'Bull Put Spread' || tpl.name === 'Bear Put Spread') {
          strike = l.bs === 'Buy' ? ATM - step * 4 : ATM;
        } else if (tpl.name === 'Iron Condor') {
          const key = `${l.bs} ${l.type}`;
          if (key === 'Sell PE') strike = ATM - step * 4;
          else if (key === 'Buy PE') strike = ATM - step * 8;
          else if (key === 'Sell CE') strike = ATM + step * 4;
          else strike = ATM + step * 8;
        } else if (tpl.name === 'Iron Fly') {
          if (l.bs === 'Sell') strike = ATM;
          else strike = l.type === 'PE' ? ATM - step * 4 : ATM + step * 4;
        } else if (tpl.name === 'Short Strangle' || tpl.name === 'Long Strangle') {
          strike = l.type === 'PE' ? ATM - step * 4 : ATM + step * 4;
        } else if (tpl.name === 'Short Call' || tpl.name === 'Long Call') {
          strike = l.type === 'CE' ? ATM + step * 2 : ATM;
        } else if (tpl.name === 'Short Put' || tpl.name === 'Long Put') {
          strike = l.type === 'PE' ? ATM - step * 2 : ATM;
        } else if (tpl.name === 'Calendar Call' || tpl.name === 'Calendar Put') {
          strike = ATM;
        }
      }
      let legExpiry = expiry;
      if ((tpl.name === 'Calendar Call' || tpl.name === 'Calendar Put') && expiries?.length > 1) {
        legExpiry = l.bs === 'Sell' ? expiries[0] : expiries[1];
      }
      return { ...makeLeg(), bs: l.bs, type: l.type, strike, expiry: legExpiry, id: newId * 100 + i };
    }) : [];
    setStrategies(s => [...s, { id: newId, name, active: true, legs }]);
    if (legs.length > 0) triggerAutoLTP();
  }, [nextId, setNextId, setStrategies, makeLeg, triggerAutoLTP, ATM, strikeStep, expiry, expiries]);

  const loadSavedStrategies = useCallback(() => {
    const saved = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key.startsWith('terminal:strategy:saved:')) continue;
      try {
        const v = JSON.parse(localStorage.getItem(key));
        if (v?.legs) saved.push(v);
      } catch {}
    }
    return saved.sort((a, b) => new Date(b.saved_at) - new Date(a.saved_at));
  }, []);

  const isExpiredLeg = useCallback((leg) => {
    if (!leg.expiry) return false;
    return new Date(leg.expiry + 'T00:00:00+05:30') < new Date();
  }, []);

  // ── Reward:Risk helper ──
  const computeRR = summ => {
    if (!summ) return '—';
    const mp = summ.maxProfit, ml = summ.maxLoss;
    if (mp === '∞' && ml === '∞') return '—';
    if (ml === '∞') return '0.00';
    if (mp === '∞') return '∞';
    const loss = +ml;
    if (loss >= 0) return '∞';   // strategy can't lose — pure credit/profit
    return (Math.abs(+mp) / Math.abs(loss)).toFixed(2);
  };

  // Abramowitz & Stegun standard normal CDF (max error 7.5e-8)
  const normCDF = x => {
    const a = Math.abs(x);
    const t = 1 / (1 + 0.2316419 * a);
    const poly = t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
    const p = 1 - 0.3989422804 * Math.exp(-0.5 * a * a) * poly;
    return x >= 0 ? p : 1 - p;
  };

  // Black-Scholes breakeven probability.
  // legs: active, non-closed legs for the strategy (or combined actives for the Total column).
  const computePOP = (summ, legs) => {
    if (!summ || !legs || legs.length === 0) return null;
    const activeLegs = legs.filter(l => l.active !== false && !l.closed);
    if (!activeLegs.length) return null;

    // Multi-expiry positions: single POP doesn't apply
    const legExpiries = [...new Set(activeLegs.map(l => l.expiry).filter(Boolean))];
    if (legExpiries.length > 1) return null;

    const S    = meta?.atm;
    const atmIV = meta?.atmIV;
    const expStr = legExpiries[0] || expiry;
    const bes  = (summ.bes || []).map(Number).sort((a, b) => a - b);

    if (!S || !atmIV || !expStr || bes.length === 0) return null;
    const T = yearsBetween(todayISO(), expStr);
    if (T <= 0) return null;

    const sigSqrtT = (atmIV / 100) * Math.sqrt(T);
    if (sigSqrtT <= 0) return null;

    // Net premium sign: positive = received (credit), negative = paid (debit)
    const netPrem = activeLegs.reduce((sum, l) =>
      sum + (l.bs === 'Sell' ? 1 : -1) * (l.ltp || l.entry || 0) * (l.lots || 1), 0);
    const isCredit = netPrem > 0;

    if (bes.length === 1) {
      // Determine which side of BE is profitable:
      //   CE + debit  (bull call / long call)       → profit ABOVE → P(S_T > BE)
      //   CE + credit (short call / bear call sprd)  → profit BELOW → P(S_T < BE)
      //   PE + credit (short put / bull put sprd)    → profit ABOVE → P(S_T > BE)
      //   PE + debit  (bear put sprd / long put)     → profit BELOW → P(S_T < BE)
      const ceDom = activeLegs.filter(l => l.type === 'CE').length >=
                    activeLegs.filter(l => l.type === 'PE').length;
      const profitAbove = ceDom ? !isCredit : isCredit;
      const d = Math.log(S / bes[0]) / sigSqrtT;
      const pop = profitAbove ? normCDF(d) : normCDF(-d);
      return Math.max(1, Math.min(99, Math.round(pop * 100)));
    }

    if (bes.length >= 2) {
      // P(BE_low < S_T < BE_high) = N(ln(BE_high/S)/σ√T) − N(ln(BE_low/S)/σ√T)
      const popInside = normCDF(Math.log(bes[1] / S) / sigSqrtT)
                      - normCDF(Math.log(bes[0] / S) / sigSqrtT);
      // Credit (iron condor, short strangle/straddle): profit between → popInside
      // Debit  (long straddle/strangle):               profit outside → 1 - popInside
      const pop = isCredit ? popInside : 1 - popInside;
      return Math.max(1, Math.min(99, Math.round(pop * 100)));
    }

    return null;
  };

  // Compact expiry formatter — reuse global fmtExpiry which already produces "2Jun26"
  const fmtExp = d => fmtExpiry(d) || '—';

  // ── Payoff date (for BS evaluation) ───────────────────────────────────────
  // payoffDateOverride: null means "use nearestExpiry" (default).
  const [payoffDateOverride, setPayoffDateOverride] = useState(null);

  const nearestExpiry = useMemo(() => {
    const exps = strategies
      .filter(s => s.active === true)          // only explicitly-active strategies
      .flatMap(s => s.legs
        .filter(l => l.active !== false)
        .map(l => l.expiry || expiry)   // same fallback the Select display uses
      )
      .filter(Boolean)
      .sort();
    // exps[0] = earliest leg expiry; fall back to global expiry only if no active legs have one
    return exps.length > 0 ? exps[0] : (expiry || null);
  }, [strategies, expiry]);

  // Reset any manual payoff-date override when the active strategies change
  // (e.g. unchecking a strategy should snap the date to the new nearest expiry)
  const prevNearestRef = useRef(nearestExpiry);
  useEffect(() => {
    if (prevNearestRef.current !== nearestExpiry) {
      prevNearestRef.current = nearestExpiry;
      setPayoffDateOverride(null);
    }
  }, [nearestExpiry]);

  // Derived payoffDate is always ≤ nearestExpiry — no async sync needed.
  const payoffDate = (payoffDateOverride && nearestExpiry && payoffDateOverride <= nearestExpiry)
    ? payoffDateOverride
    : (nearestExpiry || null);

  // ── Option Chain strip ────────────────────────────────────────────────────
  const [chainOpen,          setChainOpen]          = useState(false);
  const [chainExp,           setChainExp]           = useState(expiry || (expiries[0] ?? ''));
  const [chainRows,          setChainRows]          = useState(null);
  const [chainLoading,       setChainLoading]       = useState(false);
  const [chainRange,         setChainRange]         = useState(10);
  const [chainStripUpdated,  setChainStripUpdated]  = useState(null); // timestamp
  const [chainRefreshKey,    setChainRefreshKey]    = useState(0);    // force re-fetch
  // Which strategy gets legs added from the + buttons
  const [refStratId,         setRefStratId]         = useState(() => strategies[0]?.id ?? 1);
  const chainATMRowRef       = useRef(null);
  const chainScrollRef       = useRef(null);
  const didScrollChainToATM  = useRef(false);

  // ── Chain data cache (keyed by expiry, survives collapse/re-open) ────────────
  // Using a ref avoids stale-closure bugs when reading cache state inside effects.
  const chainCacheRef = useRef({ exp: null, rows: null });

  // Seed chainExp from parent expiry if it wasn't ready at mount time
  useEffect(() => {
    if (!expiry || chainExp) return;
    setChainExp(expiry);
  }, [expiry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync: parent rows arrive / refresh → update strip for SAME expiry ────────
  // Deliberately separate from the fetch effect so parent refreshes never
  // cancel an in-progress different-expiry fetch.
  useEffect(() => {
    if (!chainOpen || !rows?.length || chainExp !== expiry) return;
    chainCacheRef.current = { exp: chainExp, rows };
    setChainRows(rows);
    setChainLoading(false);
    setChainStripUpdated(Date.now());
  }, [rows, chainOpen, chainExp, expiry]);

  // ── ATM scroll on first load ───────────────────────────────────────────────
  useEffect(() => {
    if (!chainRows) { didScrollChainToATM.current = false; return; }
    const t = setTimeout(() => {
      if (!chainATMRowRef.current || !chainScrollRef.current) return;
      const container = chainScrollRef.current;
      const atmEl     = chainATMRowRef.current;
      const contRect  = container.getBoundingClientRect();
      const atmRect   = atmEl.getBoundingClientRect();
      container.scrollTop += (atmRect.top - contRect.top) - contRect.height / 2;
      didScrollChainToATM.current = true;
    }, 50);
    return () => clearTimeout(t);
  }, [chainRows]);

  // Range-change scroll
  useEffect(() => {
    if (!chainOpen || !chainRows) return;
    const t = setTimeout(() => {
      if (!chainATMRowRef.current || !chainScrollRef.current) return;
      const container = chainScrollRef.current;
      const atmEl     = chainATMRowRef.current;
      const contRect  = container.getBoundingClientRect();
      const atmRect   = atmEl.getBoundingClientRect();
      container.scrollTop += (atmRect.top - contRect.top) - contRect.height / 2;
    }, 30);
    return () => clearTimeout(t);
  }, [chainRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync refStratId if strategies change and selected one is gone
  useEffect(() => {
    if (!strategies.find(s => s.id === refStratId)) {
      setRefStratId(strategies[0]?.id ?? null);
    }
  }, [strategies, refStratId]);

  // ── Main fetch effect ──────────────────────────────────────────────────────
  // Fires only when strip opens, expiry changes, or forced refresh.
  // rows/expiry NOT in deps → parent live refreshes never cancel a fetch.
  // Cache check uses chainCacheRef (not stale closure) to avoid false hits.
  useEffect(() => {
    if (!chainOpen || !chainExp || !symbol) return;

    // ── Cache hit (ref-keyed by expiry) ────────────────────────────────────
    if (chainCacheRef.current.exp === chainExp && chainCacheRef.current.rows?.length) {
      setChainRows(chainCacheRef.current.rows);
      setChainLoading(false);
      return;
    }

    // ── Same expiry as parent: never double-fetch ──────────────────────────
    if (chainExp === expiry) {
      if (rows?.length) {
        // Parent already has data
        chainCacheRef.current = { exp: chainExp, rows };
        setChainRows(rows);
        setChainLoading(false);
        setChainStripUpdated(Date.now());
      } else {
        // Parent still loading → show Loading; sync effect fires when rows arrive
        setChainRows(null);
        setChainLoading(true);
      }
      return;
    }

    // ── Different expiry: fetch from API ───────────────────────────────────
    setChainRows(null);
    didScrollChainToATM.current = false;
    setChainLoading(true);
    let cancelled = false;
    getOptionChain(symbol, chainExp, exchange)
      .then(data => {
        if (!cancelled) {
          const fetched = data?.rows ?? [];
          chainCacheRef.current = { exp: chainExp, rows: fetched };
          setChainRows(fetched);
          setChainStripUpdated(Date.now());
        }
      })
      .catch(() => { if (!cancelled) setChainRows([]); })
      .finally(() => { if (!cancelled) setChainLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainOpen, chainExp, symbol, exchange, chainRefreshKey]);

  // Manual refresh: clear cache + force re-fetch
  const handleChainRefresh = () => {
    chainCacheRef.current = { exp: null, rows: null };
    setChainRows(null);
    setChainLoading(true);
    setChainRefreshKey(k => k + 1);
    if (refreshChain) refreshChain();
  };

  const addChainLeg = (strike, type, bs = 'Buy') => {
    const row  = (chainRows || []).find(r => r.strike === strike);
    const ltp  = row ? (type === 'CE' ? row.ceLTP : row.peLTP) : 0;
    const newLeg = {
      id: Date.now() + Math.random(),
      bs, type, strike,
      expiry: chainExp, lots: 1, ltp, entry: ltp,
      closed: false, active: true,
    };
    setStrategies(st => st.map(s =>
      s.id === refStratId ? { ...s, legs: [...s.legs, newLeg] } : s
    ));
  };

  const chainATM = meta?.atm ?? 0;

  return (
    <div style={{ display:'flex', gap:12, flexWrap:'nowrap', alignItems:'flex-start', minHeight:0 }}>

      {/* Confirmation modal (portal-style fixed overlay) */}
      <ConfirmModal
        message={confirm?.message}
        onConfirm={confirm?.onConfirm}
        onCancel={closeConfirm}
      />

      {/* ── Left: strategy cards — independently scrollable ── */}
      <div style={{ flex: isMobile ? '1 1 100%' : '0 0 600px', display:'flex', flexDirection:'column', gap:8,
        maxHeight: isMobile ? 'none' : 'calc(100vh - 180px)',
        overflowY: isMobile ? 'visible' : 'auto', overflowX:'clip',
        paddingRight: isMobile ? 0 : 16, paddingBottom: 180, scrollbarWidth:'thin' }}>
        {strategies.map((s, si) => {
          const isCollapsed = collapsedStrats.has(s.id);
          // Quick P&L summary for collapsed header
          const stratTotalPnl = s.legs.filter(l => l.active !== false).reduce((sum, l) => {
            const sign = l.bs==='Buy' ? 1 : -1;
            return sum + ((l.ltp||0) - (l.entry||0)) * (l.lots||1) * lotSize * sign;
          }, 0);
          const hasStratData = s.legs.some(l => l.active !== false && (l.entry > 0 || l.ltp > 0));

          return (
          <div key={s.id} className="card"
            style={{ padding:10, borderLeft:`2px solid ${STR_COLORS[si%5]}`,
              opacity: s.active ? 1 : 0.55, transition:'opacity .15s' }}>

            {/* Strategy header — click chevron area to collapse/expand */}
            <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom: isCollapsed ? 0 : 8 }}>
              {/* Collapse toggle */}
              <span
                onClick={() => toggleCollapse(s.id)}
                style={{ fontSize:11, color:'var(--ink-3)', cursor:'pointer', userSelect:'none',
                  padding:'2px 2px', lineHeight:1, flexShrink:0 }}>
                {isCollapsed ? '▶' : '▼'}
              </span>
              <input type="checkbox" checked={s.active} style={{ accentColor:'var(--amber-ink)', cursor:'pointer' }}
                onChange={() => setStrategies(st => st.map(x => x.id===s.id ? {...x, active:!x.active} : x))}/>
              <input className="inp" value={s.name} style={{ fontWeight:700, flex:1, minWidth:0 }}
                onChange={e => setStrategies(st => st.map(x => x.id===s.id ? {...x, name:e.target.value} : x))}/>
              {/* Collapsed summary: leg count + P&L */}
              {isCollapsed && (
                <span style={{ fontSize:11, color:'var(--ink-3)', whiteSpace:'nowrap', flexShrink:0 }}>
                  {s.legs.length} leg{s.legs.length!==1?'s':''}
                  {hasStratData && (
                    <span style={{ marginLeft:6, fontWeight:700, fontFamily:'var(--font-data)',
                      color: stratTotalPnl > 0 ? 'var(--up-field-ink)' : stratTotalPnl < 0 ? 'var(--down-field-ink)' : 'var(--ink-3)' }}>
                      {stratTotalPnl >= 0 ? '+' : ''}₹{Math.round(stratTotalPnl).toLocaleString('en-IN')}
                    </span>
                  )}
                </span>
              )}
              <button
                title="Save this strategy"
                onClick={() => {
                  const name = window.prompt('Save strategy as:', s.name);
                  if (!name?.trim()) return;
                  const payload = {
                    name: name.trim(),
                    saved_at: new Date().toISOString(),
                    template: s.name,
                    legs: s.legs.map(l => ({
                      bs: l.bs, expiry: l.expiry || expiry, type: l.type,
                      strike: l.strike, lots: l.lots || 1,
                    })),
                  };
                  try {
                    localStorage.setItem(`terminal:strategy:saved:${name.trim()}`, JSON.stringify(payload));
                  } catch(e) { alert('Could not save — localStorage may be full.'); }
                }}
                style={{ fontSize:11, color:'var(--ink-3)', background:'none', border:'none', cursor:'pointer', lineHeight:1, flexShrink:0, padding:'0 2px' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.25">
                  <rect x="1" y="1" width="10" height="10" rx="1.5"/>
                  <rect x="3.5" y="1" width="5" height="4" rx=".5" fill="currentColor" stroke="none"/>
                  <rect x="2.5" y="6.5" width="7" height="3.5" rx=".5" fill="currentColor" stroke="none"/>
                </svg>
              </button>
              {strategies.length > 1 && (
                <button
                  onClick={() => askConfirm(
                    `Remove "${s.name}"? All its legs will be deleted.`,
                    () => { setStrategies(st => st.filter(x => x.id !== s.id)); closeConfirm(); }
                  )}
                  style={{ fontSize:12, color:'var(--ink-3)', background:'none', border:'none', cursor:'pointer', lineHeight:1, flexShrink:0 }}>×</button>
              )}
            </div>

            {/* Collapsible body */}
            {!isCollapsed && (<>

            {/* Legs: card layout on mobile, table on desktop */}
            {isMobile ? (
              /* ── Mobile: compact 2-row card per leg ──────────────────── */
              <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:6 }}>
                {s.legs.map(leg => {
                  const sign   = leg.bs === 'Buy' ? 1 : -1;
                  const legPnl = ((leg.ltp || 0) - (leg.entry || 0)) * (leg.lots || 1) * lotSize * sign;
                  const pnlClr = legPnl > 0 ? 'var(--up-field-ink)' : legPnl < 0 ? 'var(--down-field-ink)' : 'var(--ink-3)';
                  const locked = !!leg.closed;
                  const dimmed = locked ? { opacity:0.4, pointerEvents:'none' } : {};
                  return (
                    <div key={leg.id} style={{
                      background:'var(--surface-2)', borderRadius:6, padding:'6px 8px',
                      border:'.5px solid var(--hair)',
                      opacity: leg.active===false ? 0.5 : 1, transition:'opacity .12s',
                    }}>
                      {/* Row 1: ✓ | Buy/Sell | CE/PE | Expiry(flex) | Strike | Lots | × */}
                      <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:6, ...dimmed }}>
                        <input type="checkbox" checked={leg.active !== false} disabled={locked}
                          onChange={() => updLeg(s.id, leg.id, { active: leg.active===false })}
                          style={{ accentColor:'var(--amber-ink)', cursor: locked?'default':'pointer', flexShrink:0 }}/>
                        <button disabled={locked}
                          onClick={() => { updLeg(s.id, leg.id, { bs: leg.bs==='Buy'?'Sell':'Buy', entry:0 }); triggerAutoLTP(); }}
                          style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:4, border:'none',
                            background: leg.bs==='Buy' ? 'rgba(16,152,88,0.18)' : 'rgba(200,40,40,0.15)',
                            color: leg.bs==='Buy' ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                            cursor: locked?'default':'pointer', flexShrink:0 }}>
                          {leg.bs==='Buy' ? 'Buy' : 'Sell'}
                        </button>
                        <button disabled={locked}
                          onClick={() => { updLeg(s.id, leg.id, { type: leg.type==='CE'?'PE':'CE', entry:0 }); triggerAutoLTP(); }}
                          style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:4, border:'none',
                            background: leg.type==='CE' ? 'rgba(26,90,170,0.15)' : 'rgba(192,90,16,0.15)',
                            color: leg.type==='CE' ? 'var(--cebar)' : 'var(--pebar)',
                            cursor: locked?'default':'pointer', minWidth:28, flexShrink:0 }}>
                          {leg.type}
                        </button>
                        {/* Expiry fills remaining space, absorbs any slack */}
                        <Select value={leg.expiry || expiry}
                          style={{ flex:'1 1 0', minWidth:0, fontSize:11 }}
                          disabled={locked}
                          onChange={e => { updLeg(s.id, leg.id, { expiry: e.target.value, ltp:0, entry:0 }); triggerAutoLTP(); }}>
                          {expiries.length > 0
                            ? expiries.map(d => <option key={d} value={d}>{fmtExp(d)}</option>)
                            : <option value={expiry}>{fmtExp(expiry)}</option>}
                        </Select>
                        <StrikePicker value={leg.strike} options={strikeOptions}
                          onChange={sk => { updLeg(s.id, leg.id, { strike:sk, ltp:0, entry:0 }); triggerAutoLTP(); }}/>
                        <div style={{ display:'flex', alignItems:'center', gap:1, flexShrink:0 }}>
                          <button className="step-btn" disabled={locked}
                            onClick={() => updLeg(s.id, leg.id, { lots: Math.max(1, leg.lots-1) })}>−</button>
                          <input className="inp" type="number" min="1" value={leg.lots} disabled={locked}
                            onChange={e => updLeg(s.id, leg.id, { lots: Math.max(1,+e.target.value) })}
                            style={{ width:32, textAlign:'center', padding:'2px 2px', fontWeight:600,
                              MozAppearance:'textfield', appearance:'textfield' }}/>
                          <button className="step-btn" disabled={locked}
                            onClick={() => updLeg(s.id, leg.id, { lots: leg.lots+1 })}>+</button>
                        </div>
                        <button
                          onClick={() => askConfirm(
                            `Remove this leg (${leg.bs} ${leg.strike} ${leg.type})?`,
                            () => { setStrategies(st => st.map(x => x.id===s.id ? {...x, legs: x.legs.filter(l => l.id!==leg.id)} : x)); closeConfirm(); }
                          )}
                          style={{ color:'var(--ink-3)', background:'none', border:'none', cursor:'pointer',
                            fontSize:14, lineHeight:1, padding:'0 3px', flexShrink:0 }}>×</button>
                      </div>

                      {/* Row 2: Entry(labeled) | LTP/Exit(labeled) | gap | P&L | Close(labeled)
                          alignItems:flex-start keeps all labels at the same top baseline,
                          and all controls start at the same level directly below them */}
                      <div style={{ display:'flex', alignItems:'flex-start', gap:6 }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:2 }}>Entry</div>
                          <LTPInput value={leg.entry ?? 0} color="var(--ink-2)"
                            onChange={v => updLeg(s.id, leg.id, { entry: v })}
                            style={{ width:'100%' }}/>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:2 }}>LTP/Exit</div>
                          <LTPInput value={leg.ltp} color={leg.bs==='Buy' ? 'var(--down-field-ink)' : 'var(--up-field-ink)'}
                            onChange={v => updLeg(s.id, leg.id, { ltp: v })}
                            style={{ width:'100%' }}/>
                        </div>
                        {/* spacer */}
                        <div style={{ flex:1 }}/>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:2 }}>P&amp;L</div>
                          <span style={{ fontSize:11, fontWeight:700, fontFamily:'var(--font-data)',
                            color:pnlClr, whiteSpace:'nowrap', lineHeight:'24px' }}>
                            {(leg.entry > 0 || leg.ltp > 0)
                              ? `${legPnl >= 0 ? '+' : ''}${Math.round(legPnl).toLocaleString('en-IN')}`
                              : '—'}
                          </span>
                        </div>
                        <div style={{ textAlign:'center', flexShrink:0 }}>
                          <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:2 }}>Close</div>
                          <input type="checkbox" checked={!!leg.closed}
                            onChange={() => updLeg(s.id, leg.id, { closed: !leg.closed })}
                            style={{ accentColor:'var(--ink-3)', cursor:'pointer', display:'block',
                              margin:'6px auto 0' }}/>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── Desktop: table layout (unchanged) ───────────────────── */
              <div style={{ overflowX:'clip' }}>
                <table style={{ borderCollapse:'collapse', fontSize:11, marginBottom:6, width:'100%' }}>
                  <thead>
                    <tr>
                      {['', 'B/S', 'Expiry', 'Type', 'Strike', 'Lots', 'Entry ₹', 'LTP/Exit ₹', 'P&L', 'Cls', ''].map((h, i) => (
                        <th key={i} style={{ textAlign:'center', padding:'2px 4px', color:'var(--ink-3)',
                          fontWeight:600, fontSize:11, borderBottom:'.5px solid var(--hair)',
                          whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.legs.map(leg => {
                      const sign   = leg.bs === 'Buy' ? 1 : -1;
                      const legPnl = ((leg.ltp || 0) - (leg.entry || 0)) * (leg.lots || 1) * lotSize * sign;
                      const pnlClr = legPnl > 0 ? 'var(--up-field-ink)' : legPnl < 0 ? 'var(--down-field-ink)' : 'var(--ink-3)';
                      const locked = !!leg.closed;
                      const lockStyle = locked ? { opacity:0.35, pointerEvents:'none' } : {};
                      return (
                      <tr key={leg.id}
                        style={{ opacity: leg.active===false ? 0.45 : locked ? 0.6 : 1, transition:'opacity .12s' }}>
                        <td style={{ padding:'3px 4px', ...lockStyle }}>
                          <input type="checkbox" checked={leg.active !== false}
                            disabled={locked}
                            onChange={() => updLeg(s.id, leg.id, { active: leg.active===false })}
                            style={{ accentColor:'var(--amber-ink)', cursor: locked ? 'default' : 'pointer' }}/>
                        </td>
                        <td style={{ padding:'3px 2px', ...lockStyle }}>
                          <button disabled={locked}
                            onClick={() => { updLeg(s.id, leg.id, { bs: leg.bs==='Buy'?'Sell':'Buy', entry:0 }); triggerAutoLTP(); }}
                            title={leg.bs}
                            style={{ fontSize:11, fontWeight:700, padding:'3px 6px', borderRadius:3, border:'none',
                              background: leg.bs==='Buy' ? 'rgba(16,152,88,0.18)' : 'rgba(200,40,40,0.15)',
                              color: leg.bs==='Buy' ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                              cursor: locked?'default':'pointer', minWidth:22, letterSpacing:'.02em' }}>
                            {leg.bs==='Buy' ? 'B' : 'S'}
                          </button>
                        </td>
                        <td style={{ padding:'3px 4px', ...lockStyle }}>
                          <Select value={leg.expiry || expiry} style={{ minWidth:60 }} disabled={locked}
                            onChange={e => { updLeg(s.id, leg.id, { expiry: e.target.value, ltp:0, entry:0 }); triggerAutoLTP(); }}>
                            {expiries.length > 0
                              ? expiries.map(d => <option key={d} value={d}>{fmtExp(d)}</option>)
                              : <option value={expiry}>{fmtExp(expiry)}</option>}
                          </Select>
                        </td>
                        <td style={{ padding:'3px 2px', ...lockStyle }}>
                          <button disabled={locked}
                            onClick={() => { updLeg(s.id, leg.id, { type: leg.type==='CE'?'PE':'CE', entry:0 }); triggerAutoLTP(); }}
                            style={{ fontSize:11, fontWeight:700, padding:'3px 5px', borderRadius:3, border:'none',
                              background: leg.type==='CE' ? 'rgba(26,90,170,0.15)' : 'rgba(192,90,16,0.15)',
                              color: leg.type==='CE' ? 'var(--cebar)' : 'var(--pebar)',
                              cursor: locked?'default':'pointer', minWidth:26 }}>
                            {leg.type}
                          </button>
                        </td>
                        <td style={{ padding:'3px 4px', ...lockStyle }}>
                          <StrikePicker value={leg.strike} options={strikeOptions}
                            onChange={sk => { updLeg(s.id, leg.id, { strike:sk, ltp:0, entry:0 }); triggerAutoLTP(); }}/>
                        </td>
                        <td style={{ padding:'3px 4px', ...lockStyle }}>
                          <div style={{ display:'flex', alignItems:'center', gap:2 }}>
                            <button className="step-btn" disabled={locked}
                              onClick={() => updLeg(s.id, leg.id, { lots: Math.max(1, leg.lots-1) })}>−</button>
                            <input className="inp" type="number" min="1" value={leg.lots} disabled={locked}
                              onChange={e => updLeg(s.id, leg.id, { lots: Math.max(1,+e.target.value) })}
                              style={{ width:40, textAlign:'center', padding:'2px 0', fontWeight:600,
                                MozAppearance:'textfield', appearance:'textfield' }}/>
                            <button className="step-btn" disabled={locked}
                              onClick={() => updLeg(s.id, leg.id, { lots: leg.lots+1 })}>+</button>
                          </div>
                        </td>
                        <td style={{ padding:'3px 4px' }}>
                          <LTPInput value={leg.entry ?? 0} color="var(--ink-2)"
                            onChange={v => updLeg(s.id, leg.id, { entry: v })}/>
                        </td>
                        <td style={{ padding:'3px 4px' }}>
                          <LTPInput value={leg.ltp} color={leg.bs==='Buy' ? 'var(--down-field-ink)' : 'var(--up-field-ink)'}
                            onChange={v => updLeg(s.id, leg.id, { ltp: v })}/>
                        </td>
                        <td style={{ padding:'3px 4px', textAlign:'right', fontFamily:'var(--font-data)',
                          fontSize:11, color: pnlClr, whiteSpace:'nowrap', fontWeight:600 }}>
                          {(leg.entry > 0 || leg.ltp > 0)
                            ? `${legPnl >= 0 ? '+' : ''}${Math.round(legPnl).toLocaleString('en-IN')}`
                            : '—'}
                        </td>
                        <td style={{ padding:'3px 4px', textAlign:'center' }}>
                          <input type="checkbox" checked={!!leg.closed}
                            title={locked ? 'Uncheck to re-open leg' : 'Mark leg as closed'}
                            onChange={() => updLeg(s.id, leg.id, { closed: !leg.closed })}
                            style={{ accentColor:'var(--ink-3)', cursor:'pointer' }}/>
                        </td>
                        <td style={{ padding:'3px 4px' }}>
                          <button
                            onClick={() => askConfirm(
                              `Remove this leg (${leg.bs} ${leg.strike} ${leg.type})?`,
                              () => { setStrategies(st => st.map(x => x.id===s.id ? {...x, legs: x.legs.filter(l => l.id!==leg.id)} : x)); closeConfirm(); }
                            )}
                            style={{ color:'var(--ink-3)', background:'none', border:'none', cursor:'pointer', fontSize:13, lineHeight:1 }}>×</button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* + Leg button and Total P&L on same row */}
            {(() => {
              const activeLegs = s.legs.filter(l => l.active !== false);
              const hasData    = activeLegs.some(l => l.entry > 0 || l.ltp > 0);
              const totalPnl   = hasData ? activeLegs.reduce((sum, l) => {
                const sign = l.bs === 'Buy' ? 1 : -1;
                return sum + ((l.ltp || 0) - (l.entry || 0)) * (l.lots || 1) * lotSize * sign;
              }, 0) : null;
              const pnlClr = totalPnl > 0 ? 'var(--up-field-ink)' : totalPnl < 0 ? 'var(--down-field-ink)' : 'var(--ink-3)';
              return (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  gap:8, paddingTop:6 }}>
                  <button className="pill on"
                    onClick={() => {
                      setStrategies(st => st.map(x => x.id===s.id
                        ? {...x, legs: [...x.legs, makeLeg()]} : x));
                      triggerAutoLTP();
                    }}>
                    + Leg
                  </button>
                  {totalPnl !== null && (
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ fontSize:11, color:'var(--ink-3)', fontWeight:500 }}>Total P&amp;L</span>
                      <span style={{ fontSize:11, fontWeight:700, fontFamily:'var(--font-data)',
                        color: pnlClr, letterSpacing:'-.01em' }}>
                        {totalPnl >= 0 ? '+' : ''}₹{Math.round(totalPnl).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            </>)} {/* end !isCollapsed */}
          </div>
          );
        })}

        {/* Strategy-level actions */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {strategies.length < 20 && (
            <div style={{ position:'relative', display:'inline-block' }}>
              <button
                ref={templateBtnRef}
                className="pill on"
                onClick={() => setShowTemplateDropdown(v => !v)}
                style={{ fontWeight:700 }}>
                + STRATEGY ▾
              </button>
              {showTemplateDropdown && (
                <StrategyTemplateDropdown
                  onSelect={handleSelectTemplate}
                  onClose={() => setShowTemplateDropdown(false)}
                  anchorRef={templateBtnRef}
                />
              )}
            </div>
          )}
          <button onClick={refreshLTPs} disabled={ltpLoading}
            style={{ fontSize:11, fontWeight:600, padding:'3px 12px', borderRadius:4,
              border:'.5px solid var(--amber)', color:'var(--amber)', background:'transparent',
              cursor: ltpLoading ? 'wait' : 'pointer', opacity: ltpLoading ? 0.6 : 1,
              display:'flex', alignItems:'center', gap:4 }}>
            {ltpLoading ? '↻ Fetching…' : '↺ Pull LTPs from chain'}
          </button>
          <div style={{ position:'relative', display:'inline-block' }} ref={loadBtnRef}>
            <button
              onClick={() => setShowLoadDropdown(v => !v)}
              style={{ fontSize:11, fontWeight:600, padding:'3px 12px', borderRadius:4,
                border:'.5px solid var(--hair-strong)', color:'var(--ink-2)', background:'transparent',
                cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
              📂 Load saved
            </button>
            {showLoadDropdown && (() => {
              const saved = loadSavedStrategies();
              return (
                <div style={{
                  position:'absolute', bottom:'calc(100% + 6px)', left:0,
                  minWidth:260, maxWidth:340, zIndex:100,
                  background:'var(--surface)', border:'.5px solid var(--hair-strong)',
                  borderRadius:6, boxShadow:'0 4px 18px rgba(0,0,0,.16)', padding:'6px 0',
                }}>
                  {saved.length === 0 ? (
                    <div style={{ padding:'8px 12px', fontSize:11, color:'var(--ink-3)' }}>No saved strategies</div>
                  ) : saved.map((sv, idx) => (
                    <div key={idx}
                      onClick={() => {
                        const newId = nextId;
                        setNextId(n => n + 1);
                        const loadedLegs = sv.legs.map((l, i) => ({
                          ...makeLeg(), bs:l.bs, type:l.type,
                          strike:l.strike, lots:l.lots||1,
                          expiry: l.expiry || expiry,
                          id: newId * 100 + i,
                        }));
                        setStrategies(s => [...s, { id:newId, name:sv.name, active:true, legs:loadedLegs }]);
                        setShowLoadDropdown(false);
                        triggerAutoLTP();
                      }}
                      style={{ padding:'6px 12px', cursor:'pointer', borderBottom:'.5px solid var(--hair)',
                        display:'flex', alignItems:'center', gap:8 }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, fontWeight:600, color:'var(--ink)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {sv.name}
                        </div>
                        <div style={{ fontSize:10, color:'var(--ink-3)', marginTop:1 }}>
                          {sv.legs.length} leg{sv.legs.length!==1?'s':''} · saved {new Date(sv.saved_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })}
                        </div>
                      </div>
                      {sv.legs.some(l => isExpiredLeg(l)) && (
                        <span style={{ fontSize:10, fontWeight:600, color:'var(--amber-ink)', whiteSpace:'nowrap' }}>⚠ expired leg</span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Right: payoff chart + Greeks ── */}
      <div style={{ flex:'1 1 300px', minWidth:0, display:'flex', flexDirection:'column', gap:10 }}>

        {/* ── Option Chain strip ── */}
        <div className="card" style={{ padding:0, position:'relative' }}>
          {/* Header row — always visible; only title / spacer / arrow toggle open */}
          <div style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 10px', userSelect:'none' }}>
            {/* Title + last-updated inline */}
            <div style={{ display:'flex', alignItems:'baseline', gap:5, cursor:'pointer' }}
              onClick={() => setChainOpen(o => !o)}>
              <span style={{ fontSize:11, fontWeight:600, color:'var(--ink)' }}>
                Option Chain
              </span>
              {(chainStripUpdated || chainLastUpdated) && (
                <span style={{ fontSize:11, color:'var(--ink-3)', fontWeight:400, whiteSpace:'nowrap' }}>
                  Last updated&nbsp;
                  <span style={{ color:'var(--ink-2)', fontWeight:500 }}>
                    {new Date(chainStripUpdated || chainLastUpdated).toLocaleTimeString('en-IN',
                      { hour:'2-digit', minute:'2-digit', hour12:false, timeZone:'Asia/Kolkata' })}
                    &nbsp;IST
                  </span>
                </span>
              )}
            </div>
            {/* Manual refresh button */}
            <button onClick={handleChainRefresh} disabled={chainLoading}
              title="Refresh option chain"
              style={{ fontSize:11, lineHeight:1, padding:'1px 5px', borderRadius:3,
                border:'.5px solid var(--hair-strong)', background:'transparent',
                color:'var(--ink-3)', cursor: chainLoading ? 'wait' : 'pointer',
                opacity: chainLoading ? 0.5 : 1 }}>
              ↺
            </button>
            {/* Clickable spacer — clicking this toggles open/close */}
            <div style={{ flex:1, minHeight:20, cursor:'pointer' }}
              onClick={() => setChainOpen(o => !o)} />
            {chainOpen && (
              <>
                {/* Range pills: ATM (scroll) | ±10 | ±15 | All */}
                {[['atm','ATM'], [10,'±10'], [15,'±15'], ['all','All']].map(([val, label]) => {
                  const active = val !== 'atm' && chainRange === val;
                  return (
                    <button key={val}
                      onClick={val === 'atm'
                        ? () => {
                            if (!chainATMRowRef.current || !chainScrollRef.current) return;
                            const container = chainScrollRef.current;
                            const atmEl     = chainATMRowRef.current;
                            const contRect  = container.getBoundingClientRect();
                            const atmRect   = atmEl.getBoundingClientRect();
                            container.scrollTop += (atmRect.top - contRect.top) - contRect.height / 2;
                          }
                        : () => setChainRange(val)}
                      style={{ fontSize:11, fontWeight:600, padding:'2px 6px', borderRadius:10,
                        border:`.5px solid ${active ? 'var(--amber-ink)' : 'var(--hair-strong)'}`,
                        background: active ? 'var(--amber-ink)' : 'transparent',
                        color: active ? '#fff' : 'var(--ink-3)',
                        cursor:'pointer', lineHeight:1.5, whiteSpace:'nowrap' }}>
                      {label}
                    </button>
                  );
                })}
                {/* Expiry selector */}
                <Select value={chainExp} style={{ fontSize:11, padding:'1px 4px', height:22 }}
                  onChange={e => setChainExp(e.target.value)}>
                  {expiries.length > 0
                    ? expiries.map(d => <option key={d} value={d}>{fmtExpiry(d)}</option>)
                    : <option value={chainExp}>{fmtExpiry(chainExp)}</option>}
                </Select>
                {/* Strategy selector */}
                <Select value={refStratId} style={{ fontSize:11, padding:'1px 4px', height:22, maxWidth:100 }}
                  onChange={e => setRefStratId(+e.target.value)}>
                  {strategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </Select>
              </>
            )}
            <span style={{ fontSize:11, color:'var(--ink-3)', lineHeight:1, cursor:'pointer' }}
              onClick={() => setChainOpen(o => !o)}>
              {chainOpen ? '▲' : '▼'}
            </span>
          </div>

          {/* Collapsible body */}
          {chainOpen && (
            <div ref={chainScrollRef}
              style={{ borderTop:'.5px solid var(--hair)', maxHeight:220,
                overflowY:'auto', borderRadius:'0 0 10px 10px' }}>
              {(chainLoading || !chainRows) && (
                <div style={{ padding:'16px', textAlign:'center', fontSize:11, color:'var(--ink-3)' }}>
                  Loading…
                </div>
              )}
              {!chainLoading && chainRows && chainRows.length === 0 && (
                <div style={{ padding:'16px', textAlign:'center', fontSize:11, color:'var(--ink-3)' }}>
                  No chain data
                </div>
              )}
              {!chainLoading && chainRows && chainRows.length > 0 && (() => {
                const itmBg  = 'rgba(26,74,170,.06)';
                const atmBg  = 'var(--atmbg)';
                const fmtIV  = v => (v > 0 && v <= 150) ? v.toFixed(1)+'%' : v > 150 ? '>150%' : '—';
                const sorted = [...chainRows].sort((a,b) => a.strike - b.strike);
                const atmIdx = sorted.findIndex(r => r.strike === chainATM);
                const visible = chainRange === 'all'
                  ? sorted
                  : atmIdx === -1
                    ? sorted
                    : sorted.slice(Math.max(0, atmIdx - chainRange), atmIdx + chainRange + 1);
                return (
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, tableLayout:'fixed' }}>
                    <colgroup>
                      <col style={{ width:'6%' }}/>  {/* CE Δ */}
                      <col style={{ width:'7%' }}/>  {/* CE IV */}
                      <col style={{ width:'8%' }}/>  {/* CE Intr */}
                      <col style={{ width:'8%' }}/>  {/* CE TV */}
                      <col style={{ width:'10%' }}/> {/* CE LTP */}
                      <col style={{ width:34 }}/>    {/* B/S CE */}
                      <col style={{ width:'13%' }}/> {/* Strike */}
                      <col style={{ width:34 }}/>    {/* B/S PE */}
                      <col style={{ width:'10%' }}/> {/* PE LTP */}
                      <col style={{ width:'8%' }}/>  {/* PE Intr */}
                      <col style={{ width:'8%' }}/>  {/* PE TV */}
                      <col style={{ width:'7%' }}/>  {/* PE IV */}
                      <col style={{ width:'6%' }}/>  {/* PE Δ */}
                    </colgroup>
                    <thead>
                      {/* Single header row — CE/PE labels in the B/S button cells, column labels alongside */}
                      <tr style={{ position:'sticky', top:0, background:'var(--surface-2)', zIndex:3 }}>
                        <th style={{ padding:'3px 4px', color:'var(--cebar)', fontWeight:600, textAlign:'right', borderBottom:'.5px solid var(--hair)' }}>Δ</th>
                        <th style={{ padding:'3px 4px', color:'var(--cebar)', fontWeight:600, textAlign:'right', borderBottom:'.5px solid var(--hair)' }}>IV</th>
                        <th style={{ padding:'3px 4px', color:'var(--cebar)', fontWeight:600, textAlign:'right', borderBottom:'.5px solid var(--hair)' }}>Intr</th>
                        <th style={{ padding:'3px 4px', color:'var(--cebar)', fontWeight:600, textAlign:'right', borderBottom:'.5px solid var(--hair)' }}>TV</th>
                        <th style={{ padding:'3px 5px', color:'var(--cebar)', fontWeight:600, textAlign:'right', borderBottom:'.5px solid var(--hair)' }}>LTP</th>
                        <th style={{ padding:'3px 5px', borderBottom:'.5px solid var(--hair)', borderRight:'.5px solid var(--hair)',
                          color:'var(--cebar)', fontWeight:800, fontSize:11, textAlign:'center', letterSpacing:'.02em' }}>← CE</th>
                        <th style={{ padding:'3px 6px', color:'var(--ink)', fontWeight:700, textAlign:'center', borderBottom:'.5px solid var(--hair)', background:'var(--surface-2)' }}>Strike</th>
                        <th style={{ padding:'3px 5px', borderBottom:'.5px solid var(--hair)', borderLeft:'.5px solid var(--hair)',
                          color:'var(--pebar)', fontWeight:800, fontSize:11, textAlign:'center', letterSpacing:'.02em' }}>PE →</th>
                        <th style={{ padding:'3px 5px', color:'var(--pebar)', fontWeight:600, textAlign:'left', borderBottom:'.5px solid var(--hair)' }}>LTP</th>
                        <th style={{ padding:'3px 4px', color:'var(--pebar)', fontWeight:600, textAlign:'left', borderBottom:'.5px solid var(--hair)' }}>Intr</th>
                        <th style={{ padding:'3px 4px', color:'var(--pebar)', fontWeight:600, textAlign:'left', borderBottom:'.5px solid var(--hair)' }}>TV</th>
                        <th style={{ padding:'3px 4px', color:'var(--pebar)', fontWeight:600, textAlign:'left', borderBottom:'.5px solid var(--hair)' }}>IV</th>
                        <th style={{ padding:'3px 4px', color:'var(--pebar)', fontWeight:600, textAlign:'left', borderBottom:'.5px solid var(--hair)' }}>Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(row => {
                        const isATM = row.strike === chainATM;
                        const itmCE = row.strike < chainATM;
                        const itmPE = row.strike > chainATM;
                        const ceBg  = isATM ? atmBg : itmCE ? itmBg : 'transparent';
                        const peBg  = isATM ? atmBg : itmPE ? itmBg : 'transparent';
                        const skBg  = isATM ? atmBg : 'var(--surface-2)';
                        return (
                          <tr key={row.strike}
                            ref={isATM ? chainATMRowRef : null}
                            style={{ borderBottom:'.5px solid var(--hair)' }}>
                            {/* CE Δ */}
                            <td style={{ padding:'3px 4px', textAlign:'right', color:'var(--ink-2)', background: ceBg }}>
                              {row.ceDelta !== undefined ? row.ceDelta.toFixed(2) : '—'}
                            </td>
                            {/* CE IV */}
                            <td style={{ padding:'3px 4px', textAlign:'right', color:'var(--ink-2)', background: ceBg }}>
                              {fmtIV(row.ceIV)}
                            </td>
                            {/* CE Intrinsic */}
                            {(() => {
                              const ceIntr = row.ceLTP > 0 ? Math.max(0, (spot||chainATM) - row.strike) : 0;
                              const ceTV   = row.ceLTP > 0 ? Math.max(0, row.ceLTP - ceIntr)           : 0;
                              return (<>
                                <td style={{ padding:'3px 4px', textAlign:'right', color:'var(--ink-3)', background: ceBg }}>
                                  {row.ceLTP > 0 ? ceIntr.toFixed(1) : '—'}
                                </td>
                                <td style={{ padding:'3px 4px', textAlign:'right', color:'var(--ink-3)', background: ceBg }}>
                                  {row.ceLTP > 0 ? ceTV.toFixed(1) : '—'}
                                </td>
                              </>);
                            })()}
                            {/* CE LTP */}
                            <td style={{ padding:'3px 5px', textAlign:'right', color:'var(--cebar)', background: ceBg, whiteSpace:'nowrap' }}>
                              {row.ceLTP?.toFixed(1) ?? '—'}
                            </td>
                            {/* B / S CE — right next to Strike */}
                            <td style={{ padding:'2px 3px', background: ceBg, borderRight:'.5px solid var(--hair)' }}>
                              <div style={{ display:'flex', gap:2, justifyContent:'center' }}>
                                <button onClick={() => addChainLeg(row.strike, 'CE', 'Buy')}
                                  style={{ fontSize:11, fontWeight:700, padding:'1px 5px', borderRadius:3,
                                    border:'none', color:'var(--up-field-ink)',
                                    background:'rgba(16,152,88,0.18)', cursor:'pointer', lineHeight:1.5 }}>B</button>
                                <button onClick={() => addChainLeg(row.strike, 'CE', 'Sell')}
                                  style={{ fontSize:11, fontWeight:700, padding:'1px 5px', borderRadius:3,
                                    border:'none', color:'var(--down-field-ink)',
                                    background:'rgba(200,40,40,0.15)', cursor:'pointer', lineHeight:1.5 }}>S</button>
                              </div>
                            </td>
                            {/* Strike */}
                            <td style={{ padding:'3px 6px', textAlign:'center', fontWeight: isATM?700:500,
                              color: isATM?'var(--amber-ink)':'var(--ink)', background: skBg, whiteSpace:'nowrap' }}>
                              {row.strike.toLocaleString('en-IN')}
                              {isATM && <span style={{ fontSize:11, marginLeft:3, color:'var(--amber-ink)', opacity:.7 }}>ATM</span>}
                            </td>
                            {/* B / S PE — left next to Strike */}
                            <td style={{ padding:'2px 3px', background: peBg, borderLeft:'.5px solid var(--hair)' }}>
                              <div style={{ display:'flex', gap:2, justifyContent:'center' }}>
                                <button onClick={() => addChainLeg(row.strike, 'PE', 'Buy')}
                                  style={{ fontSize:11, fontWeight:700, padding:'1px 5px', borderRadius:3,
                                    border:'none', color:'var(--up-field-ink)',
                                    background:'rgba(16,152,88,0.18)', cursor:'pointer', lineHeight:1.5 }}>B</button>
                                <button onClick={() => addChainLeg(row.strike, 'PE', 'Sell')}
                                  style={{ fontSize:11, fontWeight:700, padding:'1px 5px', borderRadius:3,
                                    border:'none', color:'var(--down-field-ink)',
                                    background:'rgba(200,40,40,0.15)', cursor:'pointer', lineHeight:1.5 }}>S</button>
                              </div>
                            </td>
                            {/* PE LTP */}
                            <td style={{ padding:'3px 5px', textAlign:'left', color:'var(--pebar)', background: peBg, whiteSpace:'nowrap' }}>
                              {row.peLTP?.toFixed(1) ?? '—'}
                            </td>
                            {/* PE Intrinsic + Time Value */}
                            {(() => {
                              const peIntr = row.peLTP > 0 ? Math.max(0, row.strike - (spot||chainATM)) : 0;
                              const peTV   = row.peLTP > 0 ? Math.max(0, row.peLTP - peIntr)           : 0;
                              return (<>
                                <td style={{ padding:'3px 4px', textAlign:'left', color:'var(--ink-3)', background: peBg }}>
                                  {row.peLTP > 0 ? peIntr.toFixed(1) : '—'}
                                </td>
                                <td style={{ padding:'3px 4px', textAlign:'left', color:'var(--ink-3)', background: peBg }}>
                                  {row.peLTP > 0 ? peTV.toFixed(1) : '—'}
                                </td>
                              </>);
                            })()}
                            {/* PE IV */}
                            <td style={{ padding:'3px 4px', textAlign:'left', color:'var(--ink-2)', background: peBg }}>
                              {fmtIV(row.peIV)}
                            </td>
                            {/* PE Δ */}
                            <td style={{ padding:'3px 4px', textAlign:'left', color:'var(--ink-2)', background: peBg }}>
                              {row.peDelta !== undefined ? row.peDelta.toFixed(2) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}
        </div>

        <div className="card" style={{ padding:12 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'var(--ink)', marginBottom:8 }}>
            Payoff on {payoffDate ? fmtExpiry(payoffDate) : '—'}
            {payoffDate && nearestExpiry && payoffDate === nearestExpiry &&
              <span style={{ fontSize:11, color:'var(--ink-3)', marginLeft:5, fontWeight:400 }}>(expiry)</span>}
          </div>
          <PayoffSVG strategies={strategies} spot={inferredSpot} lotSize={lotSize}
            payoffDate={payoffDate} nearestExpiry={nearestExpiry} symbol={symbol}/>

          {/* ── Payoff date stepper ── */}
          {payoffDate && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
              gap:6, marginTop:8, borderTop:'.5px solid var(--hair)', paddingTop:8 }}>
              <span style={{ fontSize:11, color:'var(--ink-3)', fontWeight:600,
                textTransform:'uppercase', letterSpacing:'.05em' }}>Payoff on</span>
              <button className="step-btn" title="Previous trading day"
                onClick={() => {
                  if (!payoffDate) return;
                  const prev = prevTradingDay(payoffDate);
                  if (prev >= todayISO()) setPayoffDateOverride(prev);
                }}>−</button>
              <span style={{ fontSize:11, fontWeight:700, minWidth:52, textAlign:'center',
                color:'var(--ink)' }}>
                {payoffDate ? fmtExpiry(payoffDate) : '—'}
              </span>
              <button className="step-btn" title="Next trading day"
                onClick={() => {
                  if (!payoffDate || !nearestExpiry) return;
                  const nxt = nextTradingDay(payoffDate);
                  if (nxt <= nearestExpiry) setPayoffDateOverride(nxt);
                }}>+</button>
              {/* Always rendered — greyed out when already at nearest expiry */}
              <button
                disabled={!payoffDateOverride || payoffDate === nearestExpiry}
                onClick={() => setPayoffDateOverride(null)}
                style={{ fontSize:11, padding:'1px 7px', borderRadius:3, marginLeft:2,
                  border:'.5px solid var(--hair-strong)', background:'transparent', cursor:'pointer',
                  color: (!payoffDateOverride || payoffDate === nearestExpiry)
                    ? 'var(--ink-3)' : 'var(--amber-ink)',
                  opacity: (!payoffDateOverride || payoffDate === nearestExpiry) ? 0.4 : 1,
                  transition: 'color .15s, opacity .15s',
                }}>
                ↺ Expiry
              </button>
            </div>
          )}
        </div>

        <div className="card" style={{ padding:10 }}>
          {/* Header row: title + Calc Margin button */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:600 }}>Greeks & Risk Summary</div>
            <button
              onClick={calcMargin}
              disabled={!kiteOk || strategies.filter(s=>s.active).some(s => marginResults[s.id]?.loading)}
              title={!kiteOk ? 'Kite not connected — login to calculate margin' : 'Calculate SPAN + Exposure margin'}
              style={{
                fontSize:11, padding:'2px 8px', borderRadius:3, cursor: kiteOk ? 'pointer' : 'not-allowed',
                background: kiteOk ? 'var(--amber-ink)' : 'var(--surface-2)',
                color: kiteOk ? '#fff' : 'var(--ink-3)',
                border: kiteOk ? 'none' : '.5px solid var(--hair-strong)',
                opacity: (!kiteOk || strategies.filter(s=>s.active).some(s => marginResults[s.id]?.loading)) ? 0.65 : 1,
                transition:'all .15s',
              }}>
              {!kiteOk ? 'Kite not connected' : strategies.filter(s=>s.active).some(s => marginResults[s.id]?.loading) ? '…' : 'Calc Margin'}
            </button>
          </div>
          <div style={{ overflowX:'auto' }}>
          <table style={{ minWidth:'100%', borderCollapse:'collapse', fontSize:11 }}>
            <thead><tr>
              <th style={{ textAlign:'left', padding:'3px 6px', color:'var(--ink-3)',
                borderBottom:'.5px solid var(--hair)', fontSize:11,
                position:'sticky', left:0, background:'var(--surface)', zIndex:2,
                boxShadow:'1px 0 0 var(--hair)' }}>METRIC</th>
              {strategies.filter(s=>s.active).map((s,i) => (
                <th key={s.id} style={{ textAlign:'right', padding:'3px 6px',
                  color:STR_COLORS[i%20], borderBottom:'.5px solid var(--hair)', fontSize:11,
                  whiteSpace:'nowrap', minWidth:90 }}>
                  {s.name}
                </th>
              ))}
              {strategies.filter(s=>s.active).length > 1 && (
                <th style={{ textAlign:'right', padding:'3px 6px',
                  color:'var(--ink-2)', borderBottom:'.5px solid var(--hair)', fontSize:11,
                  borderLeft:'.5px solid var(--hair-strong)',
                  position:'sticky', right:0, background:'var(--surface)', zIndex:2,
                  boxShadow:'-1px 0 0 var(--hair-strong)' }}>
                  Total
                </th>
              )}
            </tr></thead>
            <tbody>{(() => {
              const actives      = strategies.filter(s => s.active);
              const showTotal    = actives.length > 1;
              const summs        = actives.map(s => computePayoffSummary(s));
              const greeks       = actives.map(s => computeNetGreeks(s));
              const combinedSumm = showTotal ? computeCombinedPayoffSummary(actives) : null;
              const combinedGrk  = showTotal ? computeCombinedGreeks(actives) : null;

              const fmtProfit = v => v==='∞' ? <span style={{color:'var(--up-field-ink)'}}>∞</span>
                : v!=null ? <span style={{color:'var(--up-field-ink)'}}>+₹{Number(v).toLocaleString()}</span> : '—';
              const fmtLoss   = v => v==='∞' ? <span style={{color:'var(--down-field-ink)'}}>∞</span>
                : v!=null ? <span style={{color:+v<0?'var(--down-field-ink)':'var(--up-field-ink)'}}>₹{Number(v).toLocaleString()}</span> : '—';
              const fmtPrem   = v => { if(v==null)return'—'; const n=+v; return <span style={{color:n<0?'var(--down-field-ink)':'var(--up-field-ink)'}}>{n>=0?'+':'-'}₹{Math.abs(n).toLocaleString()}</span>; };
              const fmtRR     = v => {
                if (v === '∞') return <span style={{ background:'rgba(40,120,60,0.15)', color:'var(--up-field-ink)', borderRadius:3, padding:'1px 4px', fontWeight:700 }}>∞</span>;
                if (v === '—' || v === '0.00') return v;
                const n = +v;
                const bg = n > 1.5 ? 'rgba(40,120,60,0.15)' : n >= 0.5 ? 'rgba(180,130,30,0.15)' : 'rgba(160,50,40,0.15)';
                const fc = n > 1.5 ? 'var(--up-field-ink)' : n >= 0.5 ? 'var(--amber-ink)' : 'var(--down-field-ink)';
                return <span style={{ background:bg, color:fc, borderRadius:3, padding:'1px 4px', fontWeight:700 }}>{v}</span>;
              };

              // Format margin value for a single strategy
              const fmtMarginCell = sid => {
                const mr = marginResults[sid];
                if (!mr) return <span style={{color:'var(--ink-3)'}}>—</span>;
                if (mr.loading) return <span style={{color:'var(--ink-3)'}}>…</span>;
                if (mr.error === 'kite_disconnected' || mr.error === 'api_error')
                  return <span style={{color:'var(--down-field-ink)', fontSize:11}}>Login Kite</span>;
                if (mr.error)
                  return <span style={{color:'var(--down-field-ink)', fontSize:11}} title={mr.message}>Err</span>;
                const init = mr.initial ?? 0;
                const span = mr.span     ?? 0;
                const exp  = mr.exposure ?? 0;
                return (
                  <span title={`SPAN ₹${span.toLocaleString('en-IN')}  +  Exp ₹${exp.toLocaleString('en-IN')}`}>
                    ₹{Math.round(init).toLocaleString('en-IN')}
                  </span>
                );
              };

              // Combined margin: sum of per-strategy initial margins
              const combinedMarginCell = () => {
                if (!actives.some(s => marginResults[s.id])) return <span style={{color:'var(--ink-3)'}}>—</span>;
                if (actives.some(s => marginResults[s.id]?.loading)) return <span style={{color:'var(--ink-3)'}}>…</span>;
                const total = actives.reduce((sum, s) => sum + (marginResults[s.id]?.initial ?? 0), 0);
                const hasError = actives.some(s => marginResults[s.id]?.error);
                if (hasError && total === 0) return <span style={{color:'var(--down-field-ink)', fontSize:11}}>Err</span>;
                return <span>₹{Math.round(total).toLocaleString('en-IN')}{hasError ? '*' : ''}</span>;
              };

              const tdBase  = { textAlign:'right', padding:'3px 6px', fontFamily:'var(--font-data)' };
              const tdTotal = { ...tdBase, borderLeft:'.5px solid var(--hair-strong)' };

              const metrics = [
                { label:'Max Profit',   perFn:(_,i) => fmtProfit(summs[i]?.maxProfit), totalFn:() => fmtProfit(combinedSumm?.maxProfit) },
                { label:'Max Loss',     perFn:(_,i) => fmtLoss(summs[i]?.maxLoss),     totalFn:() => fmtLoss(combinedSumm?.maxLoss) },
                { label:'Breakeven(s)', perFn:(_,i) => summs[i]?.bes?.join(', ')||'—', totalFn:() => combinedSumm?.bes?.join(', ')||'—' },
                { label:'Reward:Risk',  perFn:(_,i) => fmtRR(computeRR(summs[i])),     totalFn:() => fmtRR(computeRR(combinedSumm)), separator: true },
                { label:'POP%',
                  perFn:(_,i) => {
                    const pop = computePOP(summs[i], actives[i].legs);
                    if (pop == null) return '—';
                    const bg = pop >= 60 ? 'rgba(40,120,60,0.15)' : pop >= 40 ? 'rgba(180,130,30,0.15)' : 'rgba(160,50,40,0.15)';
                    const fc = pop >= 60 ? 'var(--up-field-ink)' : pop >= 40 ? 'var(--amber-ink)' : 'var(--down-field-ink)';
                    return <span style={{ background:bg, color:fc, borderRadius:3, padding:'1px 4px', fontWeight:700 }}>{pop}%</span>;
                  },
                  totalFn:() => {
                    const pop = computePOP(combinedSumm, actives.flatMap(s => s.legs.filter(l => l.active !== false && !l.closed)));
                    if (pop == null) return '—';
                    const bg = pop >= 60 ? 'rgba(40,120,60,0.15)' : pop >= 40 ? 'rgba(180,130,30,0.15)' : 'rgba(160,50,40,0.15)';
                    const fc = pop >= 60 ? 'var(--up-field-ink)' : pop >= 40 ? 'var(--amber-ink)' : 'var(--down-field-ink)';
                    return <span style={{ background:bg, color:fc, borderRadius:3, padding:'1px 4px', fontWeight:700 }}>{pop}%</span>;
                  },
                },
                { label:'Net Delta',    perFn:(_,i) => greeks[i]?.delta??'—',           totalFn:() => combinedGrk?.delta??'—' },
                { label:'Net Theta/d',  perFn:(_,i) => greeks[i]?.theta??'—',           totalFn:() => combinedGrk?.theta??'—' },
                { label:'Net Vega',     perFn:(_,i) => greeks[i]?.vega??'—',            totalFn:() => combinedGrk?.vega??'—' },
                { label:'Net Premium',  perFn:(_,i) => fmtPrem(greeks[i]?.premium),     totalFn:() => fmtPrem(combinedGrk?.premium), separator: true },
                { label:'Init Margin',  perFn:(s)   => fmtMarginCell(s.id),             totalFn:() => combinedMarginCell(),
                  labelTitle: 'SPAN + Exposure margin via Kite. Hover individual cells for breakdown.' },
              ];

              return metrics.map(m => (
                <tr key={m.label} style={{ borderBottom:'.5px solid var(--hair)',
                  ...(m.separator ? { borderTop:'.5px solid var(--hair-strong)' } : {}) }}>
                  <td style={{ padding:'3px 6px', color:'var(--ink-2)', whiteSpace:'nowrap',
                    position:'sticky', left:0, background:'var(--surface)', zIndex:1,
                    boxShadow:'1px 0 0 var(--hair)',
                    ...(m.separator ? { fontWeight:600 } : {}) }}
                    title={m.labelTitle}>{m.label}</td>
                  {actives.map((s,i) => (
                    <td key={s.id} style={tdBase}>{m.perFn(s,i)}</td>
                  ))}
                  {showTotal && <td style={{ ...tdTotal, position:'sticky', right:0, background:'var(--surface)', zIndex:1, boxShadow:'-1px 0 0 var(--hair-strong)' }}>{m.totalFn()}</td>}
                </tr>
              ));
            })()}</tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Main OptionsTab ────────────────────────────────────────────────────────────

const TABS = [
  { v:'chain',   l:'Option Chain'     },
  { v:'oi',      l:'OI Analysis'      },
  { v:'builder', l:'Strategy Builder' },
];

export default function OptionsTab({ initSub = '', onSubChange }) {
  const [sub, setSub] = useState(
    () => ['chain','oi','builder'].includes(initSub) ? initSub : 'chain'
  );
  const handleSub = s => { setSub(s); onSubChange?.(s); };

  // Sync when sidebar navigation changes initSub externally
  useEffect(() => {
    if (initSub && ['chain','oi','builder'].includes(initSub)) setSub(initSub);
  }, [initSub]);

  const [symbol,       setSymbol]       = useState('NIFTY');
  const [exchange,     setExchange]     = useState('NFO');
  const [expiry,       setExpiry]       = useState('');
  const [expiries,     setExpiries]     = useState([]);
  const [symbolGroups, setSymbolGroups] = useState({ indices: FALLBACK_INDICES, stocks: [] });

  // Load dynamic symbol list once on mount
  useEffect(() => {
    getOptionSymbols().then(data => {
      if (data && !data._error) {
        setSymbolGroups({
          indices: data.indices?.length ? data.indices : FALLBACK_INDICES,
          stocks:  data.stocks  ?? [],
          mcx:     data.mcx     ?? [],
        });
      }
    }).catch(() => {});
  }, []);

  // Load expiries when symbol/exchange changes
  useEffect(() => {
    setExpiries([]);
    setExpiry('');
    getOptionExpiries(symbol, exchange).then(data => {
      const list = data?.expiries ?? [];
      setExpiries(list);
      if (list.length > 0) setExpiry(list[0]);
    }).catch(() => {});
  }, [symbol, exchange]);

  const { chain, loading, error, kiteOk, refresh: refreshChain, lastUpdated: chainLastUpdated } = useOptionChain(symbol, exchange, expiry);
  const meta = chain?.meta ?? null;
  const rows = chain?.rows ?? null;

  return (
    <div style={{ height:'calc(100vh - 44px)', overflowY:'auto', overflowX:'hidden',
      /* -12px cancels app-main padding-top (12px on desktop, 0 on mobile ≤600px) */
      margin: `${typeof window !== 'undefined' && window.matchMedia('(max-width:600px)').matches ? 0 : -12}px -16px -48px` }}>
      <div style={{ position:'sticky', top:0, zIndex:20, background:'var(--paper)' }}>
        <SubTabs tabs={TABS} val={sub} onChange={handleSub}/>
        <InstrBar
          symbol={symbol}
          exchange={exchange}
          setSymbol={setSymbol}
          setExchange={setExchange}
          expiries={expiries}
          expiry={expiry}
          setExpiry={setExpiry}
          meta={meta}
          kiteOk={kiteOk}
          loading={loading}
          symbolGroups={symbolGroups}
          lastUpdated={chainLastUpdated}
          onRefresh={refreshChain}
        />
      </div>
      <div style={{ padding:'12px 16px 48px' }}>
        {sub==='chain'   && <OptionChainTab rows={rows} meta={meta} loading={loading} error={error}
                              expiries={expiries} expiry={expiry} setExpiry={setExpiry}/>}
        {sub==='oi'      && <OIAnalysisTab  rows={rows} meta={meta}
                              expiries={expiries} expiry={expiry} setExpiry={setExpiry}
                              symbol={symbol} exchange={exchange}
                              loading={loading} error={error} kiteOk={kiteOk}/>}
        {sub==='builder' && <StrategyBuilderTab rows={rows} meta={meta} expiries={expiries} symbol={symbol} exchange={exchange} refreshChain={refreshChain} chainLastUpdated={chainLastUpdated} kiteOk={kiteOk}/>}
      </div>
    </div>
  );
}
